import Phaser from "phaser";
import type { TowerId } from "../types";

export type Archetype =
  | "handcaster"
  | "gunner"
  | "stafmage"
  | "lina"
  | "crystalmaiden"
  | "earthshaker"
  | "mirana"
  | "zeus"
  | "underlord"
  | "clockwerk"
  | "powerranger"
  | "sven"
  | "witchdoctor"
  | "lich"
  | "wyvern"
  | "venomancer"
  | "brewmaster"
  | "pugna"
  | "beastmaster"
  | "tidehunter"
  | "slardar"
  | "necrolyte"
  | "phantomassassin"
  | "lunamoonrider"
  | "tinker"
  | "treant";

/** Default per-slot archetype (fusion towers fall back to this). */
export const ARCHETYPE_BY_SLOT: Archetype[] = ["handcaster", "gunner", "stafmage"];

/**
 * Per-tower-id archetype roster. Each tower has up to 3 hero variants
 * (one per upgrade level / hero slot). Tower ids not listed fall back
 * to ARCHETYPE_BY_SLOT.
 */
const ARCHETYPE_BY_TOWER: Partial<Record<TowerId, Archetype[]>> = {
  // Fire — Lina the Slayer. Side flame ponytail, red dress, raised hand summoning fire.
  fire:   ["lina", "lina", "lina"],
  // Water — Crystal Maiden (Rylai). Fur ushanka, blue robe, ice staff with crystal sphere.
  water:  ["crystalmaiden", "crystalmaiden", "crystalmaiden"],
  // Earth — Witch Doctor (Zharvakko). Tribal shaman with bone mask + voodoo skull staff.
  earth:  ["witchdoctor", "witchdoctor", "witchdoctor"],
  // Nature — Mirana Moon Rider archer line. Bow, quiver, crescent helmet.
  nature: ["mirana", "mirana", "mirana"],
  // Lava — Karroch the Beastmaster. Tribal warrior with hawk companion, twin axes, Firestorm.
  lava:   ["beastmaster", "beastmaster", "beastmaster"],
  // Steam — Brewmaster (Mangix). Drunken panda monk with brew jugs and bo staff.
  steam:  ["brewmaster", "brewmaster", "brewmaster"],
  // Forest Fire — Sven the Rogue Knight. Big armored warrior with massive sword + purple cape.
  forest_fire: ["sven", "sven", "sven"],
  // Mud — Earthshaker (Raigor Stonehoof). Bull-headed minotaur with massive totem.
  mud:    ["earthshaker", "earthshaker", "earthshaker"],
  // Ice — Winter Wyvern (Auroth). Bipedal blue dragon with feathered wings,
  // floating ice tome, frost-spike crown.
  ice:    ["wyvern", "wyvern", "wyvern"],
  // Vine — Treant Protector (Rooftrellen). Ancient tree guardian with bark armor, branch arms, leaf crown.
  vine:   ["treant", "treant", "treant"],
  // Triple fusions
  brew:     ["clockwerk", "clockwerk", "clockwerk"],
  voidmage: ["zeus", "zeus", "zeus"],
  beast:    ["underlord", "underlord", "underlord"],
  // Tide — Slardar (Slithereen Guard). Armored fish-warrior with glowing
  // forehead crystal, two-handed mace, scale-plated body.
  tide:     ["slardar", "slardar", "slardar"],
  // Mythic (4-element) heroes
  necro:    ["necrolyte", "necrolyte", "necrolyte"],
  phantom:  ["phantomassassin", "phantomassassin", "phantomassassin"],
  luna:     ["lunamoonrider", "lunamoonrider", "lunamoonrider"],
  tinker:   ["tinker", "tinker", "tinker"],
};

function pickArchetype(towerId: TowerId, slot: 0 | 1 | 2): Archetype {
  const roster = ARCHETYPE_BY_TOWER[towerId];
  if (roster) return roster[slot] ?? roster[0];
  return ARCHETYPE_BY_SLOT[slot];
}

/**
 * Procedurally drawn Dota-2-inspired hero sprite. Each upgrade level adds a
 * different archetype hero (Archer → Gunner → Mage) tinted by element color.
 */
export interface CasterSprite extends Phaser.GameObjects.Container {
  archetype: Archetype;
  castFlash: () => void;
  setLevel: (level: number) => void;
  /** Local-space (relative to container) point projectiles spawn from. */
  castPoint: { x: number; y: number };
}

export function makeCaster(
  scene: Phaser.Scene,
  id: TowerId,
  x: number,
  y: number,
  variant: 0 | 1 | 2 = 0,
): CasterSprite {
  const c = scene.add.container(x, y) as CasterSprite;
  const palette = PALETTES[id];
  const archetype = pickArchetype(id, variant);
  c.archetype = archetype;

  // ---- SUPER SAIYAN KI AURA — a tall, flickering flame ENVELOPE that wraps
  // the whole hero and rises past the head (not a flat chevron/arrow). A
  // bright vertical core, a dense sheet of jagged upward flames, rising sparks
  // and a ground burst, so every hero reads as "powered up" at all times. ----
  // Tall energy envelope behind the hero — taller than wide (teardrop plume).
  const kiField = scene.add.ellipse(0, -10, 52, 84, palette.energy, 0.11);
  const kiCore = scene.add.ellipse(0, -4, 30, 58, palette.energy, 0.19);
  const kiHot = scene.add.ellipse(0, 0, 15, 36, 0xffffff, 0.16);
  // Dense flame sheet — each tongue is a CURVED, licking shape (bulging base,
  // narrowing curling tip) with a white-hot inner core, so it reads like real
  // fire instead of flat geometric spikes. Bases hug the feet; tips tower past
  // the head and flicker on independent clocks.
  const flames: Phaser.GameObjects.Graphics[] = [];
  const FLAME_N = 13;
  // Draw one organic flame tongue into a graphics object, base planted at local
  // (0,0) so scaleX/scaleY flicker grows it upward without lifting the base.
  const buildTongue = (g: Phaser.GameObjects.Graphics, w: number, h: number, curl: number) => {
    const quad = (
      p0x: number, p0y: number, cx: number, cy: number, p2x: number, p2y: number, pts: number[][],
    ) => {
      for (let s = 1; s <= 8; s++) {
        const tt = s / 8;
        const mt = 1 - tt;
        pts.push([
          mt * mt * p0x + 2 * mt * tt * cx + tt * tt * p2x,
          mt * mt * p0y + 2 * mt * tt * cy + tt * tt * p2y,
        ]);
      }
    };
    const tongue = (ww: number, hh: number, color: number, alpha: number) => {
      const tipX = curl * ww * 0.5;
      const pts: number[][] = [[-ww, 0]];
      quad(-ww, 0, -ww * 1.15, -hh * 0.42, tipX, -hh, pts); // left edge curls up to the tip
      quad(tipX, -hh, ww * 1.15, -hh * 0.42, ww, 0, pts);   // right edge curls back down
      g.fillStyle(color, alpha);
      g.beginPath();
      g.moveTo(pts[0][0], pts[0][1]);
      for (let k = 1; k < pts.length; k++) g.lineTo(pts[k][0], pts[k][1]);
      g.closePath();
      g.fillPath();
    };
    tongue(w, h, palette.energy, 0.55);        // outer body (element-tinted)
    tongue(w * 0.62, h * 0.74, 0xffcaa0, 0.6); // mid heat
    tongue(w * 0.32, h * 0.5, 0xfff4d0, 0.85); // white-hot core
  };
  for (let i = 0; i < FLAME_N; i++) {
    const t = i / (FLAME_N - 1);          // 0..1 across the body
    const fx = -22 + t * 44;               // span both sides of the hero
    const edge = Math.abs(fx) / 22;        // 0 center .. 1 edge
    const baseY = 13 - edge * 1.5;         // bases hug the feet (flat-ish line)
    // A couple of extra-tall licks near the back so the plume towers over the head.
    const tall = (i % 4 === 0) ? 14 : 0;
    const h = 24 + (1 - edge) * 9 + tall + Math.random() * 14;
    const w = 5 + Math.random() * 2.5;
    const curl = (Math.random() - 0.5) * 1.4; // sideways lean of the curling tip
    const g = scene.add.graphics({ x: fx, y: baseY });
    buildTongue(g, w, h, curl);
    g.setAlpha(0.55);
    flames.push(g);
    // Fast flicker — scaleY pump + alpha shimmer (the burning "lick" of real fire).
    scene.tweens.add({
      targets: g,
      scaleY: { from: 0.6 + Math.random() * 0.2, to: 1.15 + Math.random() * 0.3 },
      alpha: { from: 0.35, to: 0.85 },
      yoyo: true,
      repeat: -1,
      duration: 160 + Math.random() * 150,
      delay: i * 20,
      ease: "Sine.easeInOut",
    });
    // Breathing width + side-to-side sway so tongues never look static.
    scene.tweens.add({
      targets: g,
      scaleX: { from: 0.85, to: 1.12 },
      yoyo: true,
      repeat: -1,
      duration: 300 + Math.random() * 200,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({
      targets: g,
      x: fx + (Math.random() < 0.5 ? -2 : 2),
      yoyo: true,
      repeat: -1,
      duration: 260 + Math.random() * 180,
      ease: "Sine.easeInOut",
    });
  }
  // Rising ki sparks streaming up through the flame plume (energy gathering).
  const kiSparks: Phaser.GameObjects.Arc[] = [];
  for (let i = 0; i < 9; i++) {
    const sx = -18 + Math.random() * 36;
    const spark = scene.add.circle(sx, 16, 1.1 + Math.random() * 0.9, palette.trim, 0.95);
    kiSparks.push(spark);
    scene.tweens.add({
      targets: spark,
      y: -34 - Math.random() * 14,
      x: sx + (Math.random() - 0.5) * 10,
      alpha: { from: 0.95, to: 0 },
      repeat: -1,
      duration: 620 + Math.random() * 520,
      delay: i * 110,
      ease: "Sine.easeIn",
    });
  }
  // Ground energy disc that breathes power at the hero's feet.
  const kiDisc = scene.add.ellipse(0, 18, 46, 11, palette.energy, 0).setStrokeStyle(2, palette.energy, 0.7);
  scene.tweens.add({ targets: kiField, scaleX: { from: 0.9, to: 1.08 }, scaleY: { from: 0.94, to: 1.1 }, alpha: { from: 0.08, to: 0.2 }, yoyo: true, repeat: -1, duration: 520, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: kiCore, scaleX: { from: 0.85, to: 1.12 }, scaleY: { from: 0.9, to: 1.16 }, alpha: { from: 0.14, to: 0.32 }, yoyo: true, repeat: -1, duration: 420, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: kiHot, scaleY: { from: 0.8, to: 1.25 }, alpha: { from: 0.1, to: 0.28 }, yoyo: true, repeat: -1, duration: 360, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: kiDisc, scaleX: { from: 0.8, to: 1.15 }, alpha: { from: 0.3, to: 0.8 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });

  // ---- WORLD-CLASS LUXURY HALO (behind hero, premium presentation) ----
  // Triple-layer outer aura tinted by hero element for identity at a glance
  const auraOutermost = scene.add.circle(0, 0, 30, palette.energy, 0.10);
  const luxAura = scene.add.circle(0, 0, 24, 0xffd84a, 0.22);
  const luxAuraInner = scene.add.circle(0, 0, 16, palette.energy, 0.30);
  // Twin counter-rotating gold rings
  const luxRing = scene.add.circle(0, 0, 20, 0xffe070, 0).setStrokeStyle(1.4, 0xffe070, 0.85);
  const luxRingInner = scene.add.circle(0, 0, 16, palette.energy, 0).setStrokeStyle(0.8, palette.trim, 0.7);
  // Pedestal under feet — multi-layer golden disc with rim & inset
  const pedestalShadow = scene.add.ellipse(0, 19, 36, 9, 0x000000, 0.55);
  const pedestal = scene.add.ellipse(0, 17, 32, 8, 0x6a3a08, 1).setStrokeStyle(0.8, 0x000, 1);
  const pedestalRim = scene.add.ellipse(0, 17, 30, 7.4, 0xb88a2a, 1).setStrokeStyle(0.6, 0x4a2a08, 1);
  const pedestalInner = scene.add.ellipse(0, 16.5, 26, 6, 0xffd84a, 1).setStrokeStyle(0.5, 0x6a3a08, 1);
  const pedestalGem = scene.add.ellipse(0, 16.4, 8, 2.6, palette.energy, 0.9);
  const pedestalHi = scene.add.ellipse(-3, 15.6, 16, 1.8, 0xfff5b0, 0.9);
  // 4 floating gold motes orbiting hero
  const motes: Phaser.GameObjects.Arc[] = [];
  for (let i = 0; i < 4; i++) {
    const ang = (i / 4) * Math.PI * 2;
    const mote = scene.add.circle(Math.cos(ang) * 22, Math.sin(ang) * 12 - 4, 1.1, 0xffe070, 0.95);
    motes.push(mote);
    scene.tweens.add({ targets: mote, alpha: { from: 0.4, to: 1 }, yoyo: true, repeat: -1, duration: 600 + i * 90, delay: i * 110 });
  }

  // Animate
  scene.tweens.add({ targets: [auraOutermost, luxAuraInner], scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.08, to: 0.35 }, yoyo: true, repeat: -1, duration: 1400, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: luxAura, scale: { from: 0.95, to: 1.15 }, alpha: { from: 0.16, to: 0.32 }, yoyo: true, repeat: -1, duration: 1300, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: luxRing, rotation: Math.PI * 2, repeat: -1, duration: 8000 });
  scene.tweens.add({ targets: luxRingInner, rotation: -Math.PI * 2, repeat: -1, duration: 6000 });
  scene.tweens.add({ targets: [luxRing, luxRingInner], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 1700, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: pedestalInner, alpha: { from: 0.85, to: 1 }, yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: pedestalGem, scaleX: { from: 0.85, to: 1.15 }, alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 900 });

  c.add([kiField, kiCore, kiHot, kiDisc, ...flames, ...kiSparks,
         auraOutermost, luxAura, luxAuraInner, luxRing, luxRingInner, ...motes,
         pedestalShadow, pedestal, pedestalRim, pedestalInner, pedestalGem, pedestalHi]);

  // Universal shadow (under feet, on top of pedestal so it grounds the hero)
  const shadow = scene.add.ellipse(0, 16, 26, 5, 0x000000, 0.4);
  c.add(shadow);

  let result: { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void };
  if (archetype === "handcaster") result = buildHandCaster(scene, c, palette);
  else if (archetype === "gunner") result = buildGunner(scene, c, palette);
  else if (archetype === "lina") result = buildLina(scene, c, palette);
  else if (archetype === "crystalmaiden") result = buildCrystalMaiden(scene, c, palette);
  else if (archetype === "earthshaker") result = buildEarthshaker(scene, c, palette);
  else if (archetype === "mirana") result = buildMirana(scene, c, palette);
  else if (archetype === "zeus") result = buildZeus(scene, c, palette);
  else if (archetype === "underlord") result = buildUnderlord(scene, c, palette);
  else if (archetype === "clockwerk") result = buildClockwerk(scene, c, palette);
  else if (archetype === "powerranger") result = buildPowerRanger(scene, c, palette);
  else if (archetype === "sven") result = buildSven(scene, c, palette);
  else if (archetype === "witchdoctor") result = buildWitchDoctor(scene, c, palette);
  else if (archetype === "lich") result = buildLich(scene, c, palette);
  else if (archetype === "wyvern") result = buildWinterWyvern(scene, c, palette);
  else if (archetype === "venomancer") result = buildVenomancer(scene, c, palette);
  else if (archetype === "treant") result = buildTreantProtector(scene, c, palette);
  else if (archetype === "brewmaster") result = buildBrewmaster(scene, c, palette);
  else if (archetype === "pugna") result = buildPugna(scene, c, palette);
  else if (archetype === "beastmaster") result = buildBeastmaster(scene, c, palette);
  else if (archetype === "tidehunter") result = buildTidehunter(scene, c, palette);
  else if (archetype === "slardar") result = buildSlardar(scene, c, palette);
  else if (archetype === "necrolyte") result = buildNecrolyte(scene, c, palette);
  else if (archetype === "phantomassassin") result = buildPhantomAssassin(scene, c, palette);
  else if (archetype === "lunamoonrider") result = buildLuna(scene, c, palette);
  else if (archetype === "tinker") result = buildTinker(scene, c, palette);
  else result = buildMage(scene, c, palette);

  c.castPoint = result.castPoint;

  // Global hero size — keep at the original (smaller) size; the luxury halo
  // behind/below the hero handles the premium look without making the
  // sprite itself larger.
  // Sven's internal coords are ~30% larger than other heroes, so we scale him
  // down to match the visual footprint of the standard hero set.
  const baseScale = 1.32;
  const heroScale = archetype === "sven" ? baseScale / 1.3 : baseScale;
  c.setScale(heroScale);

  // Idle breathing pulse — a subtle constant "charged-up" swell. We pulse
  // scale (not y) so it never fights the Tower's formation re-layout tweens.
  scene.tweens.add({
    targets: c,
    scaleX: heroScale * 1.04,
    scaleY: heroScale * 1.06,
    yoyo: true,
    repeat: -1,
    duration: 1100 + Math.random() * 300,
    ease: "Sine.easeInOut",
  });

  // Cast flash + per-archetype hero motion (recoil / hand thrust / staff swing)
  c.castFlash = () => {
    const cp = c.castPoint;
    if (result.orb) {
      scene.tweens.add({
        targets: result.orb,
        scale: { from: 1.6, to: 1 },
        duration: 180,
        ease: "Quad.easeOut",
      });
    }
    // Hero body recoils on cast — gives the shot some weight.
    result.castAnim?.();
    const burst = scene.add.circle(c.x + cp.x * c.scaleX, c.y + cp.y * c.scaleY, 3, 0xffffff, 1);
    scene.tweens.add({
      targets: burst,
      scale: 4,
      alpha: 0,
      duration: 240,
      onComplete: () => burst.destroy(),
    });
  };
  c.setLevel = () => { /* layout handled by Tower */ };
  return c;
}

interface CasterPalette {
  primary: number;     // robe/cloak main
  primaryDark: number;
  trim: number;        // accent stripe
  metal: number;       // weapon/armor metal
  energy: number;      // glow/orb color
  skin: number;
  hair: number;
}

const PALETTES: Record<TowerId, CasterPalette> = {
  fire:   { primary: 0xc73a1a, primaryDark: 0x4a0a00, trim: 0xffd84a, metal: 0xc0c0c8, energy: 0xff5a3a, skin: 0xfde0c4, hair: 0xff8a3a },
  water:  { primary: 0x2a6aa8, primaryDark: 0x0a2a4a, trim: 0xa6e3ff, metal: 0xc0c0c8, energy: 0x4cc9f0, skin: 0xfde0c4, hair: 0xeae4d4 },
  earth:  { primary: 0x6a4a28, primaryDark: 0x2a1a08, trim: 0xb08a4f, metal: 0x8a7058, energy: 0xb08a4f, skin: 0xfde0c4, hair: 0x4a3018 },
  nature: { primary: 0x2a6a2a, primaryDark: 0x0a2a0a, trim: 0xc8ff8a, metal: 0xc0c0c8, energy: 0x7be36b, skin: 0xfde0c4, hair: 0xb04a3a },
  steam:       { primary: 0x6a8aa8, primaryDark: 0x1a3a4a, trim: 0xffffff, metal: 0xc0c0c8, energy: 0xb6e6ff, skin: 0xfde0c4, hair: 0xeae4d4 },
  lava:        { primary: 0xff8a00, primaryDark: 0xc06000, trim: 0xfff070, metal: 0xffd84a, energy: 0xfff5a8, skin: 0xfde0c4, hair: 0xffe040 },
  forest_fire: { primary: 0x6a3a9a, primaryDark: 0x2a0a4a, trim: 0xd4a0ff, metal: 0xc0c0c8, energy: 0xb84aff, skin: 0xfde0c4, hair: 0x4a2a6a },
  mud:         { primary: 0x4a3a28, primaryDark: 0x1a0a00, trim: 0xb08a4f, metal: 0x8a7058, energy: 0x7a5a3a, skin: 0xfde0c4, hair: 0x4a3018 },
  ice:         { primary: 0x6aa6c8, primaryDark: 0x2a4a6a, trim: 0xffffff, metal: 0xc0c0c8, energy: 0xa6e3ff, skin: 0xfde0c4, hair: 0xffffff },
  vine:        { primary: 0x2a4a2a, primaryDark: 0x0a1a0a, trim: 0xc8ff8a, metal: 0xc0c0c8, energy: 0x4ea84a, skin: 0xfde0c4, hair: 0x4a3018 },
  // Triple fusions
  brew:     { primary: 0xd4884a, primaryDark: 0x6a3a18, trim: 0xffd84a, metal: 0xa07060, energy: 0xff8a30, skin: 0xfde0c4, hair: 0x1a1010 },
  voidmage: { primary: 0x6a3aa8, primaryDark: 0x2a0a4a, trim: 0xd4a0ff, metal: 0xc0c0c8, energy: 0xb84aff, skin: 0xc8e0d8, hair: 0x000000 },
  beast:    { primary: 0x8a5a28, primaryDark: 0x3a1a00, trim: 0xc8ff8a, metal: 0x8a7058, energy: 0xff6a30, skin: 0xc8a070, hair: 0x4a2a10 },
  tide:     { primary: 0x2a8a6a, primaryDark: 0x0a3a2a, trim: 0xa6e3ff, metal: 0xc0c0c8, energy: 0x4cc9f0, skin: 0x4a8a6a, hair: 0x0a3a2a },
  // Mythic (4-element)
  necro:    { primary: 0x2a4a3a, primaryDark: 0x0a1a10, trim: 0xa6ffb0, metal: 0xc0c0c8, energy: 0x6aff8a, skin: 0xc8d8c0, hair: 0x000000 },
  phantom:  { primary: 0x2a0a4a, primaryDark: 0x0a000a, trim: 0xd4a0ff, metal: 0xc0c0c8, energy: 0xb84aff, skin: 0xc8c0d8, hair: 0x6a3a8a },
  luna:     { primary: 0x4a5aa8, primaryDark: 0x1a2a5a, trim: 0xc8d8ff, metal: 0xa6c0ff, energy: 0xc8d8ff, skin: 0xc8a070, hair: 0xeae4d4 },
  tinker:   { primary: 0xa05a28, primaryDark: 0x4a2a08, trim: 0xffd84a, metal: 0xc0c0c8, energy: 0xff8a3a, skin: 0xfde0c4, hair: 0xc73a1a },
};

// =====================================================================
// HAND CASTER — no weapon, channels energy between bare hands
// =====================================================================
function buildHandCaster(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- LEGS ----
  const legL = scene.add.rectangle(-3, 14, 3, 9, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const legR = scene.add.rectangle(3, 14, 3, 9, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const bootL = scene.add.rectangle(-3, 19, 4, 3, 0x2a1a08).setStrokeStyle(1, 0x000, 1);
  const bootR = scene.add.rectangle(3, 19, 4, 3, 0x2a1a08).setStrokeStyle(1, 0x000, 1);

  // ---- TORSO + CAPE ----
  const cape = scene.add.triangle(0, 6, 0, -5, -9, 14, 9, 14, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const torso = scene.add.ellipse(0, 4, 12, 13, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const belt = scene.add.rectangle(0, 9, 13, 2, p.trim);

  // ---- ARMS — both raised forward, palms cupped around energy ball ----
  // Both arms angled forward + slightly up
  const armL = scene.add.rectangle(-7, -1, 2.4, 9, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  armL.setRotation(0.6);
  const handL = scene.add.circle(-3, 4, 2, p.skin).setStrokeStyle(1, 0x4a3018, 1);
  const armR = scene.add.rectangle(7, -1, 2.4, 9, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  armR.setRotation(-0.6);
  const handR = scene.add.circle(3, 4, 2, p.skin).setStrokeStyle(1, 0x4a3018, 1);

  // (No glowing energy ball — heroes hold the spell shape, not a generic light orb.)

  // ---- HEAD ----
  // Flowing hair behind (no hat)
  const hair = scene.add.ellipse(0, -10, 12, 10, p.hair).setStrokeStyle(1, p.primaryDark, 1);
  const head = scene.add.circle(0, -10, 4.8, p.skin).setStrokeStyle(1, 0x4a3018, 1);
  const eyeL = scene.add.circle(-1.6, -10, 1.3, 0x000);
  const eyeR = scene.add.circle(1.6, -10, 1.3, 0x000);
  const eyeShineL = scene.add.circle(-1.2, -10.4, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.0, -10.4, 0.5, 0xffffff);
  // Circlet with gem
  const circlet = scene.add.rectangle(0, -13, 10, 1.6, p.trim);
  const gem = scene.add.circle(0, -13, 1.8, p.energy);

  c.add([cape, legL, legR, bootL, bootR, torso, belt, hair, head, eyeL, eyeR, eyeShineL, eyeShineR, circlet, gem,
    armL, handL, armR, handR]);

  // Cast animation: a clear "draw bow + release" — arms swing wide apart, body
  // leans back, then snap forward. Big amplitude so it reads even at small sizes.
  const castAnim = () => {
    // Phase 1 — DRAW (slow, deliberate). Arms pulled back/apart, body leans away.
    scene.tweens.add({
      targets: armL,
      rotation: 1.2, // far back-left
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({
      targets: armR,
      rotation: -1.2, // far back-right
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Hands swing outward with the arms — visible distance change.
    scene.tweens.add({
      targets: handL,
      x: "-=5",
      y: "-=2",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({
      targets: handR,
      x: "+=5",
      y: "-=2",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Whole body rocks back then forward.
    scene.tweens.add({
      targets: [torso, belt, head, hair, circlet, gem, eyeL, eyeR, cape],
      y: "+=2",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
  };

  return { castPoint: { x: 0, y: 4 }, castAnim };
}

// =====================================================================
// GUNNER (Sniper-style) — rifle + bullet, dwarf with full anatomy
// =====================================================================
function buildGunner(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- LEGS (short stocky dwarf legs + boots) ----
  const legL = scene.add.rectangle(-3, 13, 3.5, 7, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const legR = scene.add.rectangle(3, 13, 3.5, 7, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const bootL = scene.add.rectangle(-3, 18, 5, 3, 0x2a1a08).setStrokeStyle(1, 0x000, 1);
  const bootR = scene.add.rectangle(3, 18, 5, 3, 0x2a1a08).setStrokeStyle(1, 0x000, 1);

  // ---- TORSO (broad/stocky) ----
  const torso = scene.add.rectangle(0, 4, 14, 12, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const belt = scene.add.rectangle(0, 9, 16, 2, p.trim);
  // Bandolier across chest
  const bandolier = scene.add.rectangle(0, 1, 16, 1.6, p.trim);
  bandolier.setRotation(-0.4);
  // Bandolier pouches
  const pouch1 = scene.add.rectangle(-4, 0.5, 2, 2.2, 0x4a3018);
  const pouch2 = scene.add.rectangle(4, 1.5, 2, 2.2, 0x4a3018);

  // ---- ARMS (one supports barrel, one on trigger) ----
  // Left arm — supports rifle barrel forward
  const armL = scene.add.rectangle(7, 0, 8, 2.4, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const handL = scene.add.circle(11, 0, 1.6, p.skin).setStrokeStyle(1, 0x4a3018, 1);
  // Right arm — on the rifle trigger
  const armR = scene.add.rectangle(2, 4, 2.4, 6, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const handR = scene.add.circle(2, 7, 1.5, p.skin).setStrokeStyle(1, 0x4a3018, 1);

  // ---- HEAD ----
  const head = scene.add.circle(0, -8, 4.8, p.skin).setStrokeStyle(1, 0x4a3018, 1);
  // Big white beard (Sniper signature)
  const beard = scene.add.triangle(0, -3, 0, 6, -6, -3, 6, -3, 0xeae4d4).setStrokeStyle(1, 0x6a6a72, 1);
  // Goggles — larger for visibility
  const goggleL = scene.add.circle(-2, -8, 2.2, 0x000000).setStrokeStyle(1.4, p.metal, 1);
  const goggleR = scene.add.circle(2, -8, 2.2, 0x000000).setStrokeStyle(1.4, p.metal, 1);
  // Wide-brim hat
  const hatBrim = scene.add.ellipse(0, -10, 16, 3, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const hatCrown = scene.add.rectangle(0, -12, 8, 5, p.primaryDark).setStrokeStyle(1, 0x000, 1);

  // ---- RIFLE held in arms ----
  const rifleStock = scene.add.rectangle(0, 4, 6, 3, 0x4a3018).setStrokeStyle(1, 0x000, 1);
  const rifleBody = scene.add.rectangle(7, 1, 8, 2.6, 0x2a1a08).setStrokeStyle(1, 0x000, 1);
  const rifleScope = scene.add.rectangle(6, -1.6, 5, 1.8, p.metal).setStrokeStyle(1, 0x000, 1);
  const rifleBarrel = scene.add.rectangle(17, 0, 14, 1.6, p.metal).setStrokeStyle(1, 0x000, 1);
  const muzzle = scene.add.circle(24, 0, 1.4, p.energy);

  c.add([
    legL, legR, bootL, bootR,
    torso, belt, bandolier, pouch1, pouch2,
    rifleStock, rifleBody, rifleScope, rifleBarrel,
    armL, handL, armR, handR,
    head, beard, goggleL, goggleR, hatBrim, hatCrown, muzzle,
  ]);

  // Cast animation: clear rifle recoil — rifle and supporting arm jerk back hard,
  // whole body rocks back, muzzle flash flares.
  const rifleParts: Phaser.GameObjects.GameObject[] = [
    rifleStock, rifleBody, rifleScope, rifleBarrel, muzzle, armL, handL,
  ];
  const bodyParts: Phaser.GameObjects.GameObject[] = [
    torso, belt, bandolier, pouch1, pouch2, head, beard, goggleL, goggleR,
    hatBrim, hatCrown, armR, handR,
  ];
  const castAnim = () => {
    scene.tweens.add({
      targets: rifleParts,
      x: "-=6",
      duration: 80,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({
      targets: bodyParts,
      x: "-=2.5",
      duration: 100,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Muzzle flash flare at the barrel tip.
    const flash = scene.add.circle(c.x + 24 * c.scaleX, c.y + 0 * c.scaleY, 6, 0xfff066, 1);
    scene.tweens.add({
      targets: flash,
      scale: 0.3,
      alpha: 0,
      duration: 140,
      onComplete: () => flash.destroy(),
    });
  };

  return { castPoint: { x: 24, y: 0 }, castAnim };
}

// =====================================================================
// MAGE (Archmage) — staff + orb, with humanoid anatomy under the robe
// =====================================================================
function buildMage(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- LEGS peeking from under the robe ----
  const legL = scene.add.rectangle(-3, 16, 3, 5, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const legR = scene.add.rectangle(3, 16, 3, 5, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const slipL = scene.add.rectangle(-3, 19, 4, 2, 0x2a1a08).setStrokeStyle(1, 0x000, 1);
  const slipR = scene.add.rectangle(3, 19, 4, 2, 0x2a1a08).setStrokeStyle(1, 0x000, 1);

  // ---- ROBE (long flowing) ----
  const robe = scene.add.triangle(0, 8, 0, -10, -12, 18, 12, 18, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const trim = scene.add.rectangle(0, 16, 22, 3, p.trim).setStrokeStyle(1, p.primaryDark, 1);
  // ---- TORSO/CHEST ----
  const chest = scene.add.ellipse(0, -2, 12, 12, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const belt = scene.add.rectangle(0, 4, 16, 2.4, 0x3a2a18);
  const buckle = scene.add.rectangle(0, 4, 3, 2.4, p.trim);

  // ---- ARMS ----
  // Right arm holding staff out
  const armR = scene.add.rectangle(7, 0, 8, 2.4, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const handR = scene.add.circle(11, 0, 1.6, p.skin).setStrokeStyle(1, 0x4a3018, 1);
  // Left arm tucked across body (casting hand)
  const armL = scene.add.rectangle(-5, 0, 2.4, 7, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  armL.setRotation(0.3);
  const handL = scene.add.circle(-2, 4, 1.5, p.skin).setStrokeStyle(1, 0x4a3018, 1);

  // ---- HEAD ----
  const head = scene.add.circle(0, -10, 4.8, p.skin).setStrokeStyle(1, 0x4a3018, 1);
  // Eyes
  const eyeL = scene.add.circle(-1.6, -10, 1.2, 0x000);
  const eyeR = scene.add.circle(1.6, -10, 1.2, 0x000);
  const eyeShineL = scene.add.circle(-1.2, -10.4, 0.4, 0xffffff);
  const eyeShineR = scene.add.circle(2.0, -10.4, 0.4, 0xffffff);
  // Long beard (archmage)
  const beard = scene.add.triangle(0, -6, 0, 7, -5, -2, 5, -2, 0xeae4d4).setStrokeStyle(1, 0x6a6a72, 1);
  // Tall pointy hat with curl
  const hatBrim = scene.add.ellipse(0, -14, 14, 3.5, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const hatCone = scene.add.triangle(0, -22, 0, -11, -6, 4, 6, 4, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const hatTip = scene.add.circle(2, -28, 1.6, p.trim);
  const hatBand = scene.add.rectangle(0, -14, 13, 1.4, p.trim);

  // ---- STAFF ----
  // Pure shaft + small crystal topper, no glow halo.
  const staffShaft = scene.add.rectangle(13, -3, 1.6, 28, 0x4a3018);
  staffShaft.setRotation(0.05);
  const staffTopper = scene.add.triangle(13, -19, 0, -3, -2.5, 1, 2.5, 1, p.trim).setStrokeStyle(0.6, 0x000, 1);

  c.add([
    legL, legR, slipL, slipR,
    robe, trim, chest, belt, buckle,
    armR, handR, armL, handL,
    head, eyeL, eyeR, eyeShineL, eyeShineR, beard,
    hatBrim, hatCone, hatBand, hatTip,
    staffShaft, staffTopper,
  ]);

  // Cast animation: big staff swing forward — shaft rotates a lot, arm thrusts, body lifts.
  const castAnim = () => {
    scene.tweens.add({
      targets: staffShaft,
      rotation: -0.55,
      duration: 120,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Staff topper swings with the staff.
    scene.tweens.add({
      targets: staffTopper,
      x: "+=6",
      y: "-=3",
      duration: 120,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Staff arm thrusts forward visibly.
    scene.tweens.add({
      targets: [armR, handR],
      x: "+=4",
      duration: 120,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Body lifts and leans forward into the cast.
    scene.tweens.add({
      targets: [chest, head, beard, eyeL, eyeR, hatBrim, hatCone, hatBand, hatTip, armL, handL],
      y: "-=2",
      duration: 120,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
  };

  return { castPoint: { x: 13, y: -17 }, castAnim };
}

// =====================================================================
// DEMON — red-skinned horned fiend, claw hands, no humanoid robe.
// Inspired by Doom Bringer / Shadow Fiend silhouette + Lina's fiery aura.
// =====================================================================
function buildLina(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- LEGS — high-heeled boots peeking from skirt slit ----
  const legL = scene.add.rectangle(-2.5, 16, 2.4, 6, 0xfde0c4).setStrokeStyle(0.6, 0x6a3018, 1);
  const legR = scene.add.rectangle(2.5, 16, 2.4, 6, 0xfde0c4).setStrokeStyle(0.6, 0x6a3018, 1);
  const heelL = scene.add.triangle(-2.5, 21, 0, 0, -2, 2, 2, 2, 0x2a0500).setStrokeStyle(0.6, 0x000, 1);
  const heelR = scene.add.triangle(2.5, 21, 0, 0, -2, 2, 2, 2, 0x2a0500).setStrokeStyle(0.6, 0x000, 1);

  // ---- LONG RED DRESS with diamond cut-outs and flame trim ----
  // Slim hourglass skirt
  const skirt = scene.add.polygon(0, 9,
    [-3, -5, 3, -5, 7, 9, -7, 9],
    0xc73a1a,
  ).setStrokeStyle(1, 0x4a0a00, 1);
  // Gold flame trim along skirt hem
  const hem1 = scene.add.triangle(-5, 17, 0, 0, -1.5, 2, 1.5, 2, 0xffd84a);
  const hem2 = scene.add.triangle(-2, 18, 0, 0, -1.5, 2, 1.5, 2, 0xffd84a);
  const hem3 = scene.add.triangle(2, 18, 0, 0, -1.5, 2, 1.5, 2, 0xffd84a);
  const hem4 = scene.add.triangle(5, 17, 0, 0, -1.5, 2, 1.5, 2, 0xffd84a);
  // Diamond cut-out on side of skirt
  const cutout = scene.add.polygon(3, 11, [0, -2, 2, 0, 0, 2, -2, 0], 0xfde0c4).setStrokeStyle(0.6, 0xffd84a, 1);

  // ---- SLIM TORSO (cinched waist) ----
  const torso = scene.add.ellipse(0, -1, 9, 11, 0xc73a1a).setStrokeStyle(1, 0x4a0a00, 1);
  // Gold corset trim (V-neckline)
  const corsetL = scene.add.line(0, 0, -4, -5, 0, 2, 0xffd84a).setLineWidth(1.2).setOrigin(0, 0);
  const corsetR = scene.add.line(0, 0, 4, -5, 0, 2, 0xffd84a).setLineWidth(1.2).setOrigin(0, 0);
  // Gold belt buckle (jewel)
  const belt = scene.add.rectangle(0, 4, 11, 1.6, 0xffd84a);
  const beltGem = scene.add.polygon(0, 4, [0, -1.4, 1.4, 0, 0, 1.4, -1.4, 0], 0xff5a3a);

  // ---- LEFT ARM — gloved, lowered, palm cupped ----
  const armL = scene.add.rectangle(-6, 0, 2.2, 8, 0xc73a1a).setStrokeStyle(0.7, 0x4a0a00, 1);
  armL.setRotation(0.4);
  const gloveL = scene.add.rectangle(-6, 4, 2.2, 2, 0xffd84a);
  const handL = scene.add.circle(-7, 6, 1.5, 0xfde0c4).setStrokeStyle(0.6, 0x6a3018, 1);

  // ---- RIGHT ARM — lowered to her side, hand cupped at waist level ----
  const armR = scene.add.rectangle(6, 4, 2.2, 9, 0xc73a1a).setStrokeStyle(0.7, 0x4a0a00, 1);
  armR.setRotation(-0.15);
  const gloveR = scene.add.rectangle(8, 7, 2.4, 2, 0xffd84a);
  const handR = scene.add.circle(9, 8, 1.6, 0xfde0c4).setStrokeStyle(0.6, 0x6a3018, 1);

  // ---- SMALL FIRE FLAME resting in the cupped palm ----
  // Compact flame polygon — base sits ON the hand at y=8, tip rises to y=-1.
  // Well below head (head bottom ≈ y=-5.6), so flame is clearly held in hand.
  const fireOuter = scene.add.polygon(9, 4,
    [0, -5, 2, -2, 2.6, 1, 1, 3, -1, 3, -2.6, 1, -2, -2],
    0xff5a3a,
  ).setStrokeStyle(0.6, 0x4a0a00, 1);
  const fireMid = scene.add.polygon(9, 4,
    [0, -3.5, 1.4, -1.4, 1.8, 1, 0.8, 2, -0.8, 2, -1.8, 1, -1.4, -1.4],
    0xff8a3a,
  );
  const fireCore = scene.add.polygon(9, 4,
    [0, -2, 0.8, -0.6, 1, 0.6, 0, 1.4, -1, 0.6, -0.8, -0.6],
    0xfff5c0,
  );
  // Flame embers floating up from the palm
  const ember1 = scene.add.circle(11, -1, 0.8, 0xffd84a);
  const ember2 = scene.add.circle(7, -2, 0.6, 0xff8a3a);
  scene.tweens.add({ targets: ember1, y: "-=4", alpha: { from: 1, to: 0.2 }, yoyo: true, repeat: -1, duration: 700 });
  scene.tweens.add({ targets: ember2, y: "-=3", alpha: { from: 0.8, to: 0.1 }, yoyo: true, repeat: -1, duration: 900 });
  // Fire pillar idle pulse
  scene.tweens.add({ targets: [fireOuter, fireMid, fireCore], scaleY: { from: 1, to: 1.15 }, yoyo: true, repeat: -1, duration: 350, ease: "Sine.easeInOut" });

  // ---- HEAD ----
  const head = scene.add.circle(0, -10, 4.4, 0xfde0c4).setStrokeStyle(0.7, 0x6a3018, 1);
  // Eyes — fierce, with red eyeshadow
  const eyeShadowL = scene.add.ellipse(-1.6, -10.6, 3.2, 1.6, 0xff5a3a, 0.6);
  const eyeShadowR = scene.add.ellipse(1.6, -10.6, 3.2, 1.6, 0xff5a3a, 0.6);
  const eyeL = scene.add.circle(-1.6, -10, 1.3, 0x2a0500);
  const eyeR = scene.add.circle(1.6, -10, 1.3, 0x2a0500);
  const eyeShineL = scene.add.circle(-1.2, -10.4, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.0, -10.4, 0.5, 0xffffff);
  // Red lipstick
  const lips = scene.add.rectangle(0, -8, 1.8, 0.5, 0xc73a1a);

  // ---- HAIR ON FIRE — symmetric flame crown sitting ON her head ----
  // Use Graphics for predictable centered drawing (Phaser polygon setOrigin
  // can shift position based on bounding box, which caused visual left-lean).
  // Centered at (0, -14) which is top of head crown. Y is negative = upward.
  const hairFlameOuter = scene.add.graphics();
  hairFlameOuter.fillStyle(0xff5a3a, 1);
  hairFlameOuter.lineStyle(0.6, 0x4a0a00, 1);
  hairFlameOuter.beginPath();
  hairFlameOuter.moveTo(-6, -14);
  hairFlameOuter.lineTo(-7, -17);
  hairFlameOuter.lineTo(-5, -21);
  hairFlameOuter.lineTo(-3, -24);
  hairFlameOuter.lineTo(-1, -20);
  hairFlameOuter.lineTo(0, -28);
  hairFlameOuter.lineTo(1, -20);
  hairFlameOuter.lineTo(3, -24);
  hairFlameOuter.lineTo(5, -21);
  hairFlameOuter.lineTo(7, -17);
  hairFlameOuter.lineTo(6, -14);
  hairFlameOuter.closePath();
  hairFlameOuter.fillPath();
  hairFlameOuter.strokePath();

  const hairFlameMid = scene.add.graphics();
  hairFlameMid.fillStyle(0xff8a3a, 1);
  hairFlameMid.beginPath();
  hairFlameMid.moveTo(-4, -14);
  hairFlameMid.lineTo(-5, -17);
  hairFlameMid.lineTo(-3, -21);
  hairFlameMid.lineTo(-1, -18);
  hairFlameMid.lineTo(0, -26);
  hairFlameMid.lineTo(1, -18);
  hairFlameMid.lineTo(3, -21);
  hairFlameMid.lineTo(5, -17);
  hairFlameMid.lineTo(4, -14);
  hairFlameMid.closePath();
  hairFlameMid.fillPath();

  const hairFlameCore = scene.add.graphics();
  hairFlameCore.fillStyle(0xfff5c0, 1);
  hairFlameCore.beginPath();
  hairFlameCore.moveTo(-2, -14);
  hairFlameCore.lineTo(-3, -17);
  hairFlameCore.lineTo(-1, -19);
  hairFlameCore.lineTo(0, -24);
  hairFlameCore.lineTo(1, -19);
  hairFlameCore.lineTo(3, -17);
  hairFlameCore.lineTo(2, -14);
  hairFlameCore.closePath();
  hairFlameCore.fillPath();
  // Animate hair flames flickering (centered, no rotation drift)
  // Real fire effect — alpha shimmer only (no scaling = no bouncing).
  // Each layer pulses on its own clock so the flame flickers organically.
  scene.tweens.add({
    targets: hairFlameMid,
    alpha: { from: 1, to: 0.75 },
    yoyo: true,
    repeat: -1,
    duration: 220,
    ease: "Sine.easeInOut",
  });
  scene.tweens.add({
    targets: hairFlameCore,
    alpha: { from: 1, to: 0.55 },
    yoyo: true,
    repeat: -1,
    duration: 160,
    ease: "Sine.easeInOut",
  });
  // Floating ember sparks above her head
  const hairEmber1 = scene.add.circle(-3, -22, 0.7, 0xffd84a);
  const hairEmber2 = scene.add.circle(2, -24, 0.5, 0xff8a3a);
  scene.tweens.add({ targets: hairEmber1, y: "-=3", alpha: { from: 1, to: 0 }, yoyo: true, repeat: -1, duration: 700 });
  scene.tweens.add({ targets: hairEmber2, y: "-=4", alpha: { from: 0.9, to: 0.1 }, yoyo: true, repeat: -1, duration: 850 });

  // ---- GOLD TIARA / GEM (sits on forehead under flames) ----
  const tiara = scene.add.rectangle(-1, -13, 7, 1.4, 0xffd84a);
  const tiaraGem = scene.add.polygon(-1, -14, [0, -1.5, 1.5, 0, 0, 1.5, -1.5, 0], 0xff5a3a);

  void p; // Lina palette is fixed red/gold; element tint via projectile

  c.add([
    legL, legR, heelL, heelR,
    skirt, hem1, hem2, hem3, hem4, cutout,
    torso, corsetL, corsetR, belt, beltGem,
    armL, gloveL, handL,
    head, eyeShadowL, eyeShadowR, eyeL, eyeR, eyeShineL, eyeShineR, lips,
    tiara, tiaraGem,
    hairFlameOuter, hairFlameMid, hairFlameCore,
    hairEmber1, hairEmber2,
    armR, gloveR, handR,
    fireOuter, fireMid, fireCore, ember1, ember2,
  ]);

  const castAnim = () => {
    // Right arm thrusts upward — fire pillar surges higher.
    scene.tweens.add({
      targets: [armR, gloveR, handR, fireOuter, fireMid, fireCore],
      y: "-=4",
      duration: 100,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Fire pillar flares.
    scene.tweens.add({
      targets: [fireOuter, fireMid, fireCore],
      scaleY: { from: 1.8, to: 1 },
      scaleX: { from: 1.3, to: 1 },
      duration: 220,
      ease: "Quad.easeOut",
    });
    // Body leans forward — chin down, ponytail flames whip.
    scene.tweens.add({
      targets: [head, eyeL, eyeR, eyeShineL, eyeShineR, eyeShadowL, eyeShadowR, lips, tiara, tiaraGem],
      y: "+=1.5",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Hair flames flare with the cast
    scene.tweens.add({
      targets: [hairFlameOuter, hairFlameMid, hairFlameCore],
      scaleY: { from: 1.25, to: 1 },
      duration: 220,
      ease: "Quad.easeOut",
    });
  };

  return { castPoint: { x: 11, y: -17 }, orb: fireCore, castAnim };
}

// =====================================================================
// CRYSTAL MAIDEN (Rylai) — Frost mage. Fur-trim ushanka with snowflake brooch,
// blue robe with white fur trim, twin platinum braids, mittens, ice staff with
// crystal sphere top. Reference: Dota 2 Crystal Maiden — short, bundled-up
// winter mage radiating cold mist.
// =====================================================================
function buildCrystalMaiden(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- BOOTS — fur-trimmed winter boots ----
  const bootL = scene.add.rectangle(-3, 19, 4, 4, 0x3a2a18).setStrokeStyle(1, 0x000, 1);
  const bootR = scene.add.rectangle(3, 19, 4, 4, 0x3a2a18).setStrokeStyle(1, 0x000, 1);
  // Fur cuff
  const bootFurL = scene.add.ellipse(-3, 17, 5, 1.6, 0xeae4d4);
  const bootFurR = scene.add.ellipse(3, 17, 5, 1.6, 0xeae4d4);

  // ---- LONG BLUE ROBE with white fur trim hem ----
  const robe = scene.add.polygon(0, 9,
    [-3, -8, 3, -8, 9, 12, -9, 12],
    0x2a6aa8,
  ).setStrokeStyle(1, 0x0a2a4a, 1);
  // White fur trim along bottom hem
  const furHem = scene.add.ellipse(0, 14, 18, 3, 0xeae4d4).setStrokeStyle(0.6, 0xa6e3ff, 1);
  // Snowflake pattern on robe front
  const snowflake1 = scene.add.line(0, 0, -1.5, 4, 1.5, 4, 0xa6e3ff).setLineWidth(0.7).setOrigin(0, 0);
  const snowflake2 = scene.add.line(0, 0, 0, 2.5, 0, 5.5, 0xa6e3ff).setLineWidth(0.7).setOrigin(0, 0);
  const snowflakeDot = scene.add.circle(0, 4, 0.6, 0xffffff);
  // Light blue inner robe panel
  const robePanel = scene.add.triangle(0, 11, 0, -3, -3, 5, 3, 5, 0x4cc9f0, 0.4);

  // ---- TORSO with FUR-TRIMMED COAT collar ----
  const torso = scene.add.ellipse(0, -2, 11, 11, 0x2a6aa8).setStrokeStyle(1, 0x0a2a4a, 1);
  // Big fluffy white fur collar (Crystal Maiden's signature)
  const collarL = scene.add.ellipse(-4, -5, 5, 3, 0xeae4d4).setStrokeStyle(0.6, 0xa6e3ff, 1);
  const collarR = scene.add.ellipse(4, -5, 5, 3, 0xeae4d4).setStrokeStyle(0.6, 0xa6e3ff, 1);
  const collarMid = scene.add.ellipse(0, -4, 6, 2.5, 0xffffff).setStrokeStyle(0.6, 0xa6e3ff, 1);

  // ---- LEFT ARM — holding ICE STAFF, raised across body ----
  const armL = scene.add.rectangle(-5, 0, 2.2, 8, 0x2a6aa8).setStrokeStyle(0.7, 0x0a2a4a, 1);
  armL.setRotation(0.2);
  // Blue mitten
  const mittenL = scene.add.circle(-4, 5, 2, 0x4cc9f0).setStrokeStyle(0.6, 0x0a2a4a, 1);

  // ---- SHORT ICE WAND (no sphere on top — sphere moved to right hand) ----
  const staff = scene.add.rectangle(-4, -2, 2.4, 16, 0x6a4020).setStrokeStyle(0.8, 0x2a1008, 1);
  const iceA = scene.add.triangle(-5, 0, 0, 0, -3, -3, 0, 3, 0xa6e3ff);
  const iceB = scene.add.triangle(-3, 4, 0, 0, 3, -3, 0, 3, 0xa6e3ff);
  // Wand cap (gold tip)
  const staffCap = scene.add.rectangle(-4, -10, 3, 1.4, 0xffd84a);

  // ---- RIGHT ARM — extended, hand cradling the BLUE CRYSTAL BALL ----
  const armR = scene.add.rectangle(7, -1, 2.2, 8, 0x2a6aa8).setStrokeStyle(0.7, 0x0a2a4a, 1);
  armR.setRotation(-0.4);
  const mittenR = scene.add.circle(10, -4, 2.1, 0x4cc9f0).setStrokeStyle(0.6, 0x0a2a4a, 1);

  // ---- BLUE CRYSTAL SPHERE cradled directly in her right palm ----
  // Smaller ball, centered on the mitten so it sits IN her hand, not above.
  const sphereGlow = scene.add.circle(10, -4, 4.5, 0x4cc9f0, 0.55);
  const sphereOuter = scene.add.circle(10, -4, 3.4, 0xa6e3ff).setStrokeStyle(1, 0x4cc9f0, 1);
  const sphereInner = scene.add.circle(10, -4, 2.2, 0xffffff, 0.9);
  const sphereCore = scene.add.circle(10, -5, 1, 0xffffff);
  // Ice shards floating around the sphere (in hand)
  const shard1 = scene.add.triangle(6, -5, 0, -1.5, 1, 1.5, -1, 1.5, 0xa6e3ff);
  const shard2 = scene.add.triangle(14, -5, 0, -1.5, 1, 1.5, -1, 1.5, 0xa6e3ff);
  const shard3 = scene.add.triangle(10, -10, 0, -1.5, 1, 1.5, -1, 1.5, 0xffffff);
  scene.tweens.add({ targets: shard1, x: "-=1.2", y: "+=1", yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: shard2, x: "+=1.2", y: "+=1", yoyo: true, repeat: -1, duration: 900, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: shard3, y: "-=2", alpha: { from: 1, to: 0.4 }, yoyo: true, repeat: -1, duration: 700 });
  // Sphere idle pulse
  scene.tweens.add({ targets: sphereGlow, scale: { from: 1, to: 1.25 }, alpha: { from: 0.55, to: 0.25 }, yoyo: true, repeat: -1, duration: 1200, ease: "Sine.easeInOut" });

  // ---- HEAD — small face under huge fur ushanka ----
  const head = scene.add.circle(0, -10, 4.2, p.skin).setStrokeStyle(0.7, 0x88a6c0, 1);
  // Rosy cheeks (cold!)
  const cheekL = scene.add.circle(-2.5, -9, 0.8, 0xff8aaa, 0.5);
  const cheekR = scene.add.circle(2.5, -9, 0.8, 0xff8aaa, 0.5);
  // Eyes — bright icy blue, enlarged for visibility
  const eyeL = scene.add.circle(-1.5, -10, 1.4, 0x4cc9f0);
  const eyeR = scene.add.circle(1.5, -10, 1.4, 0x4cc9f0);
  const eyePupL = scene.add.circle(-1.5, -10, 0.6, 0x0a2a4a);
  const eyePupR = scene.add.circle(1.5, -10, 0.6, 0x0a2a4a);
  const eyeShineL = scene.add.circle(-1.1, -10.4, 0.4, 0xffffff);
  const eyeShineR = scene.add.circle(1.9, -10.4, 0.4, 0xffffff);
  // Small mouth
  const mouth = scene.add.rectangle(0, -8, 1.2, 0.4, 0xc06080);

  // ---- TWIN PLATINUM BRAIDS hanging from under hat ----
  const braidL = scene.add.rectangle(-5, -7, 1.8, 8, 0xeae4d4).setStrokeStyle(0.5, 0x88a6c0, 1);
  const braidLEnd = scene.add.circle(-5, -3, 1, 0xa6e3ff);
  const braidR = scene.add.rectangle(5, -7, 1.8, 8, 0xeae4d4).setStrokeStyle(0.5, 0x88a6c0, 1);
  const braidREnd = scene.add.circle(5, -3, 1, 0xa6e3ff);
  // Braid texture — small horizontal lines (cross-hatching)
  const braidLineL1 = scene.add.line(0, 0, -6, -8, -4, -8, 0x88a6c0).setLineWidth(0.5).setOrigin(0, 0);
  const braidLineL2 = scene.add.line(0, 0, -6, -5, -4, -5, 0x88a6c0).setLineWidth(0.5).setOrigin(0, 0);
  const braidLineR1 = scene.add.line(0, 0, 4, -8, 6, -8, 0x88a6c0).setLineWidth(0.5).setOrigin(0, 0);
  const braidLineR2 = scene.add.line(0, 0, 4, -5, 6, -5, 0x88a6c0).setLineWidth(0.5).setOrigin(0, 0);

  // ---- USHANKA HAT — fur winter hat with ear flaps + snowflake brooch ----
  // Main hat dome (dark blue)
  const hatDome = scene.add.ellipse(0, -14, 12, 5, 0x2a6aa8).setStrokeStyle(1, 0x0a2a4a, 1);
  // White fur band wrapping the hat
  const hatFurBand = scene.add.ellipse(0, -12, 13, 3, 0xffffff).setStrokeStyle(0.6, 0xa6e3ff, 1);
  // Hat ear flaps (fur tufts on each side)
  const earFlapL = scene.add.ellipse(-6, -11, 3.5, 4, 0xeae4d4).setStrokeStyle(0.6, 0xa6e3ff, 1);
  const earFlapR = scene.add.ellipse(6, -11, 3.5, 4, 0xeae4d4).setStrokeStyle(0.6, 0xa6e3ff, 1);
  // Top fur pom
  const hatPom = scene.add.circle(0, -17, 2, 0xffffff).setStrokeStyle(0.6, 0xa6e3ff, 1);
  // SNOWFLAKE BROOCH on the front of the hat (CM signature)
  const broochBg = scene.add.circle(0, -13.5, 1.6, 0x4cc9f0);
  const broochArm1 = scene.add.line(0, 0, -2, -13.5, 2, -13.5, 0xffffff).setLineWidth(0.7).setOrigin(0, 0);
  const broochArm2 = scene.add.line(0, 0, 0, -15.5, 0, -11.5, 0xffffff).setLineWidth(0.7).setOrigin(0, 0);
  const broochCenter = scene.add.circle(0, -13.5, 0.6, 0xffffff);

  void p; // CM uses fixed blue palette; element tint via projectile

  c.add([
    bootL, bootR, bootFurL, bootFurR,
    robe, robePanel, snowflake1, snowflake2, snowflakeDot, furHem,
    torso,
    staff, iceA, iceB, staffCap,
    armL, mittenL,
    armR, mittenR,
    collarL, collarR, collarMid,
    braidL, braidLineL1, braidLineL2, braidLEnd,
    braidR, braidLineR1, braidLineR2, braidREnd,
    head, cheekL, cheekR, eyeL, eyeR, eyePupL, eyePupR, eyeShineL, eyeShineR, mouth,
    earFlapL, earFlapR,
    hatDome, hatFurBand, hatPom,
    broochBg, broochArm1, broochArm2, broochCenter,
    sphereGlow, sphereOuter, sphereInner, sphereCore,
    shard1, shard2, shard3,
  ]);

  const castAnim = () => {
    // Right hand thrusts forward, crystal sphere flares.
    scene.tweens.add({
      targets: [armR, mittenR, sphereGlow, sphereOuter, sphereInner, sphereCore],
      x: "+=3",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({
      targets: [sphereGlow, sphereOuter, sphereInner, sphereCore],
      scale: { from: 1.5, to: 1 },
      duration: 220,
      ease: "Quad.easeOut",
    });
    // Body bobs slightly
    scene.tweens.add({
      targets: [head, eyeL, eyeR, eyePupL, eyePupR, mouth, cheekL, cheekR,
                hatDome, hatFurBand, hatPom, earFlapL, earFlapR,
                broochBg, broochArm1, broochArm2, broochCenter,
                braidL, braidR, braidLEnd, braidREnd,
                braidLineL1, braidLineL2, braidLineR1, braidLineR2],
      y: "+=1",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
  };

  return { castPoint: { x: 13, y: -5 }, orb: sphereCore, castAnim };
}

// =====================================================================
// EARTHSHAKER (Raigor Stonehoof) — bull-headed minotaur shaman.
// Massive curving horns, brown muscular body with green glowing tribal
// markings, hooves, holds a giant stone TOTEM in both hands above his head.
// Reference: Dota 2 Earthshaker — bipedal minotaur, totem-slammer, the
// definition of an AOE earth-quake hero.
// =====================================================================
function buildEarthshaker(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {

  // ---- HOOVED LEGS ----
  const legL = scene.add.rectangle(-4, 14, 4, 8, 0x6a4a28).setStrokeStyle(1, 0x2a1008, 1);
  const legR = scene.add.rectangle(4, 14, 4, 8, 0x6a4a28).setStrokeStyle(1, 0x2a1008, 1);
  // Tribal green stripe down each leg
  const legStripeL = scene.add.line(0, 0, -4, 11, -4, 17, 0x4ea84a).setLineWidth(0.7).setOrigin(0, 0);
  const legStripeR = scene.add.line(0, 0, 4, 11, 4, 17, 0x4ea84a).setLineWidth(0.7).setOrigin(0, 0);
  // Cloven hooves
  const hoofL1 = scene.add.triangle(-5, 20, 0, 0, -1.5, 2, 1.5, 2, 0x1a0500).setStrokeStyle(0.6, 0x000, 1);
  const hoofL2 = scene.add.triangle(-3, 20, 0, 0, -1.5, 2, 1.5, 2, 0x1a0500).setStrokeStyle(0.6, 0x000, 1);
  const hoofR1 = scene.add.triangle(3, 20, 0, 0, -1.5, 2, 1.5, 2, 0x1a0500).setStrokeStyle(0.6, 0x000, 1);
  const hoofR2 = scene.add.triangle(5, 20, 0, 0, -1.5, 2, 1.5, 2, 0x1a0500).setStrokeStyle(0.6, 0x000, 1);

  // ---- LOINCLOTH (tan tribal kilt with red trim) ----
  const loincloth = scene.add.polygon(0, 11,
    [-7, -2, 7, -2, 8, 6, -8, 6],
    0xb08a4f,
  ).setStrokeStyle(1, 0x4a3018, 1);
  // Red trim stripe
  const clothTrim = scene.add.rectangle(0, 14, 16, 1.4, 0xc73a1a);
  // Skull tied to belt
  const beltSkull = scene.add.circle(0, 9, 1.6, 0xeae4d4).setStrokeStyle(0.5, 0x4a3018, 1);
  const beltSkullEye1 = scene.add.circle(-0.6, 9, 0.3, 0x000);
  const beltSkullEye2 = scene.add.circle(0.6, 9, 0.3, 0x000);

  // ---- MASSIVE MUSCULAR TORSO ----
  const torso = scene.add.ellipse(0, 1, 18, 14, 0x6a4a28).setStrokeStyle(1, 0x2a1008, 1);
  // Pec separator
  const pecLine = scene.add.line(0, 0, 0, -5, 0, 4, 0x2a1008).setLineWidth(0.8).setOrigin(0, 0);
  // Tribal green glowing markings on chest (zigzag rune)
  const runeL1 = scene.add.line(0, 0, -5, -2, -3, 0, 0x4ea84a).setLineWidth(1.2).setOrigin(0, 0);
  const runeL2 = scene.add.line(0, 0, -3, 0, -5, 2, 0x4ea84a).setLineWidth(1.2).setOrigin(0, 0);
  const runeR1 = scene.add.line(0, 0, 5, -2, 3, 0, 0x4ea84a).setLineWidth(1.2).setOrigin(0, 0);
  const runeR2 = scene.add.line(0, 0, 3, 0, 5, 2, 0x4ea84a).setLineWidth(1.2).setOrigin(0, 0);
  scene.tweens.add({
    targets: [runeL1, runeL2, runeR1, runeR2],
    alpha: { from: 1, to: 0.4 },
    yoyo: true,
    repeat: -1,
    duration: 1100,
  });
  // Bone necklace
  const necklace1 = scene.add.line(0, 0, -3, -7, 3, -7, 0xeae4d4).setLineWidth(0.6).setOrigin(0, 0);
  const necklace2 = scene.add.rectangle(0, -6, 1.6, 2, 0xeae4d4);

  // ---- LEFT ARM — clenched fist raised in WAR ROAR pose (no weapon) ----
  const armL = scene.add.rectangle(-9, -2, 3, 10, 0x6a4a28).setStrokeStyle(1, 0x2a1008, 1);
  armL.setRotation(0.3);
  const bicepL = scene.add.circle(-10, -1, 2.6, 0x6a4a28).setStrokeStyle(0.7, 0x2a1008, 1);
  const wrapL = scene.add.rectangle(-12, -7, 3, 1.4, 0xc73a1a);
  // Clenched fist (knuckles)
  const fistL = scene.add.circle(-12, -8, 2.2, 0x6a4a28).setStrokeStyle(0.7, 0x2a1008, 1);
  const knuckle1 = scene.add.circle(-13, -9, 0.5, 0x4a3018);
  const knuckle2 = scene.add.circle(-12, -9.5, 0.5, 0x4a3018);
  const knuckle3 = scene.add.circle(-11, -9, 0.5, 0x4a3018);

  // ---- RIGHT ARM — extends down-forward, gripping TOTEM HAFT at side ----
  const armR = scene.add.rectangle(8, 2, 3, 10, 0x6a4a28).setStrokeStyle(1, 0x2a1008, 1);
  armR.setRotation(-0.15);
  const bicepR = scene.add.circle(8, 0, 2.6, 0x6a4a28).setStrokeStyle(0.7, 0x2a1008, 1);
  const wrapR = scene.add.rectangle(11, 7, 3, 1.4, 0xc73a1a);
  const handR = scene.add.circle(11, 9, 1.8, 0x6a4a28).setStrokeStyle(0.7, 0x2a1008, 1);

  // ---- TOTEM (held HORIZONTALLY at hip — like a war club at the side) ----
  // Wooden haft (long horizontal shaft)
  const haft = scene.add.rectangle(16, 9, 14, 1.6, 0x4a3018).setStrokeStyle(0.7, 0x2a1008, 1);
  // Stone HEAD of the totem (chunky boulder at the far end)
  const totem = scene.add.rectangle(22, 8, 10, 9, 0x8a7058).setStrokeStyle(1.4, 0x2a1008, 1);
  // Pointed cap on totem
  const totemCap = scene.add.triangle(26, 8, 0, -3.5, 3, 0, 0, 3.5, 0x6a5040).setStrokeStyle(0.7, 0x2a1008, 1);
  // Stone bands (rope wrap on the totem)
  const totemBand = scene.add.rectangle(22, 8, 1.2, 7, 0x4a3018);
  // Carved glowing rune on totem face (single eye)
  const totemEye = scene.add.circle(22, 8, 1.4, 0x4ea84a);
  const totemRune1 = scene.add.rectangle(22, 11, 4, 0.6, 0x4ea84a);
  const totemRune2 = scene.add.rectangle(22, 5, 4, 0.6, 0x4ea84a);
  const totemRune3 = scene.add.line(0, 0, 20, 8, 24, 8, 0x4ea84a).setLineWidth(0.5).setOrigin(0, 0);
  scene.tweens.add({
    targets: [totemRune1, totemRune2, totemEye],
    alpha: { from: 1, to: 0.5 },
    yoyo: true,
    repeat: -1,
    duration: 900,
  });
  // Cracks on totem (zigzag stress lines)
  const crack1 = scene.add.line(0, 0, 20, 6, 21, 9, 0x2a1008).setLineWidth(0.5).setOrigin(0, 0);
  const crack2 = scene.add.line(0, 0, 23, 6, 24, 10, 0x2a1008).setLineWidth(0.5).setOrigin(0, 0);
  // Spike studs on totem corners
  const stud1 = scene.add.triangle(19, 5, 0, 0, -1, 1.5, 1, 1.5, 0xeae4d4);
  const stud2 = scene.add.triangle(19, 11, 0, 0, -1, -1.5, 1, -1.5, 0xeae4d4);

  // ---- BULL HEAD ----
  // Large blocky muzzle below the eye line
  const muzzle = scene.add.ellipse(0, -7, 9, 6, 0x4a3018).setStrokeStyle(1, 0x2a1008, 1);
  // Nostril dots
  const nostrilL = scene.add.circle(-1.6, -6, 0.5, 0x000);
  const nostrilR = scene.add.circle(1.6, -6, 0.5, 0x000);
  // Upper bull head
  const head = scene.add.ellipse(0, -11, 11, 8, 0x6a4a28).setStrokeStyle(1, 0x2a1008, 1);
  // Tribal forehead paint
  const facePaint = scene.add.line(0, 0, -2, -13, 2, -13, 0xc73a1a).setLineWidth(0.8).setOrigin(0, 0);
  // Glowing red eyes (deep brow)
  const eyeBrowL = scene.add.rectangle(-2.5, -12.5, 3, 0.6, 0x2a1008);
  const eyeBrowR = scene.add.rectangle(2.5, -12.5, 3, 0.6, 0x2a1008);
  const eyeL = scene.add.circle(-2.5, -11, 1.6, 0xff3a3a);
  const eyeR = scene.add.circle(2.5, -11, 1.6, 0xff3a3a);
  const eyeShineL = scene.add.circle(-2.1, -11.4, 0.6, 0xfff066);
  const eyeShineR = scene.add.circle(2.9, -11.4, 0.6, 0xfff066);
  // Bull ear nubs
  const earL = scene.add.ellipse(-6, -11, 2.4, 1.6, 0x4a3018).setRotation(-0.4);
  const earR = scene.add.ellipse(6, -11, 2.4, 1.6, 0x4a3018).setRotation(0.4);

  // ---- MASSIVE CURVING HORNS ----
  // Each horn: thick base curving outward then up, ivory color, dark tip
  const hornLBase = scene.add.ellipse(-5, -14, 3, 4, 0xeae4d4).setStrokeStyle(0.7, 0x4a3018, 1).setRotation(-0.3);
  const hornLMid = scene.add.ellipse(-8, -16, 2.4, 4, 0xeae4d4).setStrokeStyle(0.7, 0x4a3018, 1).setRotation(-0.6);
  const hornLTip = scene.add.triangle(-10, -19, 0, 4, -1.4, -3, 1.4, -3, 0xc8b890).setStrokeStyle(0.6, 0x4a3018, 1);
  const hornRBase = scene.add.ellipse(5, -14, 3, 4, 0xeae4d4).setStrokeStyle(0.7, 0x4a3018, 1).setRotation(0.3);
  const hornRMid = scene.add.ellipse(8, -16, 2.4, 4, 0xeae4d4).setStrokeStyle(0.7, 0x4a3018, 1).setRotation(0.6);
  const hornRTip = scene.add.triangle(10, -19, 0, 4, -1.4, -3, 1.4, -3, 0xc8b890).setStrokeStyle(0.6, 0x4a3018, 1);
  // Gold horn rings
  const hornRingL = scene.add.ellipse(-7, -15, 3.2, 1, 0xffd84a).setRotation(-0.5);
  const hornRingR = scene.add.ellipse(7, -15, 3.2, 1, 0xffd84a).setRotation(0.5);

  // ---- NOSE RING ----
  const noseRing = scene.add.circle(0, -5, 1.4, 0, 0).setStrokeStyle(1, 0xffd84a, 1);

  void p; // Earthshaker uses fixed brown/green palette; element tint via projectile

  c.add([
    legL, legR, legStripeL, legStripeR,
    hoofL1, hoofL2, hoofR1, hoofR2,
    loincloth, clothTrim, beltSkull, beltSkullEye1, beltSkullEye2,
    torso, pecLine, runeL1, runeL2, runeR1, runeR2,
    necklace1, necklace2,
    armR, bicepR, wrapR, handR,
    haft, totem, totemBand, totemCap,
    totemRune1, totemRune2, totemRune3, totemEye,
    crack1, crack2, stud1, stud2,
    head, muzzle, nostrilL, nostrilR, noseRing,
    facePaint, eyeBrowL, eyeBrowR, eyeL, eyeR, eyeShineL, eyeShineR,
    earL, earR,
    hornLBase, hornLMid, hornLTip, hornRingL,
    hornRBase, hornRMid, hornRTip, hornRingR,
    armL, bicepL, wrapL, fistL, knuckle1, knuckle2, knuckle3,
  ]);

  const castAnim = () => {
    // Right arm + totem swing forward+down (slam motion).
    scene.tweens.add({
      targets: [armR, bicepR, wrapR, handR,
                haft, totem, totemBand, totemCap,
                totemRune1, totemRune2, totemRune3, totemEye,
                crack1, crack2, stud1, stud2],
      x: "+=3",
      y: "+=4",
      duration: 110,
      yoyo: true,
      ease: "Quad.easeIn",
    });
    // Left fist pumps up — war roar.
    scene.tweens.add({
      targets: [armL, bicepL, wrapL, fistL, knuckle1, knuckle2, knuckle3],
      y: "-=3",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Body squat (knees bend on slam)
    scene.tweens.add({
      targets: [torso, pecLine, runeL1, runeL2, runeR1, runeR2,
                necklace1, necklace2, head, muzzle, nostrilL, nostrilR, noseRing,
                facePaint, eyeBrowL, eyeBrowR, eyeL, eyeR, eyeShineL, eyeShineR,
                earL, earR,
                hornLBase, hornLMid, hornLTip, hornRingL,
                hornRBase, hornRMid, hornRTip, hornRingR],
      y: "+=2",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Totem runes flare
    scene.tweens.add({
      targets: [totemRune1, totemRune2, totemRune3, totemEye],
      scale: { from: 1.6, to: 1 },
      duration: 220,
      ease: "Quad.easeOut",
    });
    // Eyes flash
    scene.tweens.add({
      targets: [eyeL, eyeR],
      scale: { from: 1.6, to: 1 },
      duration: 220,
      ease: "Quad.easeOut",
    });
  };

  // Cast point: tip of totem (where the slam connects)
  return { castPoint: { x: 26, y: 8 }, orb: totemEye, castAnim };
}

// =====================================================================
// MIRANA (Moon Rider) — bow-and-quiver archer.
// Silhouette: D-curve bow on left, quiver on back, crescent moon helmet.
// Fires rightward toward enemies — arrow nocked on the string, draw arm
// on the right, bow arm on the left.
// =====================================================================
function buildMirana(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {

  // ---- QUIVER (drawn first — sits on the back behind everything) ----
  // Small rectangular quiver at back-left.
  const quiver = scene.add.rectangle(-7, 1, 3, 9, 0x4a3018).setStrokeStyle(1, 0x000, 1);
  // Three arrow shafts protruding upward from quiver.
  const qs1 = scene.add.line(0, 0, -6,  -2, -6,  -8, 0xe8eef7).setLineWidth(0.7).setOrigin(0, 0);
  const qs2 = scene.add.line(0, 0, -7.5, -1, -7.5, -7, 0xe8eef7).setLineWidth(0.7).setOrigin(0, 0);
  const qs3 = scene.add.line(0, 0, -8.5, -1, -8.5, -6, 0xe8eef7).setLineWidth(0.7).setOrigin(0, 0);

  // ---- LEGS ----
  const legL = scene.add.rectangle(-3, 14, 3, 9, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const legR = scene.add.rectangle(3, 14, 3, 9, p.primaryDark).setStrokeStyle(1, 0x000, 1);
  const bootL = scene.add.rectangle(-3, 19, 4, 3, p.metal).setStrokeStyle(1, 0x000, 1);
  const bootR = scene.add.rectangle(3, 19, 4, 3, p.metal).setStrokeStyle(1, 0x000, 1);

  // ---- TORSO — silver chestplate over nature-green backing ----
  const torso = scene.add.ellipse(0, 4, 13, 13, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const chestPlate = scene.add.ellipse(0, 3, 9, 8, p.metal).setStrokeStyle(0.7, p.primaryDark, 1);
  // Moon emblem on chest (small crescent outline).
  const chestMoon = scene.add.circle(0, 3, 2.8, 0, 0).setStrokeStyle(1.1, p.energy, 1);
  const belt = scene.add.rectangle(0, 9, 13, 2, p.trim);

  // ---- BOW — D-curve arc, center (-3, 2), radius 11, opening rightward ----
  // Arc from 90° (bottom tip) clockwise through 180° (left spine) to 270° (top tip).
  // fillAlpha=0 → only the stroke arc is visible.
  const bow = scene.add.arc(-3, 2, 11, 90, 270, false, 0, 0).setStrokeStyle(3.5, 0x5a3c18, 1);
  // Limb tips — small triangles at bow ends for that recurve flare.
  // Top tip is at (-3, -9); bottom tip at (-3, 13).
  const bowTipTop = scene.add.triangle(-3, -9, -1.5, -2, 1.5, -2, 0, 1.8, 0x3a2608);
  const bowTipBot = scene.add.triangle(-3, 13, -1.5, 2, 1.5, 2, 0, -1.8, 0x3a2608);

  // ---- BOWSTRING — two lines forming a V to the draw point at (5, 2) ----
  const stringTop = scene.add.line(0, 0, -3, -9, 5, 2, 0xe8eef7).setLineWidth(0.8).setOrigin(0, 0);
  const stringBot = scene.add.line(0, 0, -3, 13, 5, 2, 0xe8eef7).setLineWidth(0.8).setOrigin(0, 0);

  // ---- BOW ARM (left) — extends to grip at the bow center ----
  const armL = scene.add.rectangle(-5, 1, 2.4, 8, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  armL.setRotation(0.3);
  const handL = scene.add.circle(-4, 3, 1.8, p.skin).setStrokeStyle(1, 0x4a3018, 1);

  // ---- NOCKED ARROW on string — lies horizontal, head pointing right ----
  // Center at draw point area (x~8, y~2); shaft length 14px.
  const arrowShaft = scene.add.rectangle(8, 2, 14, 1.2, 0xe8eef7, 1);
  const arrowHead  = scene.add.triangle(16, 2, 0, -2.5, 4, 0, 0, 2.5, p.trim);
  const arrowFletch = scene.add.triangle(1, 2, 0, 0, -3, -2.2, -3, 2.2, p.energy);

  // ---- DRAW ARM (right) — pulled back, fingers on string at (5, 2) ----
  const armR = scene.add.rectangle(5, 0, 2.4, 7, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  armR.setRotation(-0.25);
  const handR = scene.add.circle(6, 2, 1.8, p.skin).setStrokeStyle(1, 0x4a3018, 1);

  // ---- HEAD + MOON-CRESCENT HELMET ----
  const hair = scene.add.ellipse(0, -11, 12, 9, 0xe8eef7).setStrokeStyle(1, p.primaryDark, 1);
  const head = scene.add.circle(0, -10, 4.8, p.skin).setStrokeStyle(1, 0x4a3018, 1);
  const eyeL = scene.add.circle(-1.6, -10, 1.3, 0x000);
  const eyeR = scene.add.circle(1.6, -10, 1.3, 0x000);
  const eyeShineL = scene.add.circle(-1.2, -10.4, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.0, -10.4, 0.5, 0xffffff);
  // Helmet base plate.
  const helmBase = scene.add.rectangle(0, -13, 10, 2.8, p.metal).setStrokeStyle(1.2, p.primaryDark, 1);
  // Crescent moon atop the helmet: two rising horn triangles + a center dot — taller.
  const hornL = scene.add.triangle(-2, -15, -1, 0, -3.5, -5, 0, -5, p.energy);
  const hornR = scene.add.triangle( 2, -15,  1, 0,  3.5, -5, 0, -5, p.energy);
  const moonDot = scene.add.circle(0, -19, 2, p.energy);

  // ---- Z-ORDER: back → front ----
  c.add([
    quiver, qs1, qs2, qs3,
    legL, legR, bootL, bootR,
    torso, chestPlate, chestMoon, belt,
    bow, bowTipTop, bowTipBot,
    stringTop, stringBot,
    armL, handL,
    arrowShaft, arrowHead, arrowFletch,
    armR, handR,
    hair, head, eyeL, eyeR,
    helmBase, hornL, hornR, moonDot,
  ]);

  // ---- CAST ANIMATION: draw + release ----
  // Phase 1 – DRAW (brief extra pull-back on the string; anticipation).
  // Phase 2 – RELEASE (yoyo snaps everything back).
  const castAnim = () => {
    // Draw arm pulls FURTHER right (more tension on string).
    scene.tweens.add({
      targets: [armR, handR],
      x: "+=5",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeIn",
    });
    // Arrow shaft + fletching + both string halves follow the draw.
    scene.tweens.add({
      targets: [arrowShaft, arrowHead, arrowFletch, stringTop, stringBot],
      x: "+=4",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeIn",
    });
    // Bow arm braces — slight forward push as limbs flex.
    scene.tweens.add({
      targets: [armL, handL, bow, bowTipTop, bowTipBot],
      x: "-=1.5",
      duration: 120,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Body leans into the shot — head tilts forward (aiming down the shaft).
    scene.tweens.add({
      targets: [torso, chestPlate, chestMoon, belt,
                head, hair, eyeL, eyeR, helmBase, hornL, hornR, moonDot],
      y: "+=1.5",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
  };

  // Cast point: arrowhead tip position (right of the arrow head triangle).
  return { castPoint: { x: 20, y: 2 }, castAnim };
}

// =====================================================================
// STORM SPIRIT (Raijin Thunderkeg) — Hovering lightning sage. Body fades
// into a swirling thunderhead at the legs, both arms spread cradling a
// crackling sphere of ball lightning. Topknot, mustache, monk robes.
// Reference: Dota 2 Storm Spirit. Standard size (no scale-up).
// =====================================================================
function buildZeus(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {

  // Storm Spirit visual hue: cyan/violet (electric purple-blue) instead of lava-orange.
  const ELECTRIC = 0x9aa8ff;
  const ELECTRIC_HOT = 0xeaf4ff;
  const ROBE = 0x3a4a8a;
  const ROBE_DARK = 0x1a2050;
  const SASH = 0xffd84a;

  // ---- THUNDERHEAD CLOUD (legs replaced with swirling lightning cloud) ----
  const cloudOuter = scene.add.ellipse(0, 16, 26, 9, ROBE_DARK, 0.85).setStrokeStyle(0.8, 0x000020, 1);
  const cloudMid = scene.add.ellipse(-4, 14, 14, 7, ROBE, 0.9);
  const cloudHi = scene.add.ellipse(5, 15, 12, 5, 0x6a7ad8, 0.85);
  scene.tweens.add({ targets: [cloudMid, cloudHi], x: "+=2", yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });
  // Tiny lightning forks dancing inside the cloud
  const cloudBolt1 = scene.add.line(0, 0, -6, 13, -3, 18, ELECTRIC_HOT, 1).setLineWidth(1).setOrigin(0, 0);
  const cloudBolt2 = scene.add.line(0, 0, 4, 14, 7, 19, ELECTRIC_HOT, 1).setLineWidth(1).setOrigin(0, 0);
  scene.tweens.add({ targets: [cloudBolt1, cloudBolt2], alpha: { from: 0, to: 1 }, yoyo: true, repeat: -1, duration: 220 });

  // ---- ROBE HEM (tapers up from cloud into the body) ----
  const robeHem = scene.add.polygon(0, 7, [
    -10, 4, 10, 4, 8, -6, -8, -6,
  ], ROBE).setStrokeStyle(1, ROBE_DARK, 1);
  const robeFoldL = scene.add.line(0, 0, -4, 0, -5, 8, ROBE_DARK, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  const robeFoldR = scene.add.line(0, 0, 4, 0, 5, 8, ROBE_DARK, 0.7).setLineWidth(0.6).setOrigin(0, 0);

  // ---- TORSO — monk robe, golden sash diagonal ----
  const torso = scene.add.ellipse(0, -2, 14, 13, ROBE).setStrokeStyle(1, ROBE_DARK, 1);
  const sash = scene.add.rectangle(0, 0, 16, 2.4, SASH).setStrokeStyle(0.5, 0x6a4000, 1).setRotation(-0.35);
  const sashKnot = scene.add.circle(7, 1, 1.4, SASH).setStrokeStyle(0.4, 0x6a4000, 1);
  // Glowing sigil on chest
  const sigil = scene.add.polygon(0, -2, [0, -2, 2, 0, 0, 2, -2, 0], ELECTRIC_HOT).setStrokeStyle(0.5, ELECTRIC, 1);
  scene.tweens.add({ targets: sigil, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 500 });

  // ---- ARMS — both spread wide cradling a ball-lightning sphere in front ----
  const armL = scene.add.rectangle(-7, -1, 2.6, 9, ROBE).setStrokeStyle(0.8, ROBE_DARK, 1).setRotation(0.65);
  const handL = scene.add.circle(-3, 4, 1.8, p.skin).setStrokeStyle(0.7, 0x4a3018, 1);
  const armR = scene.add.rectangle(7, -1, 2.6, 9, ROBE).setStrokeStyle(0.8, ROBE_DARK, 1).setRotation(-0.65);
  const handR = scene.add.circle(3, 4, 1.8, p.skin).setStrokeStyle(0.7, 0x4a3018, 1);

  // ---- BALL LIGHTNING (cradled between hands — the cast orb) ----
  const orbAura = scene.add.circle(0, 4, 6, ELECTRIC, 0.45);
  const orbCore = scene.add.circle(0, 4, 3, ELECTRIC_HOT, 1).setStrokeStyle(0.6, ELECTRIC, 1);
  const orbHot = scene.add.circle(0, 4, 1.4, 0xffffff);
  scene.tweens.add({ targets: orbAura, scale: { from: 0.85, to: 1.3 }, alpha: { from: 0.3, to: 0.7 }, yoyo: true, repeat: -1, duration: 320 });
  scene.tweens.add({ targets: orbCore, scale: { from: 0.95, to: 1.1 }, yoyo: true, repeat: -1, duration: 200 });
  // Crackling forks emanating from the orb
  const fork1 = scene.add.line(0, 0, 0, 4, -5, 0, ELECTRIC_HOT, 1).setLineWidth(0.9).setOrigin(0, 0);
  const fork2 = scene.add.line(0, 0, 0, 4, 5, 0, ELECTRIC_HOT, 1).setLineWidth(0.9).setOrigin(0, 0);
  const fork3 = scene.add.line(0, 0, 0, 4, 0, 9, ELECTRIC_HOT, 1).setLineWidth(0.9).setOrigin(0, 0);
  scene.tweens.add({ targets: [fork1, fork2, fork3], alpha: { from: 0.3, to: 1 }, yoyo: true, repeat: -1, duration: 180 });

  // ---- HEAD — golden skin with topknot, drooping mustache ----
  const head = scene.add.circle(0, -12, 4.8, p.skin).setStrokeStyle(0.9, 0x4a3018, 1);
  // Brows + glowing white eyes
  const browL = scene.add.rectangle(-1.8, -13, 2.4, 0.7, 0x000);
  const browR = scene.add.rectangle(1.8, -13, 2.4, 0.7, 0x000);
  const eyeGlowL = scene.add.circle(-1.8, -12, 1.6, ELECTRIC_HOT, 0.85);
  const eyeGlowR = scene.add.circle(1.8, -12, 1.6, ELECTRIC_HOT, 0.85);
  const eyeShineL = scene.add.circle(-1.2, -12.4, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.4, -12.4, 0.5, 0xffffff);
  scene.tweens.add({ targets: [eyeGlowL, eyeGlowR], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 460 });
  // Drooping mustache
  const stacheL = scene.add.line(0, 0, -1.5, -10, -3.5, -7, 0x1a1010, 1).setLineWidth(0.9).setOrigin(0, 0);
  const stacheR = scene.add.line(0, 0, 1.5, -10, 3.5, -7, 0x1a1010, 1).setLineWidth(0.9).setOrigin(0, 0);

  // ---- TOPKNOT (signature Storm Spirit hair) ----
  const topknotBase = scene.add.rectangle(0, -16, 1.6, 3, 0x1a1010);
  const topknotBall = scene.add.circle(0, -19, 2.4, 0x1a1010).setStrokeStyle(0.5, 0x000, 1);
  // Hair behind/back
  const hairBack = scene.add.ellipse(0, -11, 9, 7, 0x1a1010);

  // ---- HOVER GLOW (electric corona around head/shoulders) ----
  const corona = scene.add.circle(0, -8, 14, ELECTRIC, 0.16);
  scene.tweens.add({ targets: corona, scale: { from: 0.95, to: 1.12 }, alpha: { from: 0.1, to: 0.28 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });

  // ---- Z-ORDER ----
  c.add([
    corona,
    cloudOuter, cloudMid, cloudHi, cloudBolt1, cloudBolt2,
    robeHem, robeFoldL, robeFoldR,
    torso, sash, sashKnot, sigil,
    armL, handL, armR, handR,
    orbAura, orbCore, orbHot, fork1, fork2, fork3,
    hairBack, head, browL, browR, eyeGlowL, eyeGlowR, eyeShineL, eyeShineR, stacheL, stacheR,
    topknotBase, topknotBall,
  ]);

  // ---- IDLE HOVER — body bobs slowly above the cloud ----
  scene.tweens.add({
    targets: [robeHem, robeFoldL, robeFoldR, torso, sash, sashKnot, sigil, armL, handL, armR, handR, orbAura, orbCore, orbHot, fork1, fork2, fork3, hairBack, head, browL, browR, eyeGlowL, eyeGlowR, eyeShineL, eyeShineR, stacheL, stacheR, topknotBase, topknotBall, corona],
    y: "-=1.4",
    yoyo: true,
    repeat: -1,
    duration: 950,
    ease: "Sine.easeInOut",
  });

  // ---- CAST ANIMATION — orb flares, body leans into the bolt, cloud rumbles ----
  const castAnim = () => {
    scene.tweens.add({
      targets: [orbAura, orbCore, orbHot],
      scale: { from: 2, to: 1 },
      alpha: { from: 1, to: 0.6 },
      duration: 260,
      ease: "Quad.easeOut",
    });
    scene.tweens.add({
      targets: [fork1, fork2, fork3, cloudBolt1, cloudBolt2],
      alpha: { from: 1, to: 0.3 },
      duration: 220,
      ease: "Quad.easeOut",
    });
    scene.tweens.add({
      targets: corona,
      scale: { from: 1.5, to: 1 },
      alpha: { from: 0.6, to: 0.16 },
      duration: 280,
      ease: "Quad.easeOut",
    });
    // Body recoil (forward)
    scene.tweens.add({
      targets: [torso, sash, sashKnot, sigil, head, hairBack, topknotBase, topknotBall],
      y: "+=1.5",
      duration: 100,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
  };

  // Cast point: the ball-lightning sphere held in front.
  return { castPoint: { x: 0, y: 4 }, orb: orbCore, castAnim };
}

// =====================================================================
// UNDERLORD (Vrogros) — Colossal abyssal demon lord. Massive armored body
// with glowing lava-crack veins, enormous bull horns, burning red eyes.
// Firestorm on cast: fists slam down, lava erupts.
// Reference: Dota 2 Underlord.
// =====================================================================
function buildUnderlord(scene: Phaser.Scene, c: Phaser.GameObjects.Container, _p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  const ARMOR    = 0x1a0818;  // dark purple-black plate
  const ARMOR_HI = 0x3a1832;  // slightly lighter purple
  const LAVA     = 0xff4400;  // deep lava orange-red
  const LAVA_HOT = 0xff8800;  // bright lava glow
  const HORN     = 0x2a1208;  // dark bull horn
  const SKIN     = 0x3a1810;  // dark demon skin (exposed areas)
  const EYE      = 0xff2200;  // fiery red eye

  // ---- GROUND SHADOW ----
  const shadow = scene.add.ellipse(0, 24, 28, 5, 0x000000, 0.4);

  // ---- LAVA POOL at feet — molten rock gathers below him ----
  const lavaPool    = scene.add.ellipse(0, 23, 28, 7, LAVA, 0.3);
  const lavaPoolHot = scene.add.ellipse(0, 23, 16, 4, LAVA_HOT, 0.18);
  scene.tweens.add({ targets: lavaPool,    scaleX: { from: 0.9, to: 1.1 }, alpha: { from: 0.2, to: 0.45 }, yoyo: true, repeat: -1, duration: 820, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: lavaPoolHot, scaleX: { from: 0.82, to: 1.15 }, alpha: { from: 0.12, to: 0.32 }, yoyo: true, repeat: -1, duration: 620 });

  // ---- LEGS — heavy armored boots, wide stance ----
  const legL     = scene.add.rectangle(-6, 16, 9, 12, ARMOR).setStrokeStyle(1, 0x000, 1);
  const legR     = scene.add.rectangle( 6, 16, 9, 12, ARMOR).setStrokeStyle(1, 0x000, 1);
  const bootVeinL = scene.add.line(0, 0, -6, 11, -6, 22, LAVA, 0.8).setLineWidth(0.9).setOrigin(0, 0);
  const bootVeinR = scene.add.line(0, 0,  6, 11,  6, 22, LAVA, 0.8).setLineWidth(0.9).setOrigin(0, 0);
  scene.tweens.add({ targets: [bootVeinL, bootVeinR], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 750 });

  // ---- TORSO — wide armored chest ----
  const torso      = scene.add.rectangle(0, 2, 22, 18, ARMOR).setStrokeStyle(1.2, 0x000, 1);
  const chestPlate = scene.add.rectangle(0, 0, 17, 13, ARMOR_HI).setStrokeStyle(0.7, 0x000, 1);
  // Lava veins cracking across the chest
  const cv1 = scene.add.line(0, 0, -5, -4, -2,  2, LAVA,     1   ).setLineWidth(1.2).setOrigin(0, 0);
  const cv2 = scene.add.line(0, 0, -2,  2,  4,  6, LAVA_HOT, 0.9 ).setLineWidth(1.0).setOrigin(0, 0);
  const cv3 = scene.add.line(0, 0,  5, -3,  7,  4, LAVA,     1   ).setLineWidth(1.1).setOrigin(0, 0);
  const cvG = scene.add.line(0, 0, -5, -4,  4,  6, LAVA_HOT, 0.35).setLineWidth(3.5).setOrigin(0, 0);
  scene.tweens.add({ targets: [cv1, cv2, cv3, cvG], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 480, ease: "Sine.easeInOut" });

  // ---- PAULDRONS — large spiked shoulder armor ----
  const pldL = scene.add.polygon(0, -2, [-12,-1, -9,-8, -7,-10, -13,-5, -18,-3, -16,3, -11,5], ARMOR).setStrokeStyle(1, 0x000, 1);
  const pldR = scene.add.polygon(0, -2, [ 12,-1,  9,-8,  7,-10,  13,-5,  18,-3,  16,3,  11,5], ARMOR).setStrokeStyle(1, 0x000, 1);
  const spkL = scene.add.triangle(-12,-9, 0,0, -2,-7, 2,-7, HORN).setStrokeStyle(0.5, 0x000, 1);
  const spkR = scene.add.triangle( 12,-9, 0,0, -2,-7, 2,-7, HORN).setStrokeStyle(0.5, 0x000, 1);
  const pvL = scene.add.line(0, 0, -14,-2, -10, 3, LAVA, 0.75).setLineWidth(0.9).setOrigin(0, 0);
  const pvR = scene.add.line(0, 0,  14,-2,  10, 3, LAVA, 0.75).setLineWidth(0.9).setOrigin(0, 0);
  scene.tweens.add({ targets: [pvL, pvR], alpha: { from: 0.45, to: 1 }, yoyo: true, repeat: -1, duration: 600 });

  // ---- ARMS + FISTS ----
  const armL  = scene.add.rectangle(-13, 5, 6, 12, ARMOR).setStrokeStyle(1, 0x000, 1).setRotation( 0.28);
  const armR  = scene.add.rectangle( 13, 5, 6, 12, ARMOR).setStrokeStyle(1, 0x000, 1).setRotation(-0.28);
  const fistL = scene.add.circle(-16, 11, 4.8, SKIN).setStrokeStyle(0.9, 0x000, 1);
  const fistR = scene.add.circle( 16, 11, 4.8, SKIN).setStrokeStyle(0.9, 0x000, 1);
  const knkL  = scene.add.line(0, 0, -19, 10, -13, 13, LAVA_HOT, 0.9).setLineWidth(0.9).setOrigin(0, 0);
  const knkR  = scene.add.line(0, 0,  19, 10,  13, 13, LAVA_HOT, 0.9).setLineWidth(0.9).setOrigin(0, 0);
  scene.tweens.add({ targets: [knkL, knkR], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 700 });

  // ---- NECK ----
  const neck = scene.add.rectangle(0, -10, 8, 6, SKIN).setStrokeStyle(0.7, 0x000, 1);

  // ---- HEAD — broad demon skull ----
  const head          = scene.add.ellipse(0, -17, 18, 15, SKIN).setStrokeStyle(1.2, 0x000, 1);
  const foreheadPlate = scene.add.polygon(0, -20, [  -7,0, 7,0, 6,-4, 0,-6, -6,-4  ], ARMOR).setStrokeStyle(0.7, 0x000, 1);

  // ---- HORNS — massive bull horns sweeping out and up ----
  const hornL = scene.add.polygon(-5, -23, [
    0,0, -2,-3, -7,-10, -13,-16, -11,-18, -5,-12, 0,-5, 2,0,
  ], HORN).setStrokeStyle(0.7, 0x000, 1);
  const hornR = scene.add.polygon( 5, -23, [
    0,0,  2,-3,  7,-10,  13,-16,  11,-18,  5,-12, 0,-5, -2,0,
  ], HORN).setStrokeStyle(0.7, 0x000, 1);
  // Lava cracks at horn bases
  const hvL = scene.add.line(0, 0, -6,-24,  -9,-28, LAVA, 0.7).setLineWidth(0.7).setOrigin(0, 0);
  const hvR = scene.add.line(0, 0,  6,-24,   9,-28, LAVA, 0.7).setLineWidth(0.7).setOrigin(0, 0);

  // ---- EYES — large oval burning-red, glow halo ----
  const egL = scene.add.ellipse(-4, -17, 7, 4.5, LAVA_HOT, 0.55);
  const egR = scene.add.ellipse( 4, -17, 7, 4.5, LAVA_HOT, 0.55);
  const eyL = scene.add.ellipse(-4, -17, 5.5, 3.2, EYE, 1).setStrokeStyle(0.5, 0x000, 1);
  const eyR = scene.add.ellipse( 4, -17, 5.5, 3.2, EYE, 1).setStrokeStyle(0.5, 0x000, 1);
  const esL = scene.add.circle(-3.2, -17.8, 0.8, 0xffffff);
  const esR = scene.add.circle( 4.8, -17.8, 0.8, 0xffffff);
  scene.tweens.add({ targets: [egL, egR], alpha: { from: 0.35, to: 0.85 }, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [eyL, eyR], alpha: { from: 0.82, to: 1 }, yoyo: true, repeat: -1, duration: 420 });

  // ---- Z-ORDER (back → front) ----
  c.add([
    shadow, lavaPool, lavaPoolHot,
    legL, legR, bootVeinL, bootVeinR,
    armL, armR,
    torso, chestPlate, cv1, cv2, cv3, cvG,
    pldL, pldR, spkL, spkR, pvL, pvR,
    fistL, fistR, knkL, knkR,
    neck,
    hornL, hornR, hvL, hvR,
    head, foreheadPlate,
    egL, egR, eyL, eyR, esL, esR,
  ]);

  // ---- IDLE — subtle body micro-sway (very slow, heavy feel) ----
  scene.tweens.add({
    targets: [torso, chestPlate, cv1, cv2, cv3, cvG, pldL, pldR, spkL, spkR, pvL, pvR,
              armL, armR, fistL, fistR, knkL, knkR, neck, hornL, hornR, hvL, hvR,
              head, foreheadPlate, egL, egR, eyL, eyR, esL, esR],
    y: "-=0.7",
    yoyo: true,
    repeat: -1,
    duration: 1200,
    ease: "Sine.easeInOut",
  });

  // ---- CAST ANIMATION — fists slam, veins flare, eyes burst ----
  const castAnim = () => {
    scene.tweens.add({ targets: [fistL, fistR, knkL, knkR], y: "+=5", duration: 75, yoyo: true, ease: "Sine.easeIn" });
    scene.tweens.add({ targets: [cv1, cv2, cv3, cvG, pvL, pvR, bootVeinL, bootVeinR], alpha: { from: 1, to: 0.2 }, duration: 240, ease: "Quad.easeOut" });
    scene.tweens.add({ targets: [egL, egR], scale: { from: 2.2, to: 1 }, alpha: { from: 1, to: 0.35 }, duration: 300, ease: "Quad.easeOut" });
    scene.tweens.add({ targets: [lavaPool, lavaPoolHot], scale: { from: 1.9, to: 1 }, alpha: { from: 0.75, to: 0.3 }, duration: 280, ease: "Quad.easeOut" });
  };

  // Cast point: between the two outstretched fists, at the center below the body.
  return { castPoint: { x: 0, y: 11 }, orb: eyL, castAnim };
}

// =====================================================================
// STORM SPIRIT — Hovering electric sage. NO LEGS — body fades into a
// swirling cloud, holding a crackling orb of ball lightning.
// Reference: Dota 2 Storm Spirit (Raijin Thunderkeg).
// =====================================================================
function buildClockwerk(scene: Phaser.Scene, c: Phaser.GameObjects.Container, _p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // Clockwerk (Rattletrap) — Steam-powered mechanical golem.
  // Iron chassis with gear teeth, tank treads, hook arm (left), Power Claw (right),
  // exhaust pipes venting steam, single massive red sensor eye. Pure machine silhouette.

  const IRON    = 0x4a4a5a;  // main body steel
  const IRON_DK = 0x1a1a2a;  // dark edges / shadow
  const IRON_HI = 0x8a8a9a;  // light highlight
  const BRASS   = 0xb08040;  // exhaust pipes / fittings
  const STEAM   = 0xc0e8ff;  // steam cloud
  const EYE     = 0xff2020;  // sensor eye
  const CLAW_C  = 0xc0c0c8;  // polished claw steel
  const DANGER  = 0xffd020;  // yellow warning stripe

  const shadow = scene.add.ellipse(0, 19, 32, 8, 0x000000, 0.55);

  // ---- TANK TREADS ----
  const treadL = scene.add.rectangle(-9, 15, 11, 6, IRON_DK).setStrokeStyle(1.2, 0x000, 1);
  const treadR = scene.add.rectangle(9,  15, 11, 6, IRON_DK).setStrokeStyle(1.2, 0x000, 1);
  const tdL1 = scene.add.rectangle(-12, 15, 0.9, 5, IRON_HI, 0.65);
  const tdL2 = scene.add.rectangle(-9,  15, 0.9, 5, IRON_HI, 0.65);
  const tdL3 = scene.add.rectangle(-6,  15, 0.9, 5, IRON_HI, 0.65);
  const tdR1 = scene.add.rectangle(6,   15, 0.9, 5, IRON_HI, 0.65);
  const tdR2 = scene.add.rectangle(9,   15, 0.9, 5, IRON_HI, 0.65);
  const tdR3 = scene.add.rectangle(12,  15, 0.9, 5, IRON_HI, 0.65);

  // ---- MAIN BODY RING (gear chassis) ----
  const bodyRing = scene.add.circle(0, 3, 13, IRON).setStrokeStyle(2, IRON_DK, 1);
  // 8 gear teeth at cardinal + diagonal positions
  const gt1 = scene.add.rectangle(0,   -10, 4, 2.2, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const gt2 = scene.add.rectangle(9,   -7,  2.2, 4, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1).setRotation(0.785);
  const gt3 = scene.add.rectangle(13,  3,   2.2, 4, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const gt4 = scene.add.rectangle(9,   13,  2.2, 4, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1).setRotation(0.785);
  const gt5 = scene.add.rectangle(0,   16,  4, 2.2, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const gt6 = scene.add.rectangle(-9,  13,  2.2, 4, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1).setRotation(0.785);
  const gt7 = scene.add.rectangle(-13, 3,   2.2, 4, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const gt8 = scene.add.rectangle(-9,  -7,  2.2, 4, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1).setRotation(0.785);
  const bodyCore  = scene.add.circle(0, 3, 9, 0x2a2a3a).setStrokeStyle(1, IRON_DK, 1);
  const innerGear = scene.add.circle(0, 3, 5, 0x3a3a4a).setStrokeStyle(1.2, IRON_HI, 0.85);
  scene.tweens.add({ targets: innerGear, angle: "+=360", duration: 4200, repeat: -1, ease: "Linear" });
  const dangerStripe = scene.add.rectangle(0, 3, 22, 3, DANGER, 0.55).setStrokeStyle(0.5, 0x000, 0.3);
  const rv1 = scene.add.circle(-5, -1, 1.2, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const rv2 = scene.add.circle(5,  -1, 1.2, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const rv3 = scene.add.circle(-5, 7,  1.2, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const rv4 = scene.add.circle(5,  7,  1.2, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  // Pressure gauge
  const gauge  = scene.add.circle(3, 0, 2.5, 0xfff0e0).setStrokeStyle(0.7, IRON_DK, 1);
  const gaugeN = scene.add.line(0, 0, 3, 0, 4.8, -1.8, EYE, 1).setLineWidth(0.8).setOrigin(0, 0);

  // ---- EXHAUST PIPES ----
  const pipe1 = scene.add.rectangle(-5,   -12, 3.5, 7, BRASS).setStrokeStyle(1, IRON_DK, 1);
  const pipe2 = scene.add.rectangle(-0.5, -13, 2.8, 5, BRASS).setStrokeStyle(1, IRON_DK, 1);
  const cap1  = scene.add.rectangle(-5,   -16, 4.5, 1.5, IRON_HI);
  const cap2  = scene.add.rectangle(-0.5, -16.5, 3.8, 1.5, IRON_HI);
  const stm1  = scene.add.circle(-5,   -19, 3,   STEAM, 0.65);
  const stm2  = scene.add.circle(-0.5, -20, 2.5, STEAM, 0.60);
  scene.tweens.add({ targets: stm1, y: -26, scale: 1.6, alpha: 0, duration: 900,  repeat: -1 });
  scene.tweens.add({ targets: stm2, y: -28, scale: 1.5, alpha: 0, duration: 750,  repeat: -1, delay: 250 });

  // ---- HOOK ARM (left) ----
  const hookShield = scene.add.rectangle(-13, 2,  5, 3,   IRON).setStrokeStyle(0.8, IRON_DK, 1);
  const hookArm    = scene.add.rectangle(-17, 5,  3.5, 10, IRON).setStrokeStyle(1, IRON_DK, 1);
  const hookWrist  = scene.add.rectangle(-17, 11, 5, 3,   IRON_DK).setStrokeStyle(0.8, IRON_HI, 1);
  const hkChain1   = scene.add.ellipse(-20, 13,   2, 1.5, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const hkChain2   = scene.add.ellipse(-20, 15.5, 2, 1.5, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const hookTip    = scene.add.triangle(-21, 17, 0, 0, -2, 3, 1, 4, CLAW_C).setStrokeStyle(0.8, IRON_DK, 1);
  scene.tweens.add({ targets: [hookShield, hookArm, hookWrist, hkChain1, hkChain2, hookTip], rotation: { from: -0.06, to: 0.07 }, yoyo: true, repeat: -1, duration: 1300, ease: "Sine.easeInOut" });

  // ---- POWER CLAW ARM (right) ----
  const clawShield = scene.add.rectangle(13, 2,  5, 3,   IRON).setStrokeStyle(0.8, IRON_DK, 1);
  const clawArm    = scene.add.rectangle(17, 4,  3.5, 10, IRON).setStrokeStyle(1, IRON_DK, 1);
  const clawWrist  = scene.add.circle(17, 10, 3, IRON_DK).setStrokeStyle(1, IRON_HI, 1);
  const clawPiston = scene.add.rectangle(17, -0.5, 3, 6, BRASS).setStrokeStyle(0.7, IRON_DK, 1);
  const clawP1     = scene.add.triangle(15, 15, 0, 0, -1.2, 5, 0.4, 5, CLAW_C).setStrokeStyle(0.7, IRON_DK, 1);
  const clawP2     = scene.add.triangle(17, 16, 0, 0, -0.8, 6, 0.8, 6, CLAW_C).setStrokeStyle(0.7, IRON_DK, 1);
  const clawP3     = scene.add.triangle(19, 15, 0, 0, -0.4, 5, 1.2, 5, CLAW_C).setStrokeStyle(0.7, IRON_DK, 1);

  // ---- HEAD ----
  const head      = scene.add.circle(0, -10, 8.5, IRON).setStrokeStyle(2, IRON_DK, 1);
  const earL      = scene.add.circle(-10, -10, 2.8, IRON_DK).setStrokeStyle(1, IRON_HI, 1);
  const earR      = scene.add.circle(10,  -10, 2.8, IRON_DK).setStrokeStyle(1, IRON_HI, 1);
  const earPinL   = scene.add.circle(-10, -10, 1.1, IRON_HI);
  const earPinR   = scene.add.circle(10,  -10, 1.1, IRON_HI);
  const eyeSocket = scene.add.circle(0, -10, 5,   IRON_DK).setStrokeStyle(1.2, IRON_HI, 1);
  const eyeGlow   = scene.add.circle(0, -10, 4.5, EYE, 0.45);
  const eyeCore   = scene.add.circle(0, -10, 2.8, EYE);
  const eyeShine  = scene.add.circle(1, -11, 0.9, 0xffffff);
  scene.tweens.add({ targets: [eyeGlow, eyeCore], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 580 });
  const hb1 = scene.add.circle(-5.5, -14, 1, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);
  const hb2 = scene.add.circle(5.5,  -14, 1, IRON_HI).setStrokeStyle(0.5, IRON_DK, 1);

  // Steam orb — projectile spawn point, hovers above head.
  const steamOrb = scene.add.circle(0, -21, 3.5, STEAM, 0.55);
  scene.tweens.add({ targets: steamOrb, alpha: { from: 0.35, to: 0.75 }, scale: { from: 0.9, to: 1.15 }, yoyo: true, repeat: -1, duration: 720 });

  c.add([
    shadow,
    treadL, treadR, tdL1, tdL2, tdL3, tdR1, tdR2, tdR3,
    bodyRing,
    gt1, gt2, gt3, gt4, gt5, gt6, gt7, gt8,
    bodyCore, innerGear,
    dangerStripe,
    rv1, rv2, rv3, rv4,
    gauge, gaugeN,
    pipe1, pipe2, cap1, cap2,
    hookShield, hookArm, hookWrist, hkChain1, hkChain2, hookTip,
    clawShield, clawArm, clawWrist, clawPiston,
    clawP1, clawP2, clawP3,
    head, earL, earR, earPinL, earPinR,
    eyeSocket, eyeGlow, eyeCore, eyeShine,
    hb1, hb2,
    stm1, stm2,
    steamOrb,
  ]);

  // Cast animation — eye flashes, claw shoots forward, steam jets burst.
  const castAnim = () => {
    scene.tweens.add({
      targets: [eyeGlow, eyeCore],
      alpha: 1,
      scale: 1.5,
      duration: 100,
      onComplete: () => {
        scene.tweens.add({ targets: [eyeGlow, eyeCore], alpha: 0.6, scale: 1, duration: 250 });
      },
    });
    scene.tweens.add({
      targets: [clawArm, clawWrist, clawPiston, clawP1, clawP2, clawP3],
      x: "+=5",
      duration: 120,
      yoyo: true,
      ease: "Quad.easeOut",
    });
    scene.tweens.add({
      targets: [stm1, stm2],
      scale: { from: 2, to: 1 },
      alpha: { from: 1, to: 0.5 },
      duration: 280,
      ease: "Quad.easeOut",
    });
  };

  return { castPoint: { x: 0, y: -21 }, orb: steamOrb, castAnim };
}

// =====================================================================
// POWER RANGER — Spandex bodysuit hero. White boots + gloves + belt,
// helmet with black visor, chest diamond emblem, both hands gripping a
// blaster pointed forward. The blaster muzzle charges with a steam-blue
// energy core which is the cast point.
// =====================================================================
function buildPowerRanger(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  const suit = p.primary;        // bodysuit color (steam-blue)
  const suitDark = p.primaryDark;
  const white = 0xffffff;
  const visor = 0x101418;
  const accent = p.energy;       // visor edge / chest diamond glow

  // ---- LEGS — solid suit color with white boots ----
  const legL = scene.add.rectangle(-3, 13, 4, 10, suit).setStrokeStyle(1, suitDark, 1);
  const legR = scene.add.rectangle(3, 13, 4, 10, suit).setStrokeStyle(1, suitDark, 1);
  // White boots with diamond cut at top
  const bootL = scene.add.rectangle(-3, 19, 5, 4, white).setStrokeStyle(1, 0x202028, 1);
  const bootR = scene.add.rectangle(3, 19, 5, 4, white).setStrokeStyle(1, 0x202028, 1);
  const bootCutL = scene.add.triangle(-3, 17, 0, 0, -2.5, 1.6, 2.5, 1.6, suit);
  const bootCutR = scene.add.triangle(3, 17, 0, 0, -2.5, 1.6, 2.5, 1.6, suit);

  // ---- TORSO — sleek bodysuit ellipse + chest diamond emblem ----
  const torso = scene.add.ellipse(0, 4, 13, 13, suit).setStrokeStyle(1, suitDark, 1);
  // White belt with rectangular gold buckle
  const belt = scene.add.rectangle(0, 9, 14, 2.4, white).setStrokeStyle(0.6, 0x202028, 1);
  const buckle = scene.add.rectangle(0, 9, 3, 2.4, 0xffd84a).setStrokeStyle(0.5, 0x6a4a00, 1);
  // Chest diamond emblem (signature Power Ranger detail)
  const diamond = scene.add.polygon(0, 2, [0, -3.5, 3, 0, 0, 3.5, -3, 0], white).setStrokeStyle(0.8, suitDark, 1);
  const diamondInner = scene.add.polygon(0, 2, [0, -2, 1.6, 0, 0, 2, -1.6, 0], accent);

  // ---- ARMS — both forward holding a blaster ----
  // Right arm extended forward + slightly down (gripping blaster)
  const armR = scene.add.rectangle(5, 0, 2.6, 9, suit).setStrokeStyle(1, suitDark, 1);
  armR.setRotation(-0.7);
  const cuffR = scene.add.rectangle(8, 4, 3.4, 1.6, white).setStrokeStyle(0.5, 0x202028, 1);
  cuffR.setRotation(-0.7);
  const gloveR = scene.add.circle(9.4, 4.6, 1.8, white).setStrokeStyle(0.5, 0x202028, 1);
  // Left arm comes around to support — slightly behind right
  const armL = scene.add.rectangle(-2, 0, 2.6, 8, suit).setStrokeStyle(1, suitDark, 1);
  armL.setRotation(-0.95);
  const cuffL = scene.add.rectangle(2, 3.4, 3.2, 1.6, white).setStrokeStyle(0.5, 0x202028, 1);
  cuffL.setRotation(-0.95);
  const gloveL = scene.add.circle(3.6, 4.2, 1.6, white).setStrokeStyle(0.5, 0x202028, 1);

  // ---- BLASTER — held in front of torso, muzzle pointing forward-up ----
  // Body of blaster (rectangular pistol)
  const blasterGrip = scene.add.rectangle(8.6, 5.8, 2, 3, 0x202028).setStrokeStyle(0.4, 0x000, 1);
  const blasterBody = scene.add.rectangle(11, 4.4, 6, 2.6, 0x404048).setStrokeStyle(0.5, 0x101010, 1);
  const blasterTop = scene.add.rectangle(11, 3.2, 5, 1, white);
  // Muzzle ring (where energy gathers)
  const muzzleRing = scene.add.circle(13.6, 4.4, 1.8, 0x202028).setStrokeStyle(0.5, 0x000, 1);
  const muzzleInner = scene.add.circle(13.6, 4.4, 1.2, 0x101418);
  // Glowing energy core charging at the muzzle — this is the cast point
  const energyCore = scene.add.circle(13.6, 4.4, 1.5, accent, 0.85);
  const energyHalo = scene.add.circle(13.6, 4.4, 2.6, accent, 0.35);
  scene.tweens.add({
    targets: energyCore,
    scale: { from: 0.9, to: 1.2 },
    yoyo: true,
    repeat: -1,
    duration: 380,
    ease: "Sine.easeInOut",
  });
  scene.tweens.add({
    targets: energyHalo,
    scale: { from: 1, to: 1.4 },
    alpha: { from: 0.45, to: 0.15 },
    yoyo: true,
    repeat: -1,
    duration: 540,
    ease: "Sine.easeInOut",
  });

  // ---- HEAD / HELMET — rounded helmet with mouth-plate, big black visor ----
  // Helmet base — slightly larger than head, suit-colored
  const helmet = scene.add.ellipse(0, -10, 11, 10, suit).setStrokeStyle(1, suitDark, 1);
  // White mouth-plate at bottom of helmet
  const mouthPlate = scene.add.rectangle(0, -7.5, 7, 2.6, white).setStrokeStyle(0.5, 0x202028, 1);
  // Mouth-plate vent slits
  const vent1 = scene.add.line(0, 0, -1.5, -7.5, 1.5, -7.5, 0x202028, 1).setLineWidth(0.6).setOrigin(0, 0);
  const vent2 = scene.add.line(0, 0, -1.5, -6.8, 1.5, -6.8, 0x202028, 1).setLineWidth(0.6).setOrigin(0, 0);
  // Visor — wide black band across upper helmet, rounded ends
  const visorBand = scene.add.ellipse(0, -10.6, 9, 3.4, visor).setStrokeStyle(0.6, 0x000, 1);
  // Glowing accent stripe along top of visor
  const visorGlow = scene.add.rectangle(0, -11.8, 7.5, 0.6, accent, 0.85);
  // Tiny eye glints inside the visor
  const eyeL = scene.add.ellipse(-1.8, -10.6, 1.6, 0.8, accent, 0.75);
  const eyeR = scene.add.ellipse(1.8, -10.6, 1.6, 0.8, accent, 0.75);
  // Helmet crest — small white diamond on forehead matching chest emblem
  const crest = scene.add.polygon(0, -13.4, [0, -1.4, 1.2, 0, 0, 1.4, -1.2, 0], white).setStrokeStyle(0.4, suitDark, 1);

  // Z-order: back-to-front so visor stripe sits above helmet, blaster sits above arms
  c.add([
    legL, legR, bootL, bootR, bootCutL, bootCutR,
    torso, belt, buckle, diamond, diamondInner,
    armL, armR,
    cuffL, gloveL,
    cuffR, gloveR,
    blasterGrip, blasterBody, blasterTop, muzzleRing, muzzleInner,
    energyHalo, energyCore,
    helmet, mouthPlate, vent1, vent2,
    visorBand, visorGlow, eyeL, eyeR, crest,
  ]);

  // Cast animation — blaster recoils slightly, body leans back, energy core pops.
  const castAnim = () => {
    // Recoil the blaster + supporting arm/glove cluster
    const recoilGroup = [
      armR, cuffR, gloveR, armL, cuffL, gloveL,
      blasterGrip, blasterBody, blasterTop,
      muzzleRing, muzzleInner, energyHalo, energyCore,
    ];
    scene.tweens.add({
      targets: recoilGroup,
      x: "-=2",
      duration: 80,
      yoyo: true,
      ease: "Quad.easeOut",
    });
    // Body lean-back
    scene.tweens.add({
      targets: [torso, belt, buckle, diamond, diamondInner, helmet, mouthPlate, visorBand, visorGlow, eyeL, eyeR, crest],
      y: "-=1.2",
      duration: 90,
      yoyo: true,
      ease: "Quad.easeOut",
    });
    // Visor stripe flashes brighter
    scene.tweens.add({
      targets: [visorGlow, eyeL, eyeR],
      alpha: { from: 1, to: 0.75 },
      duration: 220,
      ease: "Quad.easeOut",
    });
  };

  return { castPoint: { x: 13.6, y: 4.4 }, orb: energyCore, castAnim };
}

// =====================================================================
// SVEN — The Rogue Knight. Big armored ogre-blooded warrior with a
// MASSIVE two-handed sword (Stormbolt blade), purple plate armor,
// flowing cape, horned helm. Significantly LARGER than other heroes.
// Reference: Dota 2 Sven (Yurnero's friend, ogre champion).
// =====================================================================
function buildSven(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // SVEN — bigger than other heroes (~30% scale up). Massive armored knight,
  // purple plate, flowing cape, horned helm, two-handed Stormbolt sword raised.

  // ---- CAPE behind (flowing, dark purple) ----
  const capeBack = scene.add.polygon(0, 4, [
    -10, -8, 10, -8, 12, 4, 8, 14, 4, 18, 0, 16, -4, 18, -8, 14, -12, 4,
  ], p.primaryDark).setStrokeStyle(1, 0x0a0014, 1);
  const capeFold1 = scene.add.line(0, 0, -6, -4, -4, 14, 0x1a0030, 0.7).setLineWidth(0.8).setOrigin(0, 0);
  const capeFold2 = scene.add.line(0, 0, 6, -4, 4, 14, 0x1a0030, 0.7).setLineWidth(0.8).setOrigin(0, 0);
  scene.tweens.add({ targets: [capeBack, capeFold1, capeFold2], skewX: 0.06, yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });

  // ---- HEAVY BOOTS (armored, larger) ----
  const bootL = scene.add.rectangle(-5, 22, 6, 5, p.metal).setStrokeStyle(1, 0x202028, 1);
  const bootR = scene.add.rectangle(5, 22, 6, 5, p.metal).setStrokeStyle(1, 0x202028, 1);
  const bootKneeL = scene.add.ellipse(-5, 18, 5, 2, p.trim);
  const bootKneeR = scene.add.ellipse(5, 18, 5, 2, p.trim);

  // ---- LEGS — purple plate greaves (chunky) ----
  const legL = scene.add.rectangle(-5, 14, 5.5, 9, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const legR = scene.add.rectangle(5, 14, 5.5, 9, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  // Plate accents
  const legBandL = scene.add.rectangle(-5, 12, 6, 1.4, p.trim);
  const legBandR = scene.add.rectangle(5, 12, 6, 1.4, p.trim);

  // ---- WAIST belt with crest ----
  const belt = scene.add.rectangle(0, 8, 18, 2.6, 0x2a1040).setStrokeStyle(0.8, 0x0a0014, 1);
  const beltBuckle = scene.add.rectangle(0, 8, 4, 3.2, p.energy).setStrokeStyle(0.8, 0xffffff, 1);
  scene.tweens.add({ targets: beltBuckle, alpha: 0.6, yoyo: true, repeat: -1, duration: 700 });

  // ---- LOWER TORSO / SKIRT ARMOR ----
  const skirt = scene.add.polygon(0, 6, [
    -10, -2, 10, -2, 11, 2, 6, 4, 0, 2, -6, 4, -11, 2,
  ], p.primary).setStrokeStyle(1, p.primaryDark, 1);

  // ---- HUGE TORSO with chestplate ----
  const torso = scene.add.ellipse(0, -2, 20, 14, p.primary).setStrokeStyle(1.2, p.primaryDark, 1);
  // Chest crest (heart-shape glowing energy gem)
  const crest = scene.add.polygon(0, -3, [0, -4, 3, 0, 0, 4, -3, 0], p.energy).setStrokeStyle(0.8, 0xffffff, 1);
  scene.tweens.add({ targets: crest, alpha: 0.55, scale: 1.15, yoyo: true, repeat: -1, duration: 600, ease: "Sine.easeInOut" });
  // Plate trim lines
  const chestTrim = scene.add.line(0, 0, -8, -6, 8, -6, p.trim, 0.9).setLineWidth(1).setOrigin(0, 0);

  // ---- MASSIVE PAULDRONS (shoulder armor with spikes) ----
  // Left pauldron
  const pauldronL = scene.add.ellipse(-11, -6, 9, 7, p.primaryDark).setStrokeStyle(1, 0x0a0014, 1);
  const pauldronLrim = scene.add.ellipse(-11, -7, 9, 3, p.trim);
  const spikeL1 = scene.add.triangle(-13, -10, 0, 0, -1.4, 3, 1.4, 3, p.metal).setRotation(-0.4);
  const spikeL2 = scene.add.triangle(-10, -11, 0, 0, -1.2, 3, 1.2, 3, p.metal).setRotation(-0.1);
  // Right pauldron (mirror)
  const pauldronR = scene.add.ellipse(11, -6, 9, 7, p.primaryDark).setStrokeStyle(1, 0x0a0014, 1);
  const pauldronRrim = scene.add.ellipse(11, -7, 9, 3, p.trim);
  const spikeR1 = scene.add.triangle(13, -10, 0, 0, -1.4, 3, 1.4, 3, p.metal).setRotation(0.4);
  const spikeR2 = scene.add.triangle(10, -11, 0, 0, -1.2, 3, 1.2, 3, p.metal).setRotation(0.1);

  // ---- ARMS — at sides, fists clenched (no weapon) ----
  const armL = scene.add.rectangle(-12, 1, 4, 11, p.primary).setStrokeStyle(0.8, p.primaryDark, 1).setRotation(0.05);
  const gauntletL = scene.add.rectangle(-12, 7, 5, 3.5, p.metal).setStrokeStyle(0.8, 0x202028, 1);
  const fistL = scene.add.circle(-12, 10, 2.4, p.skin).setStrokeStyle(0.8, 0x6a4a30, 1);
  const armR = scene.add.rectangle(12, 1, 4, 11, p.primary).setStrokeStyle(0.8, p.primaryDark, 1).setRotation(-0.05);
  const gauntletR = scene.add.rectangle(12, 7, 5, 3.5, p.metal).setStrokeStyle(0.8, 0x202028, 1);
  const fistR = scene.add.circle(12, 10, 2.4, p.skin).setStrokeStyle(0.8, 0x6a4a30, 1);

  // ---- HEAD (slightly bigger, ogre skin tone) ----
  const head = scene.add.ellipse(0, -16, 9, 10, p.skin).setStrokeStyle(0.9, 0x6a4a30, 1);
  // Jaw shadow
  const jaw = scene.add.ellipse(0, -13, 7, 4, 0xb88a60, 0.6);
  // Eyes (glowing energy)
  const eyeL = scene.add.circle(-2, -16, 1.5, p.energy);
  const eyeR = scene.add.circle(2, -16, 1.5, p.energy);
  const eyeShineL = scene.add.circle(-1.4, -16.5, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.6, -16.5, 0.5, 0xffffff);
  scene.tweens.add({ targets: [eyeL, eyeR], alpha: 0.5, yoyo: true, repeat: -1, duration: 800 });

  // ---- HORNED HELM ----
  // Skull cap
  const helmCap = scene.add.ellipse(0, -19, 11, 7, p.primaryDark).setStrokeStyle(1, 0x0a0014, 1);
  // Forehead trim band (energy gem in middle)
  const helmBand = scene.add.rectangle(0, -17, 10, 1.6, p.trim);
  const helmGem = scene.add.circle(0, -17, 0.9, p.energy);
  scene.tweens.add({ targets: helmGem, alpha: 0.5, yoyo: true, repeat: -1, duration: 500 });
  // Two huge curved horns
  const hornL = scene.add.triangle(-6, -22, 0, 0, -3, 6, 1, 6, p.metal).setStrokeStyle(0.6, 0x202028, 1).setRotation(-0.5);
  const hornR = scene.add.triangle(6, -22, 0, 0, -1, 6, 3, 6, p.metal).setStrokeStyle(0.6, 0x202028, 1).setRotation(0.5);
  // Cheek guards
  const cheekL = scene.add.triangle(-4, -15, 0, 0, -1, 3, 1, 3, p.primaryDark);
  const cheekR = scene.add.triangle(4, -15, 0, 0, -1, 3, 1, 3, p.primaryDark);

  // ---- HOVER bob (subtle, heavy character) ----
  const allParts = [
    capeBack, capeFold1, capeFold2,
    bootL, bootR, bootKneeL, bootKneeR,
    legL, legR, legBandL, legBandR,
    belt, beltBuckle, skirt,
    torso, crest, chestTrim,
    pauldronL, pauldronLrim, spikeL1, spikeL2,
    pauldronR, pauldronRrim, spikeR1, spikeR2,
    armL, gauntletL, fistL, armR, gauntletR, fistR,
    head, jaw, eyeL, eyeR, eyeShineL, eyeShineR,
    helmCap, helmBand, helmGem, hornL, hornR, cheekL, cheekR,
  ];
  scene.tweens.add({
    targets: allParts,
    y: "-=1.4",
    yoyo: true,
    repeat: -1,
    duration: 1200,
    ease: "Sine.easeInOut",
  });

  c.add([
    capeBack, capeFold1, capeFold2,
    bootL, bootR, bootKneeL, bootKneeR,
    legL, legR, legBandL, legBandR,
    belt, beltBuckle,
    skirt,
    torso, chestTrim, crest,
    pauldronL, pauldronLrim, spikeL1, spikeL2,
    pauldronR, pauldronRrim, spikeR1, spikeR2,
    head, jaw, eyeL, eyeR, eyeShineL, eyeShineR,
    helmCap, helmBand, helmGem, cheekL, cheekR,
    hornL, hornR,
    armL, gauntletL, fistL,
    armR, gauntletR, fistR,
  ]);

  // Cast animation — body lunges forward, fists thrust, chest crest flares.
  const castAnim = () => {
    scene.tweens.add({
      targets: [armR, gauntletR, fistR, armL, gauntletL, fistL],
      x: "+=2",
      duration: 100,
      yoyo: true,
      ease: "Quad.easeOut",
    });
    scene.tweens.add({
      targets: crest,
      scale: { from: 1.6, to: 1 },
      alpha: { from: 1, to: 0.55 },
      duration: 220,
      ease: "Quad.easeOut",
    });
  };

  // Cast point: chest crest (forward burst origin).
  return { castPoint: { x: 14, y: -2 }, orb: crest, castAnim };
}

// =====================================================================
// WITCH DOCTOR — Tribal swamp shaman. Wide bone-skull mask covering
// face, hunched posture, voodoo skull-staff, ritual feathers + necklace.
// Reference: Dota 2 Witch Doctor (Zharvakko).
// =====================================================================
function buildWitchDoctor(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- PREMIUM BACKDROP — voodoo spirit ring + floating fetish dolls ----
  const spiritRing = scene.add.circle(0, 2, 22, 0xffffff, 0).setStrokeStyle(1, p.energy, 0.55);
  const spiritRing2 = scene.add.circle(0, 2, 17, 0xffffff, 0).setStrokeStyle(0.7, p.trim, 0.45);
  scene.tweens.add({ targets: spiritRing, scale: { from: 0.95, to: 1.1 }, alpha: { from: 0.3, to: 0.7 }, yoyo: true, repeat: -1, duration: 900 });
  scene.tweens.add({ targets: spiritRing2, rotation: Math.PI * 2, repeat: -1, duration: 5000 });
  // Floating bone fetish dolls — small dangling skull motes orbiting
  const fetishMotes: Phaser.GameObjects.Arc[] = [];
  for (let i = 0; i < 4; i++) {
    const ang = (i / 4) * Math.PI * 2;
    const m = scene.add.circle(Math.cos(ang) * 19, Math.sin(ang) * 13 + 2, 1.1, 0xeae0c0, 0.9).setStrokeStyle(0.4, 0x4a3018, 1);
    fetishMotes.push(m);
    scene.tweens.add({ targets: m, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 500 + i * 80 });
  }
  // Smoke wisps rising at base
  const smoke1 = scene.add.ellipse(-5, 19, 4, 1.5, 0xeae0c0, 0.4);
  const smoke2 = scene.add.ellipse(5, 19, 4, 1.5, 0xeae0c0, 0.4);
  scene.tweens.add({ targets: [smoke1, smoke2], y: "-=4", alpha: { from: 0.5, to: 0 }, yoyo: true, repeat: -1, duration: 800 });

  // ---- BARE FEET WITH ANKLE BANDS ----
  const footL = scene.add.ellipse(-4, 20, 5, 2.4, p.skin).setStrokeStyle(0.6, 0x3a2010, 1);
  const footR = scene.add.ellipse(4, 20, 5, 2.4, p.skin).setStrokeStyle(0.6, 0x3a2010, 1);
  const ankletL = scene.add.rectangle(-4, 18, 5, 1, p.trim);
  const ankletR = scene.add.rectangle(4, 18, 5, 1, p.trim);

  // ---- LEGS — bandage-wrapped ----
  const legL = scene.add.rectangle(-3, 13, 3.4, 9, p.primary).setStrokeStyle(0.7, p.primaryDark, 1);
  const legR = scene.add.rectangle(3, 13, 3.4, 9, p.primary).setStrokeStyle(0.7, p.primaryDark, 1);
  // Bandage stripes
  const wrap1L = scene.add.rectangle(-3, 11, 4, 0.7, 0xeae0c0, 0.8);
  const wrap2L = scene.add.rectangle(-3, 14, 4, 0.7, 0xeae0c0, 0.8);
  const wrap1R = scene.add.rectangle(3, 11, 4, 0.7, 0xeae0c0, 0.8);
  const wrap2R = scene.add.rectangle(3, 14, 4, 0.7, 0xeae0c0, 0.8);

  // ---- LOIN SKIRT (ragged earthy) ----
  const skirt = scene.add.polygon(0, 8, [
    -8, 0, 8, 0, 9, 4, 6, 7, 3, 4, 0, 7, -3, 4, -6, 7, -9, 4,
  ], p.primary).setStrokeStyle(0.8, p.primaryDark, 1);
  // Skirt accents — small bone teeth dangling
  const tooth1 = scene.add.triangle(-5, 11, 0, 0, -0.6, 1.6, 0.6, 1.6, 0xeae0c0);
  const tooth2 = scene.add.triangle(0, 12, 0, 0, -0.6, 1.6, 0.6, 1.6, 0xeae0c0);
  const tooth3 = scene.add.triangle(5, 11, 0, 0, -0.6, 1.6, 0.6, 1.6, 0xeae0c0);

  // ---- TORSO (hunched, narrow) ----
  const torso = scene.add.ellipse(0, -1, 11, 11, p.skin).setStrokeStyle(0.8, 0x3a2010, 1);
  // Tribal scarification (chest lines)
  const scar1 = scene.add.line(0, 0, -3, -3, 3, 2, p.primaryDark, 0.85).setLineWidth(0.6).setOrigin(0, 0);
  const scar2 = scene.add.line(0, 0,  3, -3, -2, 1, p.primaryDark, 0.7).setLineWidth(0.5).setOrigin(0, 0);
  // Bone necklace
  const neckBead1 = scene.add.circle(-3, -5, 0.7, 0xeae0c0);
  const neckBead2 = scene.add.circle(0, -5.4, 0.9, 0xeae0c0);
  const neckBead3 = scene.add.circle(3, -5, 0.7, 0xeae0c0);

  // ---- LEFT ARM — hangs low, holds rattling shrunken head ----
  const armL = scene.add.rectangle(-7, 0, 2.4, 8, p.skin).setStrokeStyle(0.8, 0x3a2010, 1);
  armL.setRotation(0.2);
  const handL = scene.add.circle(-9, 5, 1.6, p.skin).setStrokeStyle(0.7, 0x3a2010, 1);
  // Shrunken head dangling from left hand
  const shrHead = scene.add.circle(-9, 9, 1.6, 0x4a3018).setStrokeStyle(0.6, 0x000, 1);
  const shrEye = scene.add.circle(-9, 9, 0.4, 0xff8a3a);
  scene.tweens.add({ targets: shrHead, x: -8.5, yoyo: true, repeat: -1, duration: 600, ease: "Sine.easeInOut" });

  // ---- RIGHT ARM — raised high, gripping voodoo SKULL STAFF ----
  const armR = scene.add.rectangle(7, -3, 2.4, 9, p.skin).setStrokeStyle(0.8, 0x3a2010, 1);
  armR.setRotation(-0.35);
  const handR = scene.add.circle(10, -8, 1.6, p.skin).setStrokeStyle(0.7, 0x3a2010, 1);

  // ---- VOODOO SKULL STAFF ----
  const staffShaft = scene.add.rectangle(11, -4, 1.4, 18, 0x3a2010).setStrokeStyle(0.5, 0x000, 1);
  staffShaft.setRotation(0.05);
  // Skull at top of staff
  const skull = scene.add.circle(12, -16, 4.0, 0xeae0c0).setStrokeStyle(0.8, 0x4a3018, 1);
  const skullEye1 = scene.add.circle(11, -16, 0.7, 0x000);
  const skullEye2 = scene.add.circle(13, -16, 0.7, 0x000);
  // Glowing yellow eye-flame inside skull eye sockets
  const skullEyeGlow1 = scene.add.circle(11, -16, 0.9, p.energy, 0.85);
  const skullEyeGlow2 = scene.add.circle(13, -16, 0.9, p.energy, 0.85);
  scene.tweens.add({ targets: [skullEyeGlow1, skullEyeGlow2], alpha: 0.3, yoyo: true, repeat: -1, duration: 500 });
  const skullJaw = scene.add.rectangle(12, -13.5, 3, 1.2, 0xc8b0a0);
  // Feather hanging from staff (red+green)
  const featherRed = scene.add.triangle(15, -14, 0, 0, -1.4, 4, 1.4, 0, 0xc02020).setRotation(0.4);
  const featherGreen = scene.add.triangle(15, -11, 0, 0, -1.2, 3, 1.2, 0, 0x4ea84a).setRotation(0.6);
  // Orb energy at very top above skull
  const orbAura = scene.add.circle(12, -20, 5.5, p.energy, 0.45);
  scene.tweens.add({ targets: orbAura, scale: 1.4, alpha: 0.15, yoyo: true, repeat: -1, duration: 600 });

  // ---- HEAD with HUGE BONE MASK ----
  // Head core (mostly hidden by mask)
  const head = scene.add.circle(0, -11, 4.2, p.skin).setStrokeStyle(0.7, 0x3a2010, 1);
  // Bone mask — wide, covers full face
  const mask = scene.add.ellipse(0, -11, 11, 9, 0xeae0c0).setStrokeStyle(0.9, 0x4a3018, 1);
  // Mask eye holes — dark
  const maskEye1 = scene.add.circle(-2.5, -12, 1.6, 0x000);
  const maskEye2 = scene.add.circle(2.5, -12, 1.6, 0x000);
  // Glowing orange eyes through mask
  const maskGlow1 = scene.add.circle(-2.5, -12, 1.2, p.energy);
  const maskGlow2 = scene.add.circle(2.5, -12, 1.2, p.energy);
  scene.tweens.add({ targets: [maskGlow1, maskGlow2], alpha: 0.4, yoyo: true, repeat: -1, duration: 700 });
  // Mask jagged teeth pattern at bottom
  const maskTeeth = scene.add.line(0, 0, -4, -8, 4, -8, 0x000).setLineWidth(0.8).setOrigin(0, 0);
  const tooth_a = scene.add.triangle(-2.5, -8, 0, 0, -0.8, 1.6, 0.8, 1.6, 0xeae0c0);
  const tooth_b = scene.add.triangle(0, -8, 0, 0, -0.8, 1.6, 0.8, 1.6, 0xeae0c0);
  const tooth_c = scene.add.triangle(2.5, -8, 0, 0, -0.8, 1.6, 0.8, 1.6, 0xeae0c0);
  // Tribal red paint stripe across mask
  const paintStripe = scene.add.rectangle(0, -10, 11, 0.9, 0x8a0000);
  // FEATHER HEADDRESS — three big feathers fanning out
  const headfeath1 = scene.add.triangle(-5, -17, 0, 0, -1.2, 5, 1.2, 0, 0xc02020).setRotation(-0.5);
  const headfeath2 = scene.add.triangle(0, -19, 0, 0, -1.4, 6, 1.4, 0, 0xffe070);
  const headfeath3 = scene.add.triangle(5, -17, 0, 0, -1.2, 5, 1.2, 0, 0x4ea84a).setRotation(0.5);

  // ---- PREMIUM STAFF GLOW — orbital sparks circling the skull ----
  const staffSpark1 = scene.add.circle(12, -20, 0.8, p.energy, 1);
  const staffSpark2 = scene.add.circle(12, -20, 0.8, p.trim, 1);
  scene.tweens.add({ targets: staffSpark1, x: 16, y: -16, yoyo: true, repeat: -1, duration: 700, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: staffSpark2, x: 8, y: -16, yoyo: true, repeat: -1, duration: 800, ease: "Sine.easeInOut" });
  // Skull aura halo
  const skullHalo = scene.add.circle(12, -16, 6, p.energy, 0).setStrokeStyle(0.8, p.energy, 0.7);
  scene.tweens.add({ targets: skullHalo, scale: { from: 0.85, to: 1.2 }, alpha: { from: 0.4, to: 0.9 }, yoyo: true, repeat: -1, duration: 700 });

  c.add([
    spiritRing, spiritRing2, ...fetishMotes,
    smoke1, smoke2,
    footL, footR, ankletL, ankletR,
    legL, legR, wrap1L, wrap2L, wrap1R, wrap2R,
    skirt, tooth1, tooth2, tooth3,
    torso, scar1, scar2, neckBead1, neckBead2, neckBead3,
    armL, handL, shrHead, shrEye,
    armR, handR,
    skullHalo, orbAura, staffShaft, skull, skullEye1, skullEye2, skullEyeGlow1, skullEyeGlow2, skullJaw, featherRed, featherGreen,
    staffSpark1, staffSpark2,
    head, mask, paintStripe, maskEye1, maskEye2, maskGlow1, maskGlow2, maskTeeth, tooth_a, tooth_b, tooth_c,
    headfeath1, headfeath2, headfeath3,
  ]);

  // Cast animation — staff thrust, skull eyes flare
  const castAnim = () => {
    scene.tweens.add({
      targets: [skullEyeGlow1, skullEyeGlow2, orbAura],
      scale: { from: 2, to: 1 },
      alpha: { from: 1, to: 0.4 },
      duration: 240,
      ease: "Quad.easeOut",
    });
    scene.tweens.add({
      targets: [staffShaft, skull, skullEye1, skullEye2, skullEyeGlow1, skullEyeGlow2, skullJaw, featherRed, featherGreen, orbAura, armR, handR],
      y: "-=3",
      duration: 100,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
  };

  void p;
  return { castPoint: { x: 12, y: -20 }, orb: skull, castAnim };
}

// =====================================================================
// LICH — Skeletal frost lord. Hooded skull face, dark blue cloak,
// frozen orb in raised hand, ice crystals at base.
// Reference: Dota 2 Lich (Ethreain).
// =====================================================================
function buildLich(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {

  // ---- ICE CRYSTALS AT BASE (replaces feet) ----
  const iceShard1 = scene.add.triangle(-6, 18, 0, 0, -2, 5, 2, 5, p.energy, 0.85).setStrokeStyle(0.6, 0xffffff, 1);
  const iceShard2 = scene.add.triangle(0, 19, 0, 0, -2.5, 4, 2.5, 4, p.energy, 0.95).setStrokeStyle(0.6, 0xffffff, 1);
  const iceShard3 = scene.add.triangle(6, 18, 0, 0, -2, 5, 2, 5, p.energy, 0.85).setStrokeStyle(0.6, 0xffffff, 1);
  // Shard glow
  const baseGlow = scene.add.ellipse(0, 19, 22, 4, p.energy, 0.4);
  scene.tweens.add({ targets: baseGlow, scaleX: 1.2, alpha: 0.15, yoyo: true, repeat: -1, duration: 800 });
  // Frost mist at base
  const mist = scene.add.ellipse(0, 17, 22, 3, 0xffffff, 0.55);
  scene.tweens.add({ targets: mist, alpha: 0.2, yoyo: true, repeat: -1, duration: 1100 });

  // ---- LONG TATTERED CLOAK (replaces legs/torso) ----
  // Outer hooded robe — wide at bottom, narrow at top
  const cloak = scene.add.polygon(0, 4, [
    -7, -14,
     7, -14,
    11, 14,
     8, 16,
     5, 14,
     2, 16,
    -2, 14,
    -5, 16,
    -8, 14,
    -11, 14,
  ], p.primary).setStrokeStyle(1.2, p.primaryDark, 1);
  // Cloak inner shadow
  const cloakDark = scene.add.polygon(0, 6, [
    -5, -10, 5, -10, 7, 12, -7, 12,
  ], p.primaryDark, 0.8);
  // Frozen trim at hem (icy white edge)
  const cloakHem = scene.add.line(0, 0, -10, 16, 10, 16, p.trim, 0.95).setLineWidth(1).setOrigin(0, 0);
  // Cloak clasp — blue gemstone
  const clasp = scene.add.circle(0, -7, 2, p.energy).setStrokeStyle(0.7, 0xffffff, 1);
  scene.tweens.add({ targets: clasp, alpha: 0.6, yoyo: true, repeat: -1, duration: 700 });

  // ---- SKELETAL ARMS (bone) ----
  // Left arm — claw of bones
  const armL = scene.add.rectangle(-7, 1, 1.6, 8, 0xeae0c0).setStrokeStyle(0.5, 0x4a3018, 1);
  armL.setRotation(0.25);
  const handL = scene.add.circle(-9, 5, 1.4, 0xeae0c0).setStrokeStyle(0.5, 0x4a3018, 1);
  // Bony fingers
  const fingerL1 = scene.add.line(0, 0, -9, 5, -11, 8, 0xeae0c0).setLineWidth(0.7).setOrigin(0, 0);
  const fingerL2 = scene.add.line(0, 0, -9, 5, -10, 9, 0xeae0c0).setLineWidth(0.7).setOrigin(0, 0);

  // Right arm — raised, holds frozen orb
  const armR = scene.add.rectangle(8, -3, 1.6, 9, 0xeae0c0).setStrokeStyle(0.5, 0x4a3018, 1);
  armR.setRotation(-0.4);
  const handR = scene.add.circle(11, -8, 1.4, 0xeae0c0).setStrokeStyle(0.5, 0x4a3018, 1);

  // ---- FROZEN ORB ----
  const orbAura = scene.add.circle(13, -12, 6.5, p.energy, 0.45);
  scene.tweens.add({ targets: orbAura, scale: 1.4, alpha: 0.18, yoyo: true, repeat: -1, duration: 700 });
  const orbBody = scene.add.circle(13, -12, 4, p.energy).setStrokeStyle(0.8, 0xffffff, 1);
  // Crystalline facets on orb
  const orbFacet1 = scene.add.triangle(13, -12, 0, -3, -3, 1, 3, 1, 0xffffff, 0.7);
  const orbFacet2 = scene.add.triangle(13, -12, 0, 3, -3, -1, 3, -1, 0xc8e8f0, 0.7);
  const orbCore = scene.add.circle(13, -12, 1.2, 0xffffff);
  // Frozen mist around orb
  const orbMist1 = scene.add.circle(11, -10, 1, 0xffffff, 0.5);
  const orbMist2 = scene.add.circle(15, -14, 1, 0xffffff, 0.5);
  scene.tweens.add({ targets: [orbMist1, orbMist2], alpha: 0.2, scale: 1.5, yoyo: true, repeat: -1, duration: 800 });

  // ---- HOODED SKULL HEAD ----
  // Hood (large dark covering)
  const hood = scene.add.polygon(0, -13, [
    -7, 4,
    -8, -2,
    -5, -7,
     0, -8,
     5, -7,
     8, -2,
     7, 4,
  ], p.primaryDark).setStrokeStyle(0.8, 0x000, 1);
  // Hood inner shadow (deep dark)
  const hoodInner = scene.add.ellipse(0, -11, 10, 8, 0x000, 0.95);
  // Skull face peeking out from hood
  const skull = scene.add.ellipse(0, -11, 7, 8, 0xeae0c0).setStrokeStyle(0.7, 0x4a3018, 1);
  // Glowing blue eye-sockets (frost burning)
  const eyeSockL = scene.add.circle(-1.8, -12, 1.4, 0x000);
  const eyeSockR = scene.add.circle(1.8, -12, 1.4, 0x000);
  const eyeGlowL = scene.add.circle(-1.8, -12, 1, p.energy);
  const eyeGlowR = scene.add.circle(1.8, -12, 1, p.energy);
  scene.tweens.add({ targets: [eyeGlowL, eyeGlowR], alpha: 0.4, yoyo: true, repeat: -1, duration: 600 });
  // Skull nasal cavity (small triangle)
  const nasal = scene.add.triangle(0, -10, 0, 0, -0.6, 1.4, 0.6, 1.4, 0x000);
  // Skull jaw teeth
  const jaw = scene.add.rectangle(0, -8.5, 4, 0.9, 0x000);
  const teeth1 = scene.add.rectangle(-1.4, -8.5, 0.4, 0.9, 0xeae0c0);
  const teeth2 = scene.add.rectangle(0, -8.5, 0.4, 0.9, 0xeae0c0);
  const teeth3 = scene.add.rectangle(1.4, -8.5, 0.4, 0.9, 0xeae0c0);
  // Hood horns / frost spikes
  const hoodSpikeL = scene.add.triangle(-7, -17, 0, 0, -1.5, 4, 1.5, 4, p.energy).setStrokeStyle(0.5, 0xffffff, 1).setRotation(-0.3);
  const hoodSpikeR = scene.add.triangle(7, -17, 0, 0, -1.5, 4, 1.5, 4, p.energy).setStrokeStyle(0.5, 0xffffff, 1).setRotation(0.3);

  c.add([
    iceShard1, iceShard2, iceShard3, baseGlow, mist,
    cloak, cloakDark, cloakHem, clasp,
    armL, handL, fingerL1, fingerL2,
    armR, handR,
    orbAura, orbBody, orbFacet1, orbFacet2, orbCore, orbMist1, orbMist2,
    hood, hoodInner, hoodSpikeL, hoodSpikeR,
    skull, eyeSockL, eyeSockR, eyeGlowL, eyeGlowR, nasal, jaw, teeth1, teeth2, teeth3,
  ]);

  const castAnim = () => {
    scene.tweens.add({
      targets: [orbAura, orbBody, orbCore],
      scale: { from: 1.5, to: 1 },
      duration: 220,
      ease: "Quad.easeOut",
    });
    scene.tweens.add({
      targets: [eyeGlowL, eyeGlowR],
      alpha: { from: 1, to: 0.4 },
      duration: 240,
      ease: "Quad.easeOut",
    });
  };

  void p;
  return { castPoint: { x: 13, y: -12 }, orb: orbBody, castAnim };
}

// =====================================================================
// WINTER WYVERN (Auroth, the Frost Poet) — Bipedal blue feathered dragon.
// Long-snouted dragon head with horns, feathered wings, floating ice
// tome, frost-spike crown. Reference: Dota 2 Winter Wyvern.
// =====================================================================
function buildWinterWyvern(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // Wyvern colors — icy blue dragon, frost feathers.
  const SCALE = 0x4a78c8;       // body scale blue
  const SCALE_DARK = 0x1a3868;
  const UNDER = 0xa6c8ee;       // pale underbelly
  const FEATHER = 0xd8e8ff;     // wing feather pale
  const FROST = 0xeaf6ff;
  const ICE = p.energy;          // glow color

  // ---- WINGS BEHIND — wide feathered dragon wings ----
  const wingL = scene.add.polygon(-9, -2, [
    0, 0,
    -10, -6,
    -14, -3,
    -13, 2,
    -8, 5,
    -3, 4,
  ], SCALE_DARK, 0.95).setStrokeStyle(0.7, 0x000, 1);
  const wingR = scene.add.polygon(9, -2, [
    0, 0,
    10, -6,
    14, -3,
    13, 2,
    8, 5,
    3, 4,
  ], SCALE_DARK, 0.95).setStrokeStyle(0.7, 0x000, 1);
  // Feather strokes on wings
  const featherL1 = scene.add.line(0, 0, -10, -3, -14, -2, FEATHER, 0.9).setLineWidth(0.6).setOrigin(0, 0);
  const featherL2 = scene.add.line(0, 0, -9, -1, -13, 0, FEATHER, 0.85).setLineWidth(0.6).setOrigin(0, 0);
  const featherL3 = scene.add.line(0, 0, -8, 1, -12, 3, FEATHER, 0.8).setLineWidth(0.6).setOrigin(0, 0);
  const featherR1 = scene.add.line(0, 0, 10, -3, 14, -2, FEATHER, 0.9).setLineWidth(0.6).setOrigin(0, 0);
  const featherR2 = scene.add.line(0, 0, 9, -1, 13, 0, FEATHER, 0.85).setLineWidth(0.6).setOrigin(0, 0);
  const featherR3 = scene.add.line(0, 0, 8, 1, 12, 3, FEATHER, 0.8).setLineWidth(0.6).setOrigin(0, 0);
  // Slow wing flap
  scene.tweens.add({ targets: [wingL, featherL1, featherL2, featherL3], y: "+=1.5", yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [wingR, featherR1, featherR2, featherR3], y: "+=1.5", yoyo: true, repeat: -1, duration: 1100, ease: "Sine.easeInOut" });

  // ---- LEGS — clawed dragon hindlegs ----
  const legL = scene.add.polygon(-3.5, 16, [-1.6, -4, 1.6, -4, 2.2, 4, -2.2, 4], SCALE).setStrokeStyle(0.7, SCALE_DARK, 1);
  const legR = scene.add.polygon(3.5, 16, [-1.6, -4, 1.6, -4, 2.2, 4, -2.2, 4], SCALE).setStrokeStyle(0.7, SCALE_DARK, 1);
  // Talons
  const talonL1 = scene.add.triangle(-5, 21, 0, 0, -0.7, 1.6, 0.7, 1.6, FROST).setStrokeStyle(0.3, 0x000, 1);
  const talonL2 = scene.add.triangle(-3.5, 21.4, 0, 0, -0.7, 1.6, 0.7, 1.6, FROST).setStrokeStyle(0.3, 0x000, 1);
  const talonL3 = scene.add.triangle(-2, 21, 0, 0, -0.7, 1.6, 0.7, 1.6, FROST).setStrokeStyle(0.3, 0x000, 1);
  const talonR1 = scene.add.triangle(2, 21, 0, 0, -0.7, 1.6, 0.7, 1.6, FROST).setStrokeStyle(0.3, 0x000, 1);
  const talonR2 = scene.add.triangle(3.5, 21.4, 0, 0, -0.7, 1.6, 0.7, 1.6, FROST).setStrokeStyle(0.3, 0x000, 1);
  const talonR3 = scene.add.triangle(5, 21, 0, 0, -0.7, 1.6, 0.7, 1.6, FROST).setStrokeStyle(0.3, 0x000, 1);

  // ---- TORSO — scaled dragon body ----
  const torso = scene.add.ellipse(0, 4, 14, 14, SCALE).setStrokeStyle(1, SCALE_DARK, 1);
  // Pale feathered underbelly
  const belly = scene.add.ellipse(0, 6, 8, 9, UNDER, 0.95);
  // Belly scale lines
  const bellyLine1 = scene.add.line(0, 0, -3, 3, 3, 3, SCALE_DARK, 0.7).setLineWidth(0.4).setOrigin(0, 0);
  const bellyLine2 = scene.add.line(0, 0, -3, 6, 3, 6, SCALE_DARK, 0.7).setLineWidth(0.4).setOrigin(0, 0);
  const bellyLine3 = scene.add.line(0, 0, -3, 9, 3, 9, SCALE_DARK, 0.7).setLineWidth(0.4).setOrigin(0, 0);
  // Spine ridge spikes
  const spike1 = scene.add.triangle(0, -2, 0, 0, -1, 2, 1, 2, FROST).setStrokeStyle(0.3, SCALE_DARK, 1);
  const spike2 = scene.add.triangle(0, 2, 0, 0, -1, 2, 1, 2, FROST).setStrokeStyle(0.3, SCALE_DARK, 1);

  // ---- ARMS — clawed forelimbs cradling ice tome ----
  const armR = scene.add.rectangle(6, -1, 3, 8, SCALE).setStrokeStyle(0.6, SCALE_DARK, 1).setRotation(-0.5);
  const handR = scene.add.circle(9, -5, 1.8, SCALE).setStrokeStyle(0.5, SCALE_DARK, 1);
  const clawR = scene.add.triangle(11, -6, 0, 0, -0.8, 1.8, 0.8, 1.8, FROST).setRotation(-0.5);
  const armL = scene.add.rectangle(-6, -1, 3, 8, SCALE).setStrokeStyle(0.6, SCALE_DARK, 1).setRotation(0.5);
  const handL = scene.add.circle(-9, -5, 1.8, SCALE).setStrokeStyle(0.5, SCALE_DARK, 1);
  const clawL = scene.add.triangle(-11, -6, 0, 0, -0.8, 1.8, 0.8, 1.8, FROST).setRotation(0.5);

  // ---- ICE TOME / FROST ORB — Auroth's signature spellbook of ice ----
  const tomeAura = scene.add.circle(0, -8, 10, ICE, 0.4);
  scene.tweens.add({ targets: tomeAura, scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.3, to: 0.55 }, yoyo: true, repeat: -1, duration: 520 });
  const tomeOrb = scene.add.circle(0, -8, 6, FROST).setStrokeStyle(1, ICE, 1);
  // Crystal facets inside the orb
  const facet1 = scene.add.line(0, 0, -2, -10, 2, -6, ICE, 0.95).setLineWidth(0.5).setOrigin(0, 0);
  const facet2 = scene.add.line(0, 0, 2, -10, -2, -6, ICE, 0.95).setLineWidth(0.5).setOrigin(0, 0);
  const facetCenter = scene.add.circle(0, -8, 1.4, 0xffffff, 1);
  // Ice shards orbiting orb
  const shard1 = scene.add.triangle(-4, -10, 0, 0, -0.9, 2.5, 0.9, 2.5, ICE).setStrokeStyle(0.3, FROST, 1);
  const shard2 = scene.add.triangle(4, -10, 0, 0, -0.9, 2.5, 0.9, 2.5, ICE).setStrokeStyle(0.3, FROST, 1).setRotation(Math.PI);
  const shard3 = scene.add.triangle(0, -13, 0, 0, -0.9, 2.5, 0.9, 2.5, ICE).setStrokeStyle(0.3, FROST, 1);
  scene.tweens.add({ targets: [shard1, shard2, shard3], angle: 360, repeat: -1, duration: 2400 });

  // ---- LONG DRAGON NECK + HEAD ----
  const neck = scene.add.rectangle(0, -10, 4, 6, SCALE).setStrokeStyle(0.6, SCALE_DARK, 1);
  const head = scene.add.ellipse(0, -15, 9, 7, SCALE).setStrokeStyle(0.9, SCALE_DARK, 1);
  // Long dragon snout — extends forward
  const snout = scene.add.polygon(0, -16, [
    -2, 0,
    -3, 2,
    -2, 4,
     2, 4,
     3, 2,
     2, 0,
     5, 1,
     5, 3,
     2, 5,
    -2, 5,
    -5, 3,
    -5, 1,
  ], SCALE).setStrokeStyle(0.6, SCALE_DARK, 1);
  // Nostril
  const nostrilL = scene.add.circle(-2, -12, 0.4, 0x000);
  const nostrilR = scene.add.circle(2, -12, 0.4, 0x000);
  // Slim glowing yellow dragon eyes
  const eyeL = scene.add.ellipse(-2.5, -16, 2.6, 1.8, 0xfff8a8).setStrokeStyle(0.5, SCALE_DARK, 1);
  const eyeR = scene.add.ellipse(2.5, -16, 2.6, 1.8, 0xfff8a8).setStrokeStyle(0.5, SCALE_DARK, 1);
  const pupilL = scene.add.rectangle(-2.5, -16, 0.5, 1.4, 0x000);
  const pupilR = scene.add.rectangle(2.5, -16, 0.5, 1.4, 0x000);
  // Frost-spike crown horns curving back
  const hornL = scene.add.polygon(-4, -18, [0, 0, -1, -3, -2, -6, -3, -2, -1, 1], FROST).setStrokeStyle(0.5, SCALE_DARK, 1);
  const hornR = scene.add.polygon(4, -18, [0, 0, 1, -3, 2, -6, 3, -2, 1, 1], FROST).setStrokeStyle(0.5, SCALE_DARK, 1);
  // Mid horn
  const hornC = scene.add.polygon(0, -20, [0, 0, -1, -3, 0, -5, 1, -3], FROST).setStrokeStyle(0.5, SCALE_DARK, 1);
  // Frost breath puff at nostril (idle)
  const breath = scene.add.circle(0, -12, 1.5, FROST, 0.55);
  scene.tweens.add({ targets: breath, alpha: { from: 0, to: 0.6 }, scale: { from: 0.6, to: 1.6 }, y: -14, yoyo: true, repeat: -1, duration: 1100 });
  // Eye glow pulse
  scene.tweens.add({ targets: [eyeL, eyeR], alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 700 });

  // ---- TAIL behind body ----
  const tailSeg1 = scene.add.ellipse(-3, 12, 4, 3, SCALE_DARK).setStrokeStyle(0.5, 0x000, 1);
  const tailSeg2 = scene.add.ellipse(-7, 11, 3.5, 2.5, SCALE_DARK).setStrokeStyle(0.5, 0x000, 1);
  const tailTip = scene.add.triangle(-10, 9, 0, 0, -3, 2, 0, 4, FROST).setStrokeStyle(0.4, SCALE_DARK, 1);
  scene.tweens.add({ targets: [tailSeg2, tailTip], y: "+=1.2", yoyo: true, repeat: -1, duration: 900, ease: "Sine.easeInOut" });

  c.add([
    // Wings + tail behind
    wingL, featherL1, featherL2, featherL3,
    wingR, featherR1, featherR2, featherR3,
    tailSeg1, tailSeg2, tailTip,
    // Legs
    legL, legR, talonL1, talonL2, talonL3, talonR1, talonR2, talonR3,
    // Torso
    torso, belly, bellyLine1, bellyLine2, bellyLine3, spike1, spike2,
    // Arms
    armL, handL, clawL, armR, handR, clawR,
    // Ice tome (orb)
    tomeAura, tomeOrb, facet1, facet2, facetCenter, shard1, shard2, shard3,
    // Head
    neck, head, snout, breath, nostrilL, nostrilR, eyeL, eyeR, pupilL, pupilR,
    hornL, hornR, hornC,
  ]);

  const castAnim = () => {
    // Wings flare + tome flash + brief frost burst.
    scene.tweens.add({ targets: [wingL, featherL1, featherL2, featherL3], scaleX: 1.15, y: "-=2", duration: 130, yoyo: true, ease: "Sine.easeInOut" });
    scene.tweens.add({ targets: [wingR, featherR1, featherR2, featherR3], scaleX: 1.15, y: "-=2", duration: 130, yoyo: true, ease: "Sine.easeInOut" });
    scene.tweens.add({ targets: [tomeOrb, facetCenter, tomeAura], scale: { from: 1.6, to: 1 }, duration: 240, ease: "Quad.easeOut" });
    scene.tweens.add({ targets: [shard1, shard2, shard3], scale: { from: 1.8, to: 1 }, duration: 240 });
    scene.tweens.add({ targets: head, x: 1, duration: 80, yoyo: true });
  };

  void p;
  return { castPoint: { x: 0, y: -8 }, orb: tomeOrb, castAnim };
}

// =====================================================================
// VENOMANCER — Reptilian plague shaman. Hooded scaled head with crown of
// curved spikes, green-teal scaly body, snake tail coiled behind, jagged
// spine ridge, dripping fangs, slit-pupil yellow eyes, holds a venom orb
// dripping plague green liquid. Reference: Dota 2 Venomancer — bipedal
// reptilian, signature poison-DOT specialist.
// =====================================================================
function buildVenomancer(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {

  // ---- COILED SNAKE TAIL behind/below body (drawn first, sits behind) ----
  // Tail starts from bottom of torso, curls right, then loops around
  const tailSeg1 = scene.add.ellipse(0, 12, 6, 4, 0x2a4a2a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  const tailSeg2 = scene.add.ellipse(5, 14, 5, 3.5, 0x2a4a2a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  const tailSeg3 = scene.add.ellipse(10, 13, 4.5, 3, 0x4ea84a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  const tailSeg4 = scene.add.ellipse(13, 10, 3.5, 2.5, 0x4ea84a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  const tailTip = scene.add.triangle(15, 7, 0, 2, -2, -2, 2, -2, 0xc8ff8a).setStrokeStyle(0.5, 0x0a1a0a, 1);
  // Tail scale dots (markings)
  const tailScale1 = scene.add.circle(5, 14, 0.4, 0xc8ff8a);
  const tailScale2 = scene.add.circle(10, 13, 0.4, 0xc8ff8a);
  const tailScale3 = scene.add.circle(13, 10, 0.3, 0xc8ff8a);
  // Idle tail sway
  scene.tweens.add({
    targets: [tailSeg3, tailSeg4, tailTip, tailScale2, tailScale3],
    y: "+=1.5",
    yoyo: true,
    repeat: -1,
    duration: 1300,
    ease: "Sine.easeInOut",
  });

  // ---- CLAWED REPTILIAN FEET ----
  const footL = scene.add.polygon(-3, 20, [-2, 0, 2, 0, 3, 2, -3, 2], 0x2a4a2a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  const footR = scene.add.polygon(3, 20, [-2, 0, 2, 0, 3, 2, -3, 2], 0x2a4a2a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  // Toe claws
  const clawL1 = scene.add.triangle(-4.5, 21, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);
  const clawL2 = scene.add.triangle(-3, 21.5, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);
  const clawL3 = scene.add.triangle(-1.5, 21, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);
  const clawR1 = scene.add.triangle(1.5, 21, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);
  const clawR2 = scene.add.triangle(3, 21.5, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);
  const clawR3 = scene.add.triangle(4.5, 21, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);

  // ---- LEGS (scaled green) ----
  const legL = scene.add.rectangle(-3, 14, 3, 8, 0x2a4a2a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  const legR = scene.add.rectangle(3, 14, 3, 8, 0x2a4a2a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  // Scale dot accents
  const legScaleL = scene.add.circle(-3, 16, 0.4, 0x4ea84a);
  const legScaleR = scene.add.circle(3, 16, 0.4, 0x4ea84a);

  // ---- TATTERED PLAGUE ROBE / KILT (dark green with bone trim) ----
  const robe = scene.add.polygon(0, 9,
    [-3, -5, 3, -5, 8, 9, -8, 9],
    0x1a2a1a,
  ).setStrokeStyle(1, 0x000, 1);
  // Jagged hem (tattered)
  const hemNotch1 = scene.add.triangle(-6, 13, 0, 0, -1.5, 2.5, 1.5, 2.5, 0x000);
  const hemNotch2 = scene.add.triangle(-2, 13, 0, 0, -1.5, 2.5, 1.5, 2.5, 0x000);
  const hemNotch3 = scene.add.triangle(2, 13, 0, 0, -1.5, 2.5, 1.5, 2.5, 0x000);
  const hemNotch4 = scene.add.triangle(6, 13, 0, 0, -1.5, 2.5, 1.5, 2.5, 0x000);
  // Bone fragments hanging from belt
  const boneL = scene.add.rectangle(-5, 7, 0.8, 3, 0xeae4d4).setStrokeStyle(0.4, 0x6a6a3a, 1);
  const boneR = scene.add.rectangle(5, 7, 0.8, 3, 0xeae4d4).setStrokeStyle(0.4, 0x6a6a3a, 1);
  const boneSkull = scene.add.circle(0, 5, 1.3, 0xeae4d4).setStrokeStyle(0.5, 0x6a6a3a, 1);
  const boneSkullEye1 = scene.add.circle(-0.5, 5, 0.25, 0x000);
  const boneSkullEye2 = scene.add.circle(0.5, 5, 0.25, 0x000);

  // ---- HUNCHED REPTILIAN TORSO with SPINE RIDGE ----
  const torso = scene.add.ellipse(0, -2, 10, 11, 0x2a4a2a).setStrokeStyle(1, 0x0a1a0a, 1);
  // Lighter belly
  const belly = scene.add.ellipse(0, 0, 6, 8, 0x4ea84a, 0.7);
  // Belly scales (horizontal stripes)
  const bellyStripe1 = scene.add.line(0, 0, -2.5, -1, 2.5, -1, 0x2a4a2a).setLineWidth(0.4).setOrigin(0, 0);
  const bellyStripe2 = scene.add.line(0, 0, -2.5, 1, 2.5, 1, 0x2a4a2a).setLineWidth(0.4).setOrigin(0, 0);
  const bellyStripe3 = scene.add.line(0, 0, -2.5, 3, 2.5, 3, 0x2a4a2a).setLineWidth(0.4).setOrigin(0, 0);
  // Spine spike ridge (jagged triangles along the back)
  const spineSpike1 = scene.add.triangle(-3, -5, 0, 0, -1, 2, 1, 2, 0x6a8a3a).setRotation(-0.5);
  const spineSpike2 = scene.add.triangle(-1.5, -7, 0, 0, -1.2, 2.5, 1.2, 2.5, 0x6a8a3a);
  const spineSpike3 = scene.add.triangle(0, -8, 0, 0, -1.4, 3, 1.4, 3, 0x6a8a3a);
  const spineSpike4 = scene.add.triangle(1.5, -7, 0, 0, -1.2, 2.5, 1.2, 2.5, 0x6a8a3a);
  const spineSpike5 = scene.add.triangle(3, -5, 0, 0, -1, 2, 1, 2, 0x6a8a3a).setRotation(0.5);

  // ---- LEFT CLAWED ARM — extended, holds VENOM ORB ----
  const armL = scene.add.rectangle(-7, 0, 2.4, 8, 0x2a4a2a).setStrokeStyle(0.7, 0x0a1a0a, 1);
  armL.setRotation(0.4);
  const handL = scene.add.circle(-9, 5, 1.7, 0x4ea84a).setStrokeStyle(0.5, 0x0a1a0a, 1);
  // Claw fingers
  const fingClawL1 = scene.add.triangle(-11, 6, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);
  const fingClawL2 = scene.add.triangle(-9, 7, 0, 0, -0.7, 1.5, 0.7, 1.5, 0xeae4d4);

  // ---- RIGHT ARM — RAISED, holding VENOM ORB high ----
  const armR = scene.add.rectangle(7, -4, 2.4, 9, 0x2a4a2a).setStrokeStyle(0.7, 0x0a1a0a, 1);
  armR.setRotation(-0.5);
  const handR = scene.add.circle(11, -9, 1.8, 0x4ea84a).setStrokeStyle(0.5, 0x0a1a0a, 1);
  // Claw fingers gripping orb
  const fingClawR1 = scene.add.triangle(12.5, -10, 0, 0, -0.6, 1.4, 0.6, 1.4, 0xeae4d4).setRotation(0.6);
  const fingClawR2 = scene.add.triangle(13, -8, 0, 0, -0.6, 1.4, 0.6, 1.4, 0xeae4d4).setRotation(1.2);

  // ---- VENOM ORB — bright plague-green sphere with poison drips ----
  const orbHalo = scene.add.circle(13, -13, 5, 0x4ea84a, 0.4);
  scene.tweens.add({ targets: orbHalo, scale: { from: 1, to: 1.3 }, alpha: { from: 0.5, to: 0.15 }, yoyo: true, repeat: -1, duration: 1000 });
  const orbOuter = scene.add.circle(13, -13, 4.5, 0x6abf60).setStrokeStyle(1.2, 0x2a4a2a, 1);
  const orbMid = scene.add.circle(13, -13, 2.4, 0x4ea84a);
  const orbCore = scene.add.circle(13, -13, 1, 0xc8ff8a);
  // Drip falling from orb
  const drip1 = scene.add.ellipse(13, -9, 1.2, 2.4, 0x4ea84a);
  const drip2 = scene.add.circle(11, -7, 0.6, 0x4ea84a, 0.7);
  scene.tweens.add({ targets: drip1, y: "+=4", alpha: { from: 1, to: 0.2 }, yoyo: true, repeat: -1, duration: 900 });
  scene.tweens.add({ targets: drip2, y: "+=5", alpha: { from: 0.7, to: 0 }, yoyo: true, repeat: -1, duration: 1100 });

  // ---- PLAGUE GREEN SMOKE AURA (around hero) ----
  const smoke1 = scene.add.circle(-10, -3, 3, 0x4ea84a, 0.25);
  const smoke2 = scene.add.circle(11, 2, 2.6, 0x4ea84a, 0.25);
  scene.tweens.add({ targets: smoke1, y: "-=4", alpha: { from: 0.3, to: 0 }, yoyo: true, repeat: -1, duration: 1400, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: smoke2, y: "-=4", alpha: { from: 0.3, to: 0 }, yoyo: true, repeat: -1, duration: 1700, ease: "Sine.easeInOut" });

  // ---- HOODED REPTILIAN HEAD ----
  // Hood backing (drawn first so head sits in front)
  const hood = scene.add.polygon(0, -11, [
    -7, 0, -8, -3, -5, -8, 0, -10, 5, -8, 8, -3, 7, 0,
  ], 0x1a2a1a).setStrokeStyle(1, 0x000, 1);
  // Inner head — elongated reptilian snout
  const head = scene.add.ellipse(0, -10, 8, 7, 0x2a4a2a).setStrokeStyle(0.7, 0x0a1a0a, 1);
  // Jaw / muzzle (extending forward)
  const muzzle = scene.add.ellipse(2, -8, 5, 3, 0x2a4a2a).setStrokeStyle(0.6, 0x0a1a0a, 1);
  // Slit pupil yellow eyes
  const eyeWhiteL = scene.add.circle(-1.4, -11, 1.6, 0xffd84a);
  const eyeWhiteR = scene.add.circle(1.4, -11, 1.6, 0xffd84a);
  // Vertical slit pupils
  const eyeSlitL = scene.add.rectangle(-1.4, -11, 0.5, 2.2, 0x000);
  const eyeSlitR = scene.add.rectangle(1.4, -11, 0.5, 2.2, 0x000);
  // Nose slits
  const noseSlit = scene.add.rectangle(3.5, -8.5, 0.8, 0.4, 0x000);
  // Open mouth — exposed fangs
  const mouth = scene.add.rectangle(2, -7, 3.5, 1, 0x1a0500);
  // Two long upper fangs
  const fangL = scene.add.triangle(1, -6.5, 0, 0, -0.5, 1.6, 0.5, 1.6, 0xffffff);
  const fangR = scene.add.triangle(3, -6.5, 0, 0, -0.5, 1.6, 0.5, 1.6, 0xffffff);
  // Forked tongue flicking
  const tongue = scene.add.triangle(5, -7.5, 0, 0, 1.5, -0.5, 1.5, 0.5, 0xc06080);
  scene.tweens.add({ targets: tongue, x: "+=1", yoyo: true, repeat: -1, duration: 600 });

  // ---- CROWN OF SPIKES (curved spikes around top of hood) ----
  const spikeBase = scene.add.ellipse(0, -15, 11, 2.4, 0x6a8a3a).setStrokeStyle(0.6, 0x2a4a2a, 1);
  const crownSpike1 = scene.add.triangle(-5, -17, 0, 0, -1, 3, 1, 3, 0xc8ff8a).setRotation(-0.6);
  const crownSpike2 = scene.add.triangle(-2.5, -19, 0, 0, -1, 4, 1, 4, 0xc8ff8a).setRotation(-0.2);
  const crownSpike3 = scene.add.triangle(0, -20, 0, 0, -1.2, 4.5, 1.2, 4.5, 0xc8ff8a);
  const crownSpike4 = scene.add.triangle(2.5, -19, 0, 0, -1, 4, 1, 4, 0xc8ff8a).setRotation(0.2);
  const crownSpike5 = scene.add.triangle(5, -17, 0, 0, -1, 3, 1, 3, 0xc8ff8a).setRotation(0.6);

  void p; // Venomancer uses fixed green palette; element tint via projectile

  c.add([
    tailSeg1, tailSeg2, tailSeg3, tailSeg4, tailTip,
    tailScale1, tailScale2, tailScale3,
    footL, footR,
    clawL1, clawL2, clawL3, clawR1, clawR2, clawR3,
    legL, legR, legScaleL, legScaleR,
    robe, hemNotch1, hemNotch2, hemNotch3, hemNotch4,
    boneL, boneR, boneSkull, boneSkullEye1, boneSkullEye2,
    torso, belly, bellyStripe1, bellyStripe2, bellyStripe3,
    spineSpike1, spineSpike2, spineSpike3, spineSpike4, spineSpike5,
    armL, handL, fingClawL1, fingClawL2,
    smoke1, smoke2,
    hood,
    head, muzzle, noseSlit,
    eyeWhiteL, eyeWhiteR, eyeSlitL, eyeSlitR,
    mouth, fangL, fangR, tongue,
    spikeBase,
    crownSpike1, crownSpike2, crownSpike3, crownSpike4, crownSpike5,
    armR, handR, fingClawR1, fingClawR2,
    orbHalo, orbOuter, orbMid, orbCore,
    drip1, drip2,
  ]);

  const castAnim = () => {
    // Right arm thrusts venom orb forward — orb flares.
    scene.tweens.add({
      targets: [armR, handR, fingClawR1, fingClawR2,
                orbHalo, orbOuter, orbMid, orbCore, drip1, drip2],
      x: "+=3",
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Orb pulse + halo flare
    scene.tweens.add({
      targets: [orbHalo, orbOuter, orbMid, orbCore],
      scale: { from: 1.6, to: 1 },
      duration: 220,
      ease: "Quad.easeOut",
    });
    // Head darts forward (fang strike) + tongue flick
    scene.tweens.add({
      targets: [head, muzzle, noseSlit, mouth, fangL, fangR, tongue,
                eyeWhiteL, eyeWhiteR, eyeSlitL, eyeSlitR],
      x: "+=2",
      duration: 90,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    // Smoke aura surges
    scene.tweens.add({
      targets: [smoke1, smoke2],
      scale: 1.6,
      alpha: 0,
      duration: 280,
      ease: "Quad.easeOut",
      onComplete: () => {
        smoke1.setAlpha(0.25);
        smoke2.setAlpha(0.25);
        smoke1.setScale(1);
        smoke2.setScale(1);
      },
    });
  };

  return { castPoint: { x: 13, y: -13 }, orb: orbCore, castAnim };
}

// =====================================================================
// BREWMASTER (Mangix) — Drunken panda monk. White-and-black panda head,
// orange monk robes, two huge brew jugs at belt, knotted bo staff. Cast
// = chug then breathe a fiery splash.
// =====================================================================
function buildBrewmaster(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- LEGS (short, brown wraps) ----
  const legL = scene.add.rectangle(-3.5, 14, 4, 9, 0x4a3018).setStrokeStyle(0.7, 0x000, 1);
  const legR = scene.add.rectangle(3.5, 14, 4, 9, 0x4a3018).setStrokeStyle(0.7, 0x000, 1);
  const footL = scene.add.ellipse(-3.5, 19, 5, 2.4, 0x1a0a00);
  const footR = scene.add.ellipse(3.5, 19, 5, 2.4, 0x1a0a00);

  // ---- ROBE / BELLY ----
  const robe = scene.add.ellipse(0, 4, 17, 14, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const bellyHi = scene.add.ellipse(0, 6, 9, 6, 0xeae0c0, 0.7);
  const sash = scene.add.rectangle(0, 8, 18, 2.6, p.trim).setStrokeStyle(0.5, 0x4a3018, 1);
  const sashKnot = scene.add.circle(7, 8, 1.6, p.trim).setStrokeStyle(0.4, 0x4a3018, 1);

  // ---- BREW JUGS (two huge ceramic jars hanging from belt) ----
  const jugL = scene.add.ellipse(-9, 11, 5, 6, 0x6a4028).setStrokeStyle(0.8, 0x000, 1);
  const jugLNeck = scene.add.rectangle(-9, 8, 1.6, 1.6, 0x4a2818);
  const jugLCork = scene.add.rectangle(-9, 6.8, 1.2, 0.8, 0xc8a060);
  const jugR = scene.add.ellipse(9, 11, 5, 6, 0x6a4028).setStrokeStyle(0.8, 0x000, 1);
  const jugRNeck = scene.add.rectangle(9, 8, 1.6, 1.6, 0x4a2818);
  const jugRCork = scene.add.rectangle(9, 6.8, 1.2, 0.8, 0xc8a060);

  // ---- ARMS — left holds bo staff, right hand near jug ----
  const armL = scene.add.rectangle(-7, -1, 2.6, 8, p.primary).setStrokeStyle(0.7, p.primaryDark, 1).setRotation(-0.15);
  const handL = scene.add.circle(-8, 4, 1.7, 0x1a1010).setStrokeStyle(0.6, 0x000, 1);
  const armR = scene.add.rectangle(7, 1, 2.6, 8, p.primary).setStrokeStyle(0.7, p.primaryDark, 1).setRotation(0.4);
  const handR = scene.add.circle(11, 5, 1.7, 0x1a1010).setStrokeStyle(0.6, 0x000, 1);

  // ---- BO STAFF (held diagonally across body) ----
  const staff = scene.add.rectangle(-6, -2, 1.4, 28, 0x3a1a08).setStrokeStyle(0.5, 0x000, 1).setRotation(-0.18);
  const staffTie = scene.add.rectangle(-2, -10, 2.4, 1, 0xc02020);

  // ---- HEAD — panda (white face, black ears + eye patches) ----
  const headWhite = scene.add.circle(0, -12, 5.5, 0xffffff).setStrokeStyle(0.9, 0x1a1010, 1);
  const earL = scene.add.circle(-4.5, -16, 2.2, 0x1a1010);
  const earR = scene.add.circle(4.5, -16, 2.2, 0x1a1010);
  const earLin = scene.add.circle(-4.5, -16, 1.1, 0x6a4028);
  const earRin = scene.add.circle(4.5, -16, 1.1, 0x6a4028);
  const eyePatchL = scene.add.ellipse(-2, -12.5, 2.6, 2.8, 0x1a1010);
  const eyePatchR = scene.add.ellipse(2, -12.5, 2.6, 2.8, 0x1a1010);
  const eyeL = scene.add.circle(-2, -12.5, 1.4, p.energy);
  const eyeR = scene.add.circle(2, -12.5, 1.4, p.energy);
  const eyeShineL = scene.add.circle(-1.4, -12.9, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.6, -12.9, 0.5, 0xffffff);
  // Round nose + smirky mouth
  const nose = scene.add.ellipse(0, -10, 1.4, 1, 0x1a1010);
  const mouth = scene.add.line(0, 0, -1.5, -8, 1.5, -8, 0x1a1010, 1).setLineWidth(0.7).setOrigin(0, 0);
  // Drunk red blush
  const blushL = scene.add.ellipse(-3.5, -10.5, 2, 1, 0xff8a8a, 0.6);
  const blushR = scene.add.ellipse(3.5, -10.5, 2, 1, 0xff8a8a, 0.6);

  // ---- FIRE PUFF in front of mouth (the brew breath orb) ----
  const fireOuter = scene.add.circle(0, -5, 6, p.energy, 0.5);
  const fireCore = scene.add.circle(0, -5, 3.2, p.trim, 1);
  const fireHot = scene.add.circle(0, -5, 1.2, 0xffffff);
  scene.tweens.add({ targets: fireOuter, scale: { from: 0.85, to: 1.2 }, alpha: { from: 0.3, to: 0.7 }, yoyo: true, repeat: -1, duration: 380 });
  scene.tweens.add({ targets: fireCore, scale: { from: 0.95, to: 1.1 }, yoyo: true, repeat: -1, duration: 240 });

  c.add([
    legL, legR, footL, footR,
    staff, staffTie,
    robe, bellyHi, sash, sashKnot,
    jugL, jugLNeck, jugLCork, jugR, jugRNeck, jugRCork,
    armL, handL, armR, handR,
    headWhite, earL, earR, earLin, earRin,
    eyePatchL, eyePatchR, eyeL, eyeR, eyeShineL, eyeShineR, nose, mouth, blushL, blushR,
    fireOuter, fireCore, fireHot,
  ]);

  const castAnim = () => {
    scene.tweens.add({ targets: [fireOuter, fireCore, fireHot], scale: { from: 2.1, to: 1 }, alpha: { from: 1, to: 0.6 }, duration: 280, ease: "Quad.easeOut" });
    scene.tweens.add({ targets: [headWhite, earL, earR, eyePatchL, eyePatchR, eyeL, eyeR, eyeShineL, eyeShineR, nose, mouth, blushL, blushR, earLin, earRin], y: "+=1.5", duration: 100, yoyo: true, ease: "Sine.easeInOut" });
    scene.tweens.add({ targets: [armR, handR], rotation: "+=0.25", duration: 120, yoyo: true, ease: "Sine.easeInOut" });
  };

  return { castPoint: { x: 0, y: -5 }, orb: fireCore, castAnim };
}

// =====================================================================
// PUGNA — Oblivion lich. Hairless purple-skinned sorcerer with skull-like
// face, hovering legless above a robe, glowing eyes, hand cradling a
// soul-orb of decay.
// =====================================================================
function buildPugna(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- BACKDROP — slowly rotating hexagonal void sigil ----
  const sigilOuter = scene.add.polygon(0, -2, [0,-22, 19,-11, 19,11, 0,22, -19,11, -19,-11], 0x000, 0).setStrokeStyle(0.8, p.energy, 0.7);
  const sigilInner = scene.add.polygon(0, -2, [0,-14, 12,-7, 12,7, 0,14, -12,7, -12,-7], 0x000, 0).setStrokeStyle(0.6, p.trim, 0.55);
  scene.tweens.add({ targets: sigilOuter, rotation: Math.PI * 2, repeat: -1, duration: 6000 });
  scene.tweens.add({ targets: sigilInner, rotation: -Math.PI * 2, repeat: -1, duration: 4500 });
  // Floating ether motes around the sigil
  const motes: Phaser.GameObjects.Arc[] = [];
  for (let i = 0; i < 5; i++) {
    const ang = (i / 5) * Math.PI * 2;
    const mote = scene.add.circle(Math.cos(ang) * 17, Math.sin(ang) * 17 - 2, 0.9, p.energy, 0.85);
    motes.push(mote);
    scene.tweens.add({ targets: mote, alpha: { from: 0.4, to: 1 }, yoyo: true, repeat: -1, duration: 400 + i * 90, delay: i * 60 });
  }

  // ---- HOVER WISP (no legs — robe tapers into smoky tail) ----
  const wispOuter = scene.add.ellipse(0, 18, 16, 6, p.primaryDark, 0.7);
  const wispMid = scene.add.ellipse(0, 16, 12, 4, p.primary, 0.85);
  // Premium: extra ground glow ring and rising soul flames
  const groundGlow = scene.add.ellipse(0, 19, 22, 5, p.energy, 0.45);
  scene.tweens.add({ targets: groundGlow, scaleX: { from: 0.85, to: 1.25 }, alpha: { from: 0.2, to: 0.55 }, yoyo: true, repeat: -1, duration: 700 });
  const soulFlame1 = scene.add.triangle(-5, 17, 0, 0, -1.2, -3, 1.2, -3, p.energy, 0.7);
  const soulFlame2 = scene.add.triangle(5, 17, 0, 0, -1.2, -3, 1.2, -3, p.energy, 0.7);
  scene.tweens.add({ targets: [soulFlame1, soulFlame2], y: "-=2", alpha: { from: 0.8, to: 0.1 }, yoyo: true, repeat: -1, duration: 620 });
  scene.tweens.add({ targets: [wispOuter, wispMid], scaleX: { from: 0.85, to: 1.15 }, alpha: { from: 0.5, to: 0.85 }, yoyo: true, repeat: -1, duration: 540 });

  // ---- ROBE (long, tapering down into wisp) ----
  const robe = scene.add.polygon(0, 6, [
    -8, -8, 8, -8, 11, 6, 7, 12, 0, 14, -7, 12, -11, 6,
  ], p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const robeMid = scene.add.line(0, 0, 0, -2, 0, 13, p.primaryDark, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  // Trim along bottom
  const robeTrim = scene.add.polygon(0, 12, [-9, 0, 9, 0, 6, 3, 0, 4, -6, 3], p.trim);

  // ---- TORSO upper (collar) ----
  const collar = scene.add.polygon(0, -4, [-7, 4, 7, 4, 5, -3, -5, -3], p.primaryDark);
  const collarTrim = scene.add.line(0, 0, -7, 0, 7, 0, p.trim, 1).setLineWidth(0.8).setOrigin(0, 0);

  // ---- ARMS — extended forward, cradling soul orb ----
  const armL = scene.add.rectangle(-5, -1, 2.2, 8, p.primary).setStrokeStyle(0.7, p.primaryDark, 1).setRotation(0.7);
  const handL = scene.add.circle(-1.5, 4, 1.6, p.skin).setStrokeStyle(0.6, p.primaryDark, 1);
  const armR = scene.add.rectangle(5, -1, 2.2, 8, p.primary).setStrokeStyle(0.7, p.primaryDark, 1).setRotation(-0.7);
  const handR = scene.add.circle(1.5, 4, 1.6, p.skin).setStrokeStyle(0.6, p.primaryDark, 1);

  // ---- SOUL ORB (decay/oblivion energy) ----
  const orbAura = scene.add.circle(0, 4, 8, p.energy, 0.4);
  const orbCore = scene.add.circle(0, 4, 3.8, p.trim).setStrokeStyle(0.8, 0xffffff, 1);
  const orbHot = scene.add.circle(0, 4, 1.2, 0xffffff);
  // Wispy tendrils trailing off the orb
  const tendril1 = scene.add.line(0, 0, 0, 4, -4, 1, p.energy, 0.85).setLineWidth(0.7).setOrigin(0, 0);
  const tendril2 = scene.add.line(0, 0, 0, 4, 4, 1, p.energy, 0.85).setLineWidth(0.7).setOrigin(0, 0);
  scene.tweens.add({ targets: orbAura, scale: { from: 0.85, to: 1.25 }, alpha: { from: 0.25, to: 0.6 }, yoyo: true, repeat: -1, duration: 360 });
  scene.tweens.add({ targets: [tendril1, tendril2], alpha: { from: 0.4, to: 1 }, yoyo: true, repeat: -1, duration: 280 });

  // ---- HEAD — bald, skull-like, purple-greenish skin ----
  const head = scene.add.circle(0, -12, 4.8, p.skin).setStrokeStyle(0.9, p.primaryDark, 1);
  // Sunken cheeks line
  const cheekL = scene.add.line(0, 0, -3, -10, -2.4, -8.5, p.primaryDark, 0.85).setLineWidth(0.5).setOrigin(0, 0);
  const cheekR = scene.add.line(0, 0, 3, -10, 2.4, -8.5, p.primaryDark, 0.85).setLineWidth(0.5).setOrigin(0, 0);
  // Recessed glowing eyes
  const eyeSocketL = scene.add.ellipse(-1.8, -12.5, 2.2, 1.6, 0x000);
  const eyeSocketR = scene.add.ellipse(1.8, -12.5, 2.2, 1.6, 0x000);
  const eyeGlowL = scene.add.circle(-1.8, -12.5, 1.6, p.energy, 0.95);
  const eyeGlowR = scene.add.circle(1.8, -12.5, 1.6, p.energy, 0.95);
  const eyeShineL = scene.add.circle(-1.2, -12.9, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.4, -12.9, 0.5, 0xffffff);
  scene.tweens.add({ targets: [eyeGlowL, eyeGlowR], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 480 });
  // Grim mouth
  const mouth = scene.add.line(0, 0, -2, -9.5, 2, -9.5, p.primaryDark, 1).setLineWidth(0.6).setOrigin(0, 0);

  // ---- HOOD/COLLAR HORNS (Pugna's signature crown ridges on hood) ----
  const hornL = scene.add.triangle(-5, -16, 0, 0, -1.2, 4, 1.2, 4, p.primaryDark);
  const hornC = scene.add.triangle(0, -18, 0, 0, -1.4, 4, 1.4, 4, p.primaryDark);
  const hornR = scene.add.triangle(5, -16, 0, 0, -1.2, 4, 1.2, 4, p.primaryDark);

  // ---- PREMIUM ROBE TRIM — gold sigil bands ----
  const sigilBandL = scene.add.line(0, 0, -8, 8, -8, 14, p.trim, 0.85).setLineWidth(0.6).setOrigin(0, 0);
  const sigilBandR = scene.add.line(0, 0, 8, 8, 8, 14, p.trim, 0.85).setLineWidth(0.6).setOrigin(0, 0);
  const robeRune = scene.add.circle(0, 8, 1.6, p.energy, 0.9).setStrokeStyle(0.5, p.trim, 1);
  scene.tweens.add({ targets: robeRune, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 600 });

  c.add([
    sigilOuter, sigilInner, ...motes,
    groundGlow, soulFlame1, soulFlame2,
    wispOuter, wispMid,
    robe, robeMid, robeTrim, sigilBandL, sigilBandR, robeRune,
    collar, collarTrim,
    armL, handL, armR, handR,
    orbAura, orbCore, orbHot, tendril1, tendril2,
    head, cheekL, cheekR, eyeSocketL, eyeSocketR, eyeGlowL, eyeGlowR, eyeShineL, eyeShineR, mouth,
    hornL, hornC, hornR,
  ]);

  const castAnim = () => {
    scene.tweens.add({ targets: [orbAura, orbCore, orbHot], scale: { from: 2, to: 1 }, alpha: { from: 1, to: 0.6 }, duration: 260, ease: "Quad.easeOut" });
    scene.tweens.add({ targets: [tendril1, tendril2], alpha: { from: 1, to: 0.4 }, scaleX: { from: 1.5, to: 1 }, duration: 240 });
    scene.tweens.add({ targets: [head, eyeGlowL, eyeGlowR, eyeShineL, eyeShineR, eyeSocketL, eyeSocketR, hornL, hornC, hornR, cheekL, cheekR, mouth], y: "-=1.2", duration: 110, yoyo: true });
  };

  return { castPoint: { x: 0, y: 4 }, orb: orbCore, castAnim };
}

// =====================================================================
// BEASTMASTER (Karroch) — Wild barbarian. Bare-chested, leather kilt,
// tusked horned helm, massive double-bladed axe over shoulder, war horn
// at hip. Brown/red palette.
// =====================================================================
function buildBeastmaster(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- LEGS (heavy hide wraps, fur boots) ----
  const legL = scene.add.rectangle(-4, 14, 5, 9, 0x6a4028).setStrokeStyle(0.7, 0x000, 1);
  const legR = scene.add.rectangle(4, 14, 5, 9, 0x6a4028).setStrokeStyle(0.7, 0x000, 1);
  const furL = scene.add.ellipse(-4, 19, 6, 3, 0x3a2010);
  const furR = scene.add.ellipse(4, 19, 6, 3, 0x3a2010);
  const bootL = scene.add.rectangle(-4, 21, 5.5, 2.4, 0x1a0a00);
  const bootR = scene.add.rectangle(4, 21, 5.5, 2.4, 0x1a0a00);

  // ---- KILT (leather strips) ----
  const kilt = scene.add.polygon(0, 7, [
    -8, -2, 8, -2, 9, 4, 5, 7, 2, 4, 0, 7, -2, 4, -5, 7, -9, 4,
  ], 0x4a2818).setStrokeStyle(0.8, 0x000, 1);
  const kiltBelt = scene.add.rectangle(0, 5, 17, 2, 0x1a0a00);
  const buckle = scene.add.rectangle(0, 5, 3, 2.4, p.trim).setStrokeStyle(0.5, 0x000, 1);

  // ---- BARE TORSO (muscled, tan skin) ----
  const torso = scene.add.ellipse(0, -1, 14, 13, p.skin).setStrokeStyle(0.9, 0x4a2818, 1);
  // Pec/abs detail
  const pecLine = scene.add.line(0, 0, -5, -3, 5, -3, 0x6a3818, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  const abLine = scene.add.line(0, 0, 0, -3, 0, 5, 0x6a3818, 0.6).setLineWidth(0.5).setOrigin(0, 0);
  // Leather harness across chest
  const harness = scene.add.rectangle(0, -2, 16, 2, 0x4a2818).setStrokeStyle(0.5, 0x000, 1).setRotation(-0.3);
  // War horn at hip
  const horn = scene.add.polygon(8, 6, [0, -2, 4, -1, 5, 1, 1, 2], 0xeae0c0).setStrokeStyle(0.5, 0x4a3018, 1);

  // ---- ARMS — left arm forward (axe haft), right arm holds axe head over shoulder ----
  const armL = scene.add.rectangle(-7, -1, 3, 9, p.skin).setStrokeStyle(0.8, 0x4a2818, 1).setRotation(0.2);
  const handL = scene.add.circle(-9, 5, 1.8, p.skin).setStrokeStyle(0.6, 0x4a2818, 1);
  const armR = scene.add.rectangle(7, -3, 3, 9, p.skin).setStrokeStyle(0.8, 0x4a2818, 1).setRotation(-0.5);
  const handR = scene.add.circle(11, -8, 1.8, p.skin).setStrokeStyle(0.6, 0x4a2818, 1);
  // Bicep band
  const bandL = scene.add.rectangle(-7, -3, 4, 1, 0x4a2818);
  const bandR = scene.add.rectangle(7, -5, 4, 1, 0x4a2818);

  // ---- DOUBLE-BLADED AXE (massive, raised diagonally over right shoulder) ----
  const axeShaft = scene.add.rectangle(2, -10, 1.6, 22, 0x3a1a08).setStrokeStyle(0.6, 0x000, 1).setRotation(-0.3);
  const axeWrap = scene.add.rectangle(2, -8, 2.2, 1.5, 0xc02020);
  // Two big blades sweeping out from the head
  const bladeL = scene.add.polygon(-4, -18, [
    0, 0, -7, -3, -8, 2, -3, 5,
  ], p.metal).setStrokeStyle(0.8, 0x000, 1);
  const bladeR = scene.add.polygon(8, -18, [
    0, 0, 7, -3, 8, 2, 3, 5,
  ], p.metal).setStrokeStyle(0.8, 0x000, 1);
  const bladeShine = scene.add.line(0, 0, -3, -19, 7, -19, 0xffffff, 0.7).setLineWidth(0.8).setOrigin(0, 0);
  // Glowing rune on axe core (energy color)
  const axeRune = scene.add.circle(2, -18, 2.4, p.energy, 0.95).setStrokeStyle(0.6, 0xffffff, 1);
  scene.tweens.add({ targets: axeRune, alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 540 });

  // ---- HEAD — fierce, bearded barbarian face ----
  const head = scene.add.circle(0, -12, 5, p.skin).setStrokeStyle(0.9, 0x4a2818, 1);
  const beard = scene.add.polygon(0, -8, [-4, -2, 4, -2, 3, 4, 0, 5, -3, 4], p.hair).setStrokeStyle(0.5, 0x000, 1);
  const eyeL = scene.add.circle(-1.8, -13, 1.5, p.energy);
  const eyeR = scene.add.circle(1.8, -13, 1.5, p.energy);
  const eyeShineL = scene.add.circle(-1.2, -13.5, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(2.4, -13.5, 0.5, 0xffffff);
  scene.tweens.add({ targets: [eyeL, eyeR], alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 520 });
  const browL = scene.add.rectangle(-2, -14.5, 2.4, 0.7, p.hair);
  const browR = scene.add.rectangle(2, -14.5, 2.4, 0.7, p.hair);
  // Nose stripe (war paint)
  const warStripe = scene.add.rectangle(0, -12, 0.8, 4, 0xc02020);

  // ---- TUSKED HORN HELM ----
  const helm = scene.add.polygon(0, -16, [-6, 2, 6, 2, 5, -2, -5, -2], 0x4a2818).setStrokeStyle(0.6, 0x000, 1);
  const hornHelmL = scene.add.triangle(-6, -16, 0, 0, -3, 5, 0, 5, 0xeae0c0).setStrokeStyle(0.4, 0x4a3018, 1).setRotation(-0.4);
  const hornHelmR = scene.add.triangle(6, -16, 0, 0, 0, 5, 3, 5, 0xeae0c0).setStrokeStyle(0.4, 0x4a3018, 1).setRotation(0.4);
  // Tusks descending past jaw
  const tuskL = scene.add.triangle(-3, -10, 0, 0, -0.7, 4, 0.7, 4, 0xeae0c0).setStrokeStyle(0.3, 0x4a3018, 1);
  const tuskR = scene.add.triangle(3, -10, 0, 0, -0.7, 4, 0.7, 4, 0xeae0c0).setStrokeStyle(0.3, 0x4a3018, 1);

  c.add([
    legL, legR, furL, furR, bootL, bootR,
    kilt, kiltBelt, buckle,
    torso, pecLine, abLine, harness, horn,
    armL, handL, bandL,
    axeShaft, axeWrap, bladeL, bladeR, bladeShine, axeRune,
    armR, handR, bandR,
    head, beard, browL, browR, eyeL, eyeR, eyeShineL, eyeShineR, warStripe,
    helm, hornHelmL, hornHelmR, tuskL, tuskR,
  ]);

  const castAnim = () => {
    // Axe slam — rotate the whole axe assembly
    scene.tweens.add({
      targets: [axeShaft, axeWrap, bladeL, bladeR, bladeShine, axeRune, armR, handR, bandR],
      angle: -12,
      duration: 90,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({ targets: axeRune, scale: { from: 1.8, to: 1 }, alpha: { from: 1, to: 0.6 }, duration: 240 });
    scene.tweens.add({ targets: [head, helm, hornHelmL, hornHelmR, beard, eyeL, eyeR, eyeShineL, eyeShineR, browL, browR, warStripe, tuskL, tuskR], y: "+=1.5", duration: 100, yoyo: true });
  };

  return { castPoint: { x: 2, y: -18 }, orb: axeRune, castAnim };
}

// =====================================================================
// TIDEHUNTER (Leviathan) — Hulking sea monster. Green fishlike head with
// mandibles + glowing eyes, coral spikes on shoulders, massive anchor
// gripped by both hands.
// =====================================================================
function buildTidehunter(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- LEGS (stout, scaly green) ----
  const legL = scene.add.rectangle(-4, 14, 5, 9, p.primaryDark).setStrokeStyle(0.7, 0x000, 1);
  const legR = scene.add.rectangle(4, 14, 5, 9, p.primaryDark).setStrokeStyle(0.7, 0x000, 1);
  // Webbed feet
  const footL = scene.add.polygon(-4, 21, [-3, 0, 3, 0, 2, 2, -2, 2], 0x2a4a3a).setStrokeStyle(0.5, 0x000, 1);
  const footR = scene.add.polygon(4, 21, [-3, 0, 3, 0, 2, 2, -2, 2], 0x2a4a3a).setStrokeStyle(0.5, 0x000, 1);

  // ---- HUNCHED TORSO (broad, scaly, bulging belly) ----
  const torso = scene.add.ellipse(0, 2, 18, 16, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  const bellyHi = scene.add.ellipse(0, 5, 11, 7, p.skin, 0.7);
  // Scale rows
  const scale1 = scene.add.line(0, 0, -6, -1, 6, -1, p.primaryDark, 0.7).setLineWidth(0.5).setOrigin(0, 0);
  const scale2 = scene.add.line(0, 0, -7, 3, 7, 3, p.primaryDark, 0.7).setLineWidth(0.5).setOrigin(0, 0);

  // ---- CORAL SHOULDER SPIKES ----
  const spikeL1 = scene.add.triangle(-9, -4, 0, 0, -1.5, 4, 1.5, 4, p.trim).setStrokeStyle(0.4, p.primaryDark, 1).setRotation(-0.4);
  const spikeL2 = scene.add.triangle(-7, -7, 0, 0, -1.2, 4, 1.2, 4, p.trim).setStrokeStyle(0.4, p.primaryDark, 1).setRotation(-0.2);
  const spikeR1 = scene.add.triangle(9, -4, 0, 0, -1.5, 4, 1.5, 4, p.trim).setStrokeStyle(0.4, p.primaryDark, 1).setRotation(0.4);
  const spikeR2 = scene.add.triangle(7, -7, 0, 0, -1.2, 4, 1.2, 4, p.trim).setStrokeStyle(0.4, p.primaryDark, 1).setRotation(0.2);

  // ---- ARMS — both grip anchor; raised across body ----
  const armL = scene.add.rectangle(-6, 0, 3, 9, p.primary).setStrokeStyle(0.7, p.primaryDark, 1).setRotation(0.4);
  const handL = scene.add.circle(-3, 5, 1.9, p.primary).setStrokeStyle(0.6, p.primaryDark, 1);
  const armR = scene.add.rectangle(6, -2, 3, 9, p.primary).setStrokeStyle(0.7, p.primaryDark, 1).setRotation(-0.6);
  const handR = scene.add.circle(10, -7, 1.9, p.primary).setStrokeStyle(0.6, p.primaryDark, 1);

  // ---- MASSIVE ANCHOR (raised, dripping water) ----
  const anchorShaft = scene.add.rectangle(4, -10, 1.8, 22, 0x4a4a5a).setStrokeStyle(0.7, 0x000, 1).setRotation(-0.25);
  const anchorRing = scene.add.circle(7, -19, 2.4, 0x4a4a5a).setStrokeStyle(0.8, 0x000, 1);
  const anchorRingHole = scene.add.circle(7, -19, 1.2, p.primary, 0.6);
  const anchorCross = scene.add.rectangle(2, -3, 12, 1.8, 0x4a4a5a).setStrokeStyle(0.7, 0x000, 1).setRotation(-0.25);
  // Two flukes (curved hook tips at base)
  const flukeL = scene.add.polygon(-4, -2, [0, 0, -3, 4, -1, 6, 2, 3], 0x4a4a5a).setStrokeStyle(0.6, 0x000, 1);
  const flukeR = scene.add.polygon(8, -4, [0, 0, 3, 4, 1, 6, -2, 3], 0x4a4a5a).setStrokeStyle(0.6, 0x000, 1);
  // Glow rune on anchor neck
  const anchorRune = scene.add.circle(4, -10, 2.0, p.energy, 0.9).setStrokeStyle(0.5, 0xffffff, 1);
  scene.tweens.add({ targets: anchorRune, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 520 });
  // Water drip
  const drip1 = scene.add.ellipse(-2, 2, 1, 2.4, p.energy, 0.85);
  const drip2 = scene.add.ellipse(7, 0, 1, 2.4, p.energy, 0.85);
  scene.tweens.add({ targets: [drip1, drip2], y: "+=4", alpha: 0, yoyo: true, repeat: -1, duration: 700 });

  // ---- HEAD — fishlike, mandibles, low-set on shoulders ----
  const head = scene.add.ellipse(0, -10, 11, 9, p.primary).setStrokeStyle(1, p.primaryDark, 1);
  // Mandibles (left + right) curving out from mouth
  const mandibleL = scene.add.triangle(-4, -8, 0, 0, -3, 5, -1, 5, p.primaryDark).setStrokeStyle(0.5, 0x000, 1);
  const mandibleR = scene.add.triangle(4, -8, 0, 0, 3, 5, 1, 5, p.primaryDark).setStrokeStyle(0.5, 0x000, 1);
  // Mouth slit
  const mouth = scene.add.rectangle(0, -8.5, 5, 1, 0x000);
  // Sharp teeth
  const tooth1 = scene.add.triangle(-1.5, -7.8, 0, 0, -0.5, 1, 0.5, 1, 0xffffff);
  const tooth2 = scene.add.triangle(1.5, -7.8, 0, 0, -0.5, 1, 0.5, 1, 0xffffff);
  // Glowing yellow eyes (Tidehunter signature)
  const eyeGlowL = scene.add.circle(-3, -11, 2.0, 0xffd84a, 0.9);
  const eyeGlowR = scene.add.circle(3, -11, 2.0, 0xffd84a, 0.9);
  const eyeL = scene.add.circle(-3, -11, 1.0, 0x000);
  const eyeR = scene.add.circle(3, -11, 1.0, 0x000);
  scene.tweens.add({ targets: [eyeGlowL, eyeGlowR], alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 460 });
  // Crest fin atop the head
  const finBack = scene.add.triangle(0, -16, 0, 0, -4, 5, 4, 5, p.primaryDark);
  const finMid = scene.add.triangle(-3, -16, 0, 0, -2, 4, 1, 4, p.primary);
  const finFront = scene.add.triangle(3, -16, 0, 0, 1, 4, 2, 4, p.primary);

  c.add([
    legL, legR, footL, footR,
    torso, bellyHi, scale1, scale2,
    spikeL1, spikeL2, spikeR1, spikeR2,
    armL, handL,
    anchorShaft, anchorCross, flukeL, flukeR, anchorRing, anchorRingHole, anchorRune,
    armR, handR,
    drip1, drip2,
    finBack, finMid, finFront,
    head, mandibleL, mandibleR, mouth, tooth1, tooth2,
    eyeGlowL, eyeGlowR, eyeL, eyeR,
  ]);

  const castAnim = () => {
    // Anchor smash — swing down
    scene.tweens.add({
      targets: [anchorShaft, anchorCross, flukeL, flukeR, anchorRing, anchorRingHole, anchorRune, armR, handR],
      angle: 18,
      duration: 110,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({ targets: anchorRune, scale: { from: 2, to: 1 }, alpha: { from: 1, to: 0.6 }, duration: 260 });
    scene.tweens.add({ targets: [head, mandibleL, mandibleR, eyeGlowL, eyeGlowR, eyeL, eyeR, finBack, finMid, finFront], y: "+=1.5", duration: 100, yoyo: true });
  };

  return { castPoint: { x: 7, y: -19 }, orb: anchorRune, castAnim };
}

// =====================================================================
// SLARDAR — Slithereen Guard. Armored fish-warrior with blue-green
// scales, glowing forehead crystal (Amplify Damage), spiked pauldrons,
// fin crests, two-handed mace. Reference: Dota 2 Slardar.
// =====================================================================
function buildSlardar(scene: Phaser.Scene, c: Phaser.GameObjects.Container, _p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // Slardar palette (deep-sea fish-warrior).
  const SCALE = 0x2a6a78;        // teal-blue scales
  const SCALE_DARK = 0x0a2a3a;
  const SCALE_HI = 0x4ea8b8;
  const ARMOR = 0x6a8a90;        // bronzed sea-iron armor
  const ARMOR_DARK = 0x2a3a4a;
  const CRYSTAL = 0xa6e3ff;      // forehead crystal glow
  const FIN = 0x88ccdd;
  const TOOTH = 0xeae4d4;

  // ---- TAIL FIN behind body (signature fish trail) ----
  const tailBase = scene.add.ellipse(0, 14, 5, 4, SCALE).setStrokeStyle(0.6, SCALE_DARK, 1);
  const tailFin = scene.add.polygon(0, 18, [
    0, -2,
    -5, 4,
    -2, 5,
     0, 3,
     2, 5,
     5, 4,
  ], FIN).setStrokeStyle(0.6, SCALE_DARK, 1);
  scene.tweens.add({ targets: tailFin, angle: { from: -8, to: 8 }, yoyo: true, repeat: -1, duration: 900, ease: "Sine.easeInOut" });

  // ---- LEGS — scaled, finned ankles ----
  const legL = scene.add.rectangle(-3.5, 14, 4, 9, SCALE).setStrokeStyle(0.7, SCALE_DARK, 1);
  const legR = scene.add.rectangle(3.5, 14, 4, 9, SCALE).setStrokeStyle(0.7, SCALE_DARK, 1);
  // Ankle fins
  const ankleFinL = scene.add.triangle(-5, 18, 0, 0, -3, 3, 0, 3, FIN, 0.95).setStrokeStyle(0.4, SCALE_DARK, 1);
  const ankleFinR = scene.add.triangle(5, 18, 0, 0, 3, 3, 0, 3, FIN, 0.95).setStrokeStyle(0.4, SCALE_DARK, 1);
  // Webbed feet
  const footL = scene.add.polygon(-3.5, 20, [-3, 0, 3, 0, 2, 2, -2, 2], FIN).setStrokeStyle(0.5, SCALE_DARK, 1);
  const footR = scene.add.polygon(3.5, 20, [-3, 0, 3, 0, 2, 2, -2, 2], FIN).setStrokeStyle(0.5, SCALE_DARK, 1);
  // Toe-claws
  const toeL1 = scene.add.triangle(-5.5, 21.5, 0, 0, -0.6, 1.2, 0.6, 1.2, TOOTH);
  const toeL2 = scene.add.triangle(-3.5, 21.5, 0, 0, -0.6, 1.2, 0.6, 1.2, TOOTH);
  const toeL3 = scene.add.triangle(-1.5, 21.5, 0, 0, -0.6, 1.2, 0.6, 1.2, TOOTH);
  const toeR1 = scene.add.triangle(1.5, 21.5, 0, 0, -0.6, 1.2, 0.6, 1.2, TOOTH);
  const toeR2 = scene.add.triangle(3.5, 21.5, 0, 0, -0.6, 1.2, 0.6, 1.2, TOOTH);
  const toeR3 = scene.add.triangle(5.5, 21.5, 0, 0, -0.6, 1.2, 0.6, 1.2, TOOTH);

  // ---- TORSO — scaled blue body with breastplate ----
  const torso = scene.add.ellipse(0, 4, 15, 14, SCALE).setStrokeStyle(1, SCALE_DARK, 1);
  // Pale underbelly
  const belly = scene.add.ellipse(0, 7, 8, 7, SCALE_HI, 0.85);
  // Bronze breastplate
  const plate = scene.add.polygon(0, 3, [
    -7, -4,
     7, -4,
     8, 3,
     5, 7,
    -5, 7,
    -8, 3,
  ], ARMOR).setStrokeStyle(0.8, ARMOR_DARK, 1);
  // Engraved chest emblem (sea-rune circle)
  const emblem = scene.add.circle(0, 3, 2, CRYSTAL, 0.95).setStrokeStyle(0.5, ARMOR_DARK, 1);
  const emblemDot = scene.add.circle(0, 3, 0.8, 0xffffff, 1);
  scene.tweens.add({ targets: [emblem, emblemDot], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 850 });
  // Belt
  const belt = scene.add.rectangle(0, 9, 14, 1.6, ARMOR_DARK).setStrokeStyle(0.4, 0x000, 1);
  const buckle = scene.add.rectangle(0, 9, 2.2, 2, ARMOR).setStrokeStyle(0.3, ARMOR_DARK, 1);
  // Scale dots scattered on body
  const scaleDot1 = scene.add.circle(-4, -1, 0.4, SCALE_HI);
  const scaleDot2 = scene.add.circle(4, -1, 0.4, SCALE_HI);
  const scaleDot3 = scene.add.circle(-3, 6, 0.4, SCALE_HI);
  const scaleDot4 = scene.add.circle(3, 6, 0.4, SCALE_HI);

  // ---- DORSAL FIN ridge running up back of head ----
  const dorsal = scene.add.polygon(0, -8, [
    0, -10,
    -1, -6,
    -2, -2,
    -1, 1,
     1, 1,
     2, -2,
     1, -6,
  ], FIN).setStrokeStyle(0.5, SCALE_DARK, 1);

  // ---- SPIKED PAULDRONS (shoulder armor with spikes) ----
  const pauldL = scene.add.ellipse(-9, -3, 5, 4, ARMOR).setStrokeStyle(0.7, ARMOR_DARK, 1);
  const pauldR = scene.add.ellipse(9, -3, 5, 4, ARMOR).setStrokeStyle(0.7, ARMOR_DARK, 1);
  // Pauldron spikes
  const spikeL1 = scene.add.triangle(-11, -5, 0, 0, -1, -3, 1, -3, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const spikeL2 = scene.add.triangle(-9, -6, 0, 0, -1, -3, 1, -3, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const spikeR1 = scene.add.triangle(11, -5, 0, 0, -1, -3, 1, -3, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const spikeR2 = scene.add.triangle(9, -6, 0, 0, -1, -3, 1, -3, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);

  // ---- ARMS — both gripping the two-handed mace ----
  const armR = scene.add.rectangle(6, 0, 3.5, 9, SCALE).setStrokeStyle(0.7, SCALE_DARK, 1).setRotation(-0.5);
  const wristR = scene.add.rectangle(9, -5, 3, 1.6, ARMOR).setStrokeStyle(0.4, ARMOR_DARK, 1).setRotation(-0.5);
  const handR = scene.add.circle(10, -6, 1.6, SCALE).setStrokeStyle(0.5, SCALE_DARK, 1);
  const armL = scene.add.rectangle(-5, -1, 3.5, 9, SCALE).setStrokeStyle(0.7, SCALE_DARK, 1).setRotation(0.5);
  const wristL = scene.add.rectangle(-8, -5, 3, 1.6, ARMOR).setStrokeStyle(0.4, ARMOR_DARK, 1).setRotation(0.5);
  const handL = scene.add.circle(-9, -6, 1.6, SCALE).setStrokeStyle(0.5, SCALE_DARK, 1);

  // ---- TWO-HANDED MACE — heavy spiked club above head ----
  const haft = scene.add.rectangle(0, -10, 1.6, 14, ARMOR_DARK).setStrokeStyle(0.4, 0x000, 1);
  const haftWrap = scene.add.rectangle(0, -8, 2, 1.4, 0x4a3018).setStrokeStyle(0.3, 0x000, 1);
  // Mace head — chunky bronze ball with spikes
  const maceHead = scene.add.circle(0, -18, 4.5, ARMOR).setStrokeStyle(0.7, ARMOR_DARK, 1);
  const maceCenter = scene.add.circle(0, -18, 1.5, CRYSTAL, 0.9);
  scene.tweens.add({ targets: maceCenter, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 700 });
  // Mace spikes
  const mSp1 = scene.add.triangle(0, -22.5, 0, 0, -1.2, 2.5, 1.2, 2.5, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const mSp2 = scene.add.triangle(-4.2, -18, 0, 0, -2.5, 1.2, -2.5, -1.2, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const mSp3 = scene.add.triangle(4.2, -18, 0, 0, 2.5, 1.2, 2.5, -1.2, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const mSp4 = scene.add.triangle(0, -13.5, 0, 0, -1.2, -2.5, 1.2, -2.5, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const mSp5 = scene.add.triangle(-3, -21, 0, 0, -1.6, 1, -1, -1.6, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);
  const mSp6 = scene.add.triangle(3, -21, 0, 0, 1.6, 1, 1, -1.6, TOOTH).setStrokeStyle(0.3, ARMOR_DARK, 1);

  // ---- HEAD — armored fish-warrior with crystal forehead ----
  const head = scene.add.ellipse(0, -10, 10, 10, SCALE).setStrokeStyle(0.9, SCALE_DARK, 1);
  // Lower jaw bulge with teeth
  const jaw = scene.add.ellipse(0, -7, 8, 4, SCALE_HI, 0.95).setStrokeStyle(0.5, SCALE_DARK, 1);
  // Sharp teeth
  const tooth1 = scene.add.triangle(-2.5, -6, 0, 0, -0.5, 1.5, 0.5, 1.5, TOOTH);
  const tooth2 = scene.add.triangle(-1, -5.7, 0, 0, -0.5, 1.5, 0.5, 1.5, TOOTH);
  const tooth3 = scene.add.triangle(1, -5.7, 0, 0, -0.5, 1.5, 0.5, 1.5, TOOTH);
  const tooth4 = scene.add.triangle(2.5, -6, 0, 0, -0.5, 1.5, 0.5, 1.5, TOOTH);
  // Cheek gill slashes
  const gillL = scene.add.line(0, 0, -4, -10, -4, -8, SCALE_DARK, 1).setLineWidth(0.5).setOrigin(0, 0);
  const gillR = scene.add.line(0, 0, 4, -10, 4, -8, SCALE_DARK, 1).setLineWidth(0.5).setOrigin(0, 0);
  // Head fins on each side (frill)
  const finL = scene.add.triangle(-5, -10, 0, 0, -4, -2, -4, 3, FIN, 0.95).setStrokeStyle(0.4, SCALE_DARK, 1);
  const finR = scene.add.triangle(5, -10, 0, 0, 4, -2, 4, 3, FIN, 0.95).setStrokeStyle(0.4, SCALE_DARK, 1);
  // Glowing yellow fish eyes
  const eyeL = scene.add.circle(-2.5, -11, 1.6, 0xfff8a8).setStrokeStyle(0.5, SCALE_DARK, 1);
  const eyeR = scene.add.circle(2.5, -11, 1.6, 0xfff8a8).setStrokeStyle(0.5, SCALE_DARK, 1);
  const pupilL = scene.add.circle(-2.5, -11, 0.6, 0x000);
  const pupilR = scene.add.circle(2.5, -11, 0.6, 0x000);
  // Crystal forehead gem (Amplify Damage signature)
  const crystalAura = scene.add.circle(0, -15, 5.5, CRYSTAL, 0.45);
  scene.tweens.add({ targets: crystalAura, alpha: { from: 0.3, to: 0.6 }, scale: { from: 0.9, to: 1.25 }, yoyo: true, repeat: -1, duration: 600 });
  const crystalGem = scene.add.polygon(0, -15, [
    0, -2.6,
    1.6, -1,
    1.4, 1.5,
    0, 2.4,
    -1.4, 1.5,
    -1.6, -1,
  ], CRYSTAL).setStrokeStyle(0.5, 0xffffff, 1);
  const crystalShine = scene.add.line(0, 0, -0.6, -16, 0.6, -14, 0xffffff, 1).setLineWidth(0.4).setOrigin(0, 0);
  scene.tweens.add({ targets: crystalGem, scale: { from: 0.95, to: 1.1 }, yoyo: true, repeat: -1, duration: 900 });
  // Eye glow pulse
  scene.tweens.add({ targets: [eyeL, eyeR], alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 800 });

  c.add([
    // Tail behind
    tailBase, tailFin,
    // Legs
    legL, legR, ankleFinL, ankleFinR, footL, footR,
    toeL1, toeL2, toeL3, toeR1, toeR2, toeR3,
    // Torso
    torso, belly, plate, emblem, emblemDot, belt, buckle,
    scaleDot1, scaleDot2, scaleDot3, scaleDot4,
    dorsal,
    // Arms
    armL, wristL, handL, armR, wristR, handR,
    // Pauldrons over arms
    pauldL, spikeL1, spikeL2, pauldR, spikeR1, spikeR2,
    // Mace
    haft, haftWrap, maceHead, maceCenter, mSp1, mSp2, mSp3, mSp4, mSp5, mSp6,
    // Head
    head, jaw, tooth1, tooth2, tooth3, tooth4, gillL, gillR, finL, finR,
    eyeL, eyeR, pupilL, pupilR,
    crystalAura, crystalGem, crystalShine,
  ]);

  const castAnim = () => {
    // Mace slam: angle slightly forward and crystal flash.
    scene.tweens.add({
      targets: [haft, haftWrap, maceHead, maceCenter, mSp1, mSp2, mSp3, mSp4, mSp5, mSp6],
      angle: 18,
      y: "+=1",
      duration: 130,
      yoyo: true,
      ease: "Sine.easeInOut",
    });
    scene.tweens.add({ targets: [crystalGem, crystalAura, emblem, emblemDot], scale: { from: 1.6, to: 1 }, duration: 260, ease: "Quad.easeOut" });
    // Body lean
    scene.tweens.add({ targets: [head, finL, finR, dorsal, eyeL, eyeR, pupilL, pupilR, crystalGem, crystalShine, crystalAura], x: 1, duration: 90, yoyo: true });
  };

  return { castPoint: { x: 0, y: -15 }, orb: crystalGem, castAnim };
}

// =====================================================================
// NECROLYTE (Rotund'jere) — DEATH MECH: iron robot priest, emerald reactor
// core, mechanical scythe arm, twin emerald optics, tank treads.
// =====================================================================
function buildNecrolyte(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  const IRON    = 0x3a3a44;
  const IRON_DK = 0x14141c;
  const IRON_HI = 0x6a6a78;
  const GOLD    = 0xffd84a;
  const ENERGY  = p.energy;       // emerald
  const ENERGY_HI = 0xffffff;

  const shadow = scene.add.ellipse(0, 19, 32, 8, 0x000000, 0.6);
  // Ground aura — emerald reactor leak.
  const groundGlow = scene.add.ellipse(0, 18, 22, 5, ENERGY, 0.55);
  scene.tweens.add({ targets: groundGlow, scaleX: { from: 0.85, to: 1.25 }, alpha: { from: 0.35, to: 0.7 }, yoyo: true, repeat: -1, duration: 900 });

  // ---- TANK TREADS ----
  const treadL = scene.add.rectangle(-9, 15, 11, 6, IRON_DK).setStrokeStyle(1.2, 0x000, 1);
  const treadR = scene.add.rectangle(9,  15, 11, 6, IRON_DK).setStrokeStyle(1.2, 0x000, 1);
  const tdL1 = scene.add.rectangle(-12, 15, 0.9, 5, IRON_HI, 0.7);
  const tdL2 = scene.add.rectangle(-9,  15, 0.9, 5, IRON_HI, 0.7);
  const tdL3 = scene.add.rectangle(-6,  15, 0.9, 5, IRON_HI, 0.7);
  const tdR1 = scene.add.rectangle(6,   15, 0.9, 5, IRON_HI, 0.7);
  const tdR2 = scene.add.rectangle(9,   15, 0.9, 5, IRON_HI, 0.7);
  const tdR3 = scene.add.rectangle(12,  15, 0.9, 5, IRON_HI, 0.7);

  // ---- TORSO ROBE-PLATE — iron with emerald rune seam ----
  const robeOuter = scene.add.polygon(0, 6, [-9, -10, 9, -10, 12, 13, 6, 16, 0, 14, -6, 16, -12, 13], IRON).setStrokeStyle(1, IRON_DK, 1);
  // Gold trim hem.
  const hemMid = scene.add.rectangle(0, 14, 14, 1.2, GOLD);
  const hemL = scene.add.polygon(-7, 13, [-3, 0, 3, 0, 2, 2, -2, 2], GOLD);
  const hemR = scene.add.polygon(7, 13, [-3, 0, 3, 0, 2, 2, -2, 2], GOLD);
  // Reactor seam.
  const robeRune = scene.add.line(0, 0, 0, -8, 0, 12, ENERGY, 0.95).setLineWidth(1).setOrigin(0, 0);
  scene.tweens.add({ targets: robeRune, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 500 });
  // Side rivets on plate.
  const rv1 = scene.add.circle(-6, -3, 0.9, IRON_HI).setStrokeStyle(0.4, IRON_DK, 1);
  const rv2 = scene.add.circle(6, -3, 0.9, IRON_HI).setStrokeStyle(0.4, IRON_DK, 1);
  const rv3 = scene.add.circle(-7, 6, 0.9, IRON_HI).setStrokeStyle(0.4, IRON_DK, 1);
  const rv4 = scene.add.circle(7, 6, 0.9, IRON_HI).setStrokeStyle(0.4, IRON_DK, 1);

  // ---- REACTOR CORE on chest ----
  const coreRing = scene.add.circle(0, -1, 3.4, IRON_DK).setStrokeStyle(1, GOLD, 1);
  const coreGlow = scene.add.circle(0, -1, 2.6, ENERGY, 0.85);
  const coreInner = scene.add.circle(0, -1, 1.4, ENERGY_HI, 1);
  scene.tweens.add({ targets: [coreGlow, coreInner], alpha: { from: 0.6, to: 1 }, scale: { from: 0.9, to: 1.15 }, yoyo: true, repeat: -1, duration: 520 });

  // ---- HOOD-PLATE HELMET ----
  const hoodOuter = scene.add.polygon(0, -10, [-7, 5, -6, -6, 0, -12, 6, -6, 7, 5], IRON_DK).setStrokeStyle(1, 0x000, 1);
  const hoodEdge = scene.add.polygon(0, -10, [-6, 4, -5, -5, 0, -10, 5, -5, 6, 4], IRON);
  const hoodGold1 = scene.add.line(0, 0, -6, -4, 0, -10, GOLD, 1).setLineWidth(0.7).setOrigin(0, 0);
  const hoodGold2 = scene.add.line(0, 0, 0, -10, 6, -4, GOLD, 1).setLineWidth(0.7).setOrigin(0, 0);
  const hoodTip = scene.add.circle(0, -13, 1.1, GOLD).setStrokeStyle(0.4, IRON_DK, 1);
  // Antenna with green pulse.
  const antenna = scene.add.rectangle(0, -16, 0.8, 4, IRON_HI);
  const antennaTip = scene.add.circle(0, -19, 1.1, ENERGY);
  scene.tweens.add({ targets: antennaTip, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 480 });

  // ---- SKULL FACEPLATE — robotic skull ----
  const face = scene.add.circle(0, -10, 4.4, 0xc8c8d0).setStrokeStyle(0.7, IRON_DK, 1);
  const visor = scene.add.rectangle(0, -10.6, 6, 1.6, IRON_DK);
  const eyeL = scene.add.circle(-1.7, -10.6, 0.95, ENERGY, 1);
  const eyeR = scene.add.circle(1.7, -10.6, 0.95, ENERGY, 1);
  const eyeFlareL = scene.add.circle(-1.7, -10.6, 1.6, ENERGY, 0.45);
  const eyeFlareR = scene.add.circle(1.7, -10.6, 1.6, ENERGY, 0.45);
  scene.tweens.add({ targets: [eyeL, eyeR, eyeFlareL, eyeFlareR], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 480 });
  // Mech jaw vents.
  const jawL = scene.add.rectangle(-1.4, -7.7, 0.7, 1.4, IRON_DK);
  const jawM = scene.add.rectangle(0, -7.7, 0.7, 1.4, IRON_DK);
  const jawR = scene.add.rectangle(1.4, -7.7, 0.7, 1.4, IRON_DK);
  const teethBar = scene.add.rectangle(0, -7.0, 5, 0.6, IRON_HI);

  // ---- SHOULDER PAULDRONS — gold-trimmed iron ----
  const pauldL = scene.add.ellipse(-7, -5, 5, 3.4, IRON).setStrokeStyle(0.7, GOLD, 1);
  const pauldR = scene.add.ellipse(7, -5, 5, 3.4, IRON).setStrokeStyle(0.7, GOLD, 1);
  const pauldGemL = scene.add.circle(-7, -5, 0.9, ENERGY, 1);
  const pauldGemR = scene.add.circle(7, -5, 0.9, ENERGY, 1);
  scene.tweens.add({ targets: [pauldGemL, pauldGemR], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 700 });

  // ---- LEFT MECHANICAL ARM — segmented piston, holds scythe ----
  const armUpper = scene.add.rectangle(-8, -2, 2.4, 6, IRON).setStrokeStyle(0.5, IRON_DK, 1);
  const armPiston = scene.add.rectangle(-8, 1, 1.4, 5, IRON_HI);
  const elbow = scene.add.circle(-8, 4, 1.6, IRON_DK).setStrokeStyle(0.5, IRON_HI, 1);
  const armLower = scene.add.rectangle(-9, 7, 2.0, 5, IRON).setStrokeStyle(0.5, IRON_DK, 1);
  const wristCuff = scene.add.rectangle(-9, 9.5, 3, 1.2, GOLD);

  // ---- ROBOTIC SCYTHE ----
  const scytheHaft = scene.add.rectangle(-10, -2, 1.8, 26, 0x1a1018).setStrokeStyle(0.6, 0x000, 1);
  scytheHaft.setRotation(-0.18);
  const haftBand1 = scene.add.rectangle(-8.5, -10, 2.6, 0.9, GOLD).setRotation(-0.18);
  const haftBand2 = scene.add.rectangle(-9, -3, 2.6, 0.9, GOLD).setRotation(-0.18);
  const haftBand3 = scene.add.rectangle(-9.5, 4, 2.6, 0.9, GOLD).setRotation(-0.18);
  const pommel = scene.add.circle(-11, 10, 1.6, IRON_HI).setStrokeStyle(0.5, 0x000, 1);
  const pommelGem = scene.add.circle(-11, 10, 0.5, ENERGY);
  // Curved blade with energy edge.
  const bladeBack = scene.add.polygon(-12, -14, [0, 0, 8, -4, 11, -10, 5, -12, -3, -7], IRON_DK).setStrokeStyle(0.7, 0x000, 1);
  const blade = scene.add.polygon(-11, -14, [0, 0, 7, -3, 10, -9, 4, -11, -2, -6], 0xc8c8d0).setStrokeStyle(0.7, IRON_DK, 1);
  const bladeEdge = scene.add.line(0, 0, -11, -14, -1, -25, ENERGY, 1).setLineWidth(1.6).setOrigin(0, 0);
  const bladeGlow = scene.add.line(0, 0, -10, -13, 0, -23, ENERGY_HI, 0.7).setLineWidth(0.7).setOrigin(0, 0);
  scene.tweens.add({ targets: [bladeEdge, bladeGlow], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 500 });

  // ---- AURA HALO above head ----
  const aura1 = scene.add.circle(0, -22, 7, ENERGY, 0.18);
  const aura2 = scene.add.circle(0, -22, 4.8, ENERGY, 0.45);
  scene.tweens.add({ targets: aura1, scale: { from: 0.85, to: 1.25 }, alpha: { from: 0.1, to: 0.3 }, yoyo: true, repeat: -1, duration: 1100 });
  scene.tweens.add({ targets: aura2, scale: { from: 0.9, to: 1.2 }, alpha: { from: 0.3, to: 0.65 }, yoyo: true, repeat: -1, duration: 720 });

  // Floating soul motes.
  const mote1 = scene.add.circle(-8, -16, 0.9, ENERGY, 1);
  const mote2 = scene.add.circle(8, -14, 0.7, ENERGY, 1);
  scene.tweens.add({ targets: mote1, x: -4, y: -22, yoyo: true, repeat: -1, duration: 1500, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: mote2, x: 4, y: -20, yoyo: true, repeat: -1, duration: 1300, ease: "Sine.easeInOut" });

  c.add([
    shadow, groundGlow,
    treadL, treadR, tdL1, tdL2, tdL3, tdR1, tdR2, tdR3,
    aura1, aura2,
    robeOuter, robeRune, hemMid, hemL, hemR,
    rv1, rv2, rv3, rv4,
    coreRing, coreGlow, coreInner,
    pauldL, pauldR, pauldGemL, pauldGemR,
    armUpper, armPiston, elbow, armLower, wristCuff,
    pommel, pommelGem, scytheHaft, haftBand1, haftBand2, haftBand3,
    bladeBack, blade, bladeEdge, bladeGlow,
    hoodOuter, hoodEdge, hoodGold1, hoodGold2, hoodTip,
    antenna, antennaTip,
    face, visor, eyeFlareL, eyeFlareR, eyeL, eyeR,
    jawL, jawM, jawR, teethBar,
    mote1, mote2,
  ]);

  const castAnim = () => {
    scene.tweens.add({ targets: [blade, bladeEdge, bladeGlow], scale: { from: 1, to: 1.3 }, yoyo: true, duration: 130 });
    scene.tweens.add({ targets: [aura1, aura2], scale: { from: 1, to: 1.7 }, alpha: { from: 0.7, to: 0 }, duration: 280, onComplete: () => { aura1.setAlpha(0.18).setScale(1); aura2.setAlpha(0.45).setScale(1); } });
    scene.tweens.add({ targets: [eyeL, eyeR, eyeFlareL, eyeFlareR], scale: { from: 1.6, to: 1 }, duration: 200 });
    scene.tweens.add({ targets: [coreGlow, coreInner], scale: { from: 1.5, to: 1 }, duration: 200 });
  };
  return { castPoint: { x: -10, y: -22 }, orb: blade, castAnim };
}

// =====================================================================
// PHANTOM ASSASSIN (Mortred) — STEALTH MECH: black/violet sleek robot,
// cape of fiber-optic ribbons, twin retractable mech blades.
// =====================================================================
function buildPhantomAssassin(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // STEALTH MECH — sleek black/violet robot with twin mechanical sword arms.
  const STEEL    = 0x1a141e;       // black-violet chassis
  const STEEL_HI = 0x4a3a5a;
  const VIOLET   = 0xd4a0ff;       // glowing seams + optic
  const GOLD     = 0xffd84a;
  const SILVER   = 0xc8c8d0;

  const shadowPool = scene.add.ellipse(0, 19, 28, 7, 0x000000, 0.65);
  const violetPool = scene.add.ellipse(0, 19, 22, 4, p.energy, 0.4);
  scene.tweens.add({ targets: violetPool, alpha: { from: 0.2, to: 0.55 }, scaleX: { from: 0.85, to: 1.2 }, yoyo: true, repeat: -1, duration: 900 });

  // ---- VIOLET AURA HALO behind body ----
  const auraBack = scene.add.ellipse(0, 4, 18, 22, p.energy, 0.18);
  scene.tweens.add({ targets: auraBack, alpha: { from: 0.1, to: 0.28 }, scaleX: { from: 0.9, to: 1.15 }, yoyo: true, repeat: -1, duration: 900 });

  // ---- TANK TREADS / STEALTH SKIDS ----
  const treadL = scene.add.rectangle(-9, 15, 11, 6, 0x000).setStrokeStyle(1.2, 0x000, 1);
  const treadR = scene.add.rectangle(9,  15, 11, 6, 0x000).setStrokeStyle(1.2, 0x000, 1);
  const tdL1 = scene.add.rectangle(-12, 15, 0.9, 5, STEEL_HI, 0.7);
  const tdL2 = scene.add.rectangle(-9,  15, 0.9, 5, STEEL_HI, 0.7);
  const tdL3 = scene.add.rectangle(-6,  15, 0.9, 5, STEEL_HI, 0.7);
  const tdR1 = scene.add.rectangle(6,   15, 0.9, 5, STEEL_HI, 0.7);
  const tdR2 = scene.add.rectangle(9,   15, 0.9, 5, STEEL_HI, 0.7);
  const tdR3 = scene.add.rectangle(12,  15, 0.9, 5, STEEL_HI, 0.7);

  // ---- FIBER-OPTIC CAPE behind body ----
  const cape = scene.add.polygon(0, 4, [-6, -8, 6, -8, 10, 14, 5, 16, 0, 14, -5, 16, -10, 14], STEEL).setStrokeStyle(0.7, 0x000, 1);
  const capeFiber1 = scene.add.line(0, 0, -8, 12, -2, -6, p.energy, 0.85).setLineWidth(0.5).setOrigin(0, 0);
  const capeFiber2 = scene.add.line(0, 0, 0, 14, 0, -7, p.energy, 0.85).setLineWidth(0.5).setOrigin(0, 0);
  const capeFiber3 = scene.add.line(0, 0, 8, 12, 2, -6, p.energy, 0.85).setLineWidth(0.5).setOrigin(0, 0);
  scene.tweens.add({ targets: [capeFiber1, capeFiber2, capeFiber3], alpha: { from: 0.4, to: 1 }, yoyo: true, repeat: -1, duration: 600 });

  // ---- SLEEK CHASSIS TORSO — narrow assassin profile ----
  const torso = scene.add.polygon(0, 0, [-5, -7, 5, -7, 6, 7, 4, 9, -4, 9, -6, 7], STEEL).setStrokeStyle(0.7, 0x000, 1);
  const torsoHi = scene.add.polygon(0, -5, [-3, -2, 3, -2, 3, 2, -3, 2], STEEL_HI, 0.6);
  // Glowing seams.
  const seamMid = scene.add.line(0, 0, 0, -7, 0, 9, p.energy, 1).setLineWidth(0.7).setOrigin(0, 0);
  const seamL = scene.add.line(0, 0, -3, -6, -3, 8, p.energy, 0.7).setLineWidth(0.4).setOrigin(0, 0);
  const seamR = scene.add.line(0, 0, 3, -6, 3, 8, p.energy, 0.7).setLineWidth(0.4).setOrigin(0, 0);
  scene.tweens.add({ targets: [seamMid, seamL, seamR], alpha: { from: 0.45, to: 1 }, yoyo: true, repeat: -1, duration: 540 });
  // Chest reactor — single violet gem in gold.
  const reactorRing = scene.add.circle(0, -2, 2.4, GOLD).setStrokeStyle(0.5, 0x6a4a18, 1);
  const reactorGem = scene.add.polygon(0, -2, [0, -1.6, 1.5, 0, 0, 1.6, -1.5, 0], p.energy);
  scene.tweens.add({ targets: reactorGem, alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 460 });
  // Gold belt with violet buckle.
  const belt = scene.add.rectangle(0, 7, 10, 1.4, GOLD);
  const buckle = scene.add.circle(0, 7, 0.9, p.energy);
  // Side rivets.
  const rv1 = scene.add.circle(-4, -5, 0.6, STEEL_HI, 1);
  const rv2 = scene.add.circle(4, -5, 0.6, STEEL_HI, 1);
  const rv3 = scene.add.circle(-4, 5, 0.6, STEEL_HI, 1);
  const rv4 = scene.add.circle(4, 5, 0.6, STEEL_HI, 1);

  // ---- SHOULDER PAULDRONS — pointed ----
  const pauldL = scene.add.polygon(-6, -5, [-3, 0, 0, -3, 3, 0, 2, 3, -2, 3], STEEL).setStrokeStyle(0.6, GOLD, 1);
  const pauldR = scene.add.polygon(6, -5, [-3, 0, 0, -3, 3, 0, 2, 3, -2, 3], STEEL).setStrokeStyle(0.6, GOLD, 1);
  const pauldGemL = scene.add.circle(-6, -5, 0.7, p.energy);
  const pauldGemR = scene.add.circle(6, -5, 0.7, p.energy);

  // ---- TWIN MECHANICAL BLADE-ARMS ----
  // Left arm raised, segmented with elbow piston, ending in long curved blade.
  const armUL = scene.add.rectangle(-7.5, 0, 2.0, 6, STEEL).setStrokeStyle(0.5, 0x000, 1);
  const elbowL = scene.add.circle(-7.5, 3, 1.4, STEEL_HI).setStrokeStyle(0.5, 0x000, 1);
  const armLL = scene.add.rectangle(-9, 6, 1.8, 6, STEEL).setStrokeStyle(0.5, 0x000, 1);
  const wristL = scene.add.rectangle(-9, 9, 2.4, 1.0, GOLD);
  // Long curved blade L.
  const bladeL = scene.add.polygon(-9, -1, [0, 0, -1.4, -12, 0, -16, 1.4, -12], SILVER).setStrokeStyle(0.7, 0x000, 1);
  bladeL.setRotation(-0.30);
  const bladeLEdge = scene.add.line(0, 0, -9, -1, -3.6, -16, p.energy, 1).setLineWidth(0.8).setOrigin(0, 0);
  const bladeLGuard = scene.add.rectangle(-9, 0, 4, 1.2, GOLD).setRotation(-0.30);

  // Right arm — same.
  const armUR = scene.add.rectangle(7.5, 0, 2.0, 6, STEEL).setStrokeStyle(0.5, 0x000, 1);
  const elbowR = scene.add.circle(7.5, 3, 1.4, STEEL_HI).setStrokeStyle(0.5, 0x000, 1);
  const armLR = scene.add.rectangle(9, 6, 1.8, 6, STEEL).setStrokeStyle(0.5, 0x000, 1);
  const wristR = scene.add.rectangle(9, 9, 2.4, 1.0, GOLD);
  const bladeR = scene.add.polygon(9, -1, [0, 0, -1.4, -12, 0, -16, 1.4, -12], SILVER).setStrokeStyle(0.7, 0x000, 1);
  bladeR.setRotation(0.30);
  const bladeREdge = scene.add.line(0, 0, 9, -1, 3.6, -16, p.energy, 1).setLineWidth(0.8).setOrigin(0, 0);
  const bladeRGuard = scene.add.rectangle(9, 0, 4, 1.2, GOLD).setRotation(0.30);
  scene.tweens.add({ targets: [bladeLEdge, bladeREdge], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 380 });

  // ---- HOODED HELMET — sleek black/violet ----
  const hood = scene.add.polygon(0, -10, [-6, 5, -6, -4, -3, -9, 0, -12, 3, -9, 6, -4, 6, 5], 0x000).setStrokeStyle(0.7, 0x000, 1);
  const hoodInner = scene.add.polygon(0, -10, [-5, 4, -5, -4, -3, -8, 0, -10, 3, -8, 5, -4, 5, 4], STEEL);
  const hoodEdge = scene.add.line(0, 0, -6, 5, 0, -12, GOLD, 0.95).setLineWidth(0.5).setOrigin(0, 0);
  const hoodEdge2 = scene.add.line(0, 0, 0, -12, 6, 5, GOLD, 0.95).setLineWidth(0.5).setOrigin(0, 0);
  // Hood spine ornament.
  const hoodSpine = scene.add.triangle(0, -16, 0, 0, -1.4, 4, 1.4, 4, GOLD).setStrokeStyle(0.3, 0x000, 1);
  // Visor — single horizontal violet slit (cyclops bar).
  const visorBack = scene.add.ellipse(0, -10, 8, 4.4, 0x0a040f).setStrokeStyle(0.7, GOLD, 1);
  const visorBar = scene.add.rectangle(0, -10, 7.5, 1.4, p.energy, 1);
  const visorCore = scene.add.rectangle(0, -10, 6.5, 0.5, VIOLET, 1);
  const visorPulse = scene.add.rectangle(0, -10, 8, 2, p.energy, 0.45);
  scene.tweens.add({ targets: [visorBar, visorCore, visorPulse], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 360 });
  // Mech jaw vents below visor.
  const ventL = scene.add.rectangle(-1.4, -7.5, 0.6, 1.2, STEEL_HI);
  const ventM = scene.add.rectangle(0, -7.5, 0.6, 1.2, STEEL_HI);
  const ventR = scene.add.rectangle(1.4, -7.5, 0.6, 1.2, STEEL_HI);

  // ---- FLOATING SHADOW DAGGER DRONES (3) ----
  const drone1 = scene.add.triangle(-12, -6, 0, 0, -0.9, 4, 0.9, 4, p.energy, 1);
  const drone2 = scene.add.triangle(12, -6, 0, 0, -0.9, 4, 0.9, 4, p.energy, 1);
  const drone3 = scene.add.triangle(0, -18, 0, 0, -0.9, 4, 0.9, 4, p.energy, 1);
  scene.tweens.add({ targets: drone1, x: -10, y: -10, yoyo: true, repeat: -1, duration: 1400, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: drone2, x: 10, y: -10, yoyo: true, repeat: -1, duration: 1200, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: drone3, alpha: { from: 0.5, to: 1 }, scale: { from: 0.7, to: 1.2 }, yoyo: true, repeat: -1, duration: 700 });

  c.add([
    shadowPool, violetPool,
    auraBack,
    treadL, treadR, tdL1, tdL2, tdL3, tdR1, tdR2, tdR3,
    cape, capeFiber1, capeFiber2, capeFiber3,
    torso, torsoHi, seamL, seamR, seamMid,
    rv1, rv2, rv3, rv4,
    reactorRing, reactorGem,
    belt, buckle,
    pauldL, pauldR, pauldGemL, pauldGemR,
    armUL, elbowL, armLL, wristL, bladeLGuard, bladeL, bladeLEdge,
    armUR, elbowR, armLR, wristR, bladeRGuard, bladeR, bladeREdge,
    hood, hoodInner, hoodEdge, hoodEdge2, hoodSpine,
    visorBack, visorPulse, visorBar, visorCore,
    ventL, ventM, ventR,
    drone1, drone2, drone3,
  ]);

  const castAnim = () => {
    scene.tweens.add({ targets: [bladeR, bladeRGuard, bladeREdge], rotation: { from: 0.30, to: 1.2 }, x: "+=3", yoyo: true, duration: 150 });
    scene.tweens.add({ targets: [bladeL, bladeLGuard, bladeLEdge], rotation: { from: -0.30, to: -1.2 }, x: "-=3", yoyo: true, duration: 150 });
    scene.tweens.add({ targets: [visorBar, visorCore, visorPulse], scale: { from: 1.4, to: 1 }, duration: 200 });
    scene.tweens.add({ targets: [drone1, drone2, drone3], scale: { from: 1.5, to: 1 }, alpha: { from: 1, to: 0.85 }, duration: 220 });
    scene.tweens.add({ targets: [reactorGem], scale: { from: 1.5, to: 1 }, duration: 200 });
  };
  return { castPoint: { x: 9, y: -16 }, orb: bladeR, castAnim };
}

// =====================================================================
// LUNA — MOON KNIGHT MECH: silver/gold robot with crescent shoulder cannons
// and a glaive-rifle (laser barrel that fires the moon-tornado beam).
// =====================================================================
function buildLuna(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  const SILVER   = 0xc8d8e8;
  const STEEL    = 0x4a5a78;
  const STEEL_DK = 0x1a2030;
  const GOLD     = 0xffd84a;
  const GOLD_DK  = 0x6a4a18;
  const SAPPHIRE = p.energy;       // sapphire/cyan accent

  const shadow = scene.add.ellipse(0, 19, 32, 8, 0x000000, 0.6);
  // ---- LARGE CRESCENT MOON HALO behind hero ----
  const moonAura = scene.add.circle(0, -20, 14, p.energy, 0.18);
  const moonMid = scene.add.circle(0, -20, 10, p.energy, 0.32);
  const moonInner = scene.add.circle(0, -20, 8, 0xffffff, 0.55);
  const moonMask = scene.add.circle(3, -20, 7.2, 0x0b1020, 1);
  scene.tweens.add({ targets: moonAura, alpha: { from: 0.1, to: 0.3 }, scale: { from: 0.9, to: 1.1 }, yoyo: true, repeat: -1, duration: 1500 });
  scene.tweens.add({ targets: moonMid, alpha: { from: 0.2, to: 0.5 }, yoyo: true, repeat: -1, duration: 1100 });
  scene.tweens.add({ targets: moonInner, alpha: { from: 0.4, to: 0.75 }, yoyo: true, repeat: -1, duration: 800 });

  // ---- STARLIGHT MOTES around halo ----
  const star1 = scene.add.circle(-8, -22, 0.7, 0xffffff, 1);
  const star2 = scene.add.circle(7, -25, 0.5, 0xffffff, 1);
  const star3 = scene.add.circle(-6, -28, 0.6, p.trim, 1);
  const star4 = scene.add.circle(8, -16, 0.5, p.trim, 1);
  scene.tweens.add({ targets: star1, alpha: { from: 0.4, to: 1 }, scale: { from: 0.6, to: 1.4 }, yoyo: true, repeat: -1, duration: 700 });
  scene.tweens.add({ targets: star2, alpha: { from: 0.3, to: 1 }, scale: { from: 0.7, to: 1.5 }, yoyo: true, repeat: -1, duration: 900 });
  scene.tweens.add({ targets: star3, alpha: { from: 0.5, to: 1 }, scale: { from: 0.6, to: 1.3 }, yoyo: true, repeat: -1, duration: 1100 });
  scene.tweens.add({ targets: star4, alpha: { from: 0.4, to: 1 }, scale: { from: 0.7, to: 1.3 }, yoyo: true, repeat: -1, duration: 850 });

  // ---- TANK TREADS ----
  const treadL = scene.add.rectangle(-9, 15, 11, 6, STEEL_DK).setStrokeStyle(1.2, 0x000, 1);
  const treadR = scene.add.rectangle(9,  15, 11, 6, STEEL_DK).setStrokeStyle(1.2, 0x000, 1);
  const tdL1 = scene.add.rectangle(-12, 15, 0.9, 5, SILVER, 0.7);
  const tdL2 = scene.add.rectangle(-9,  15, 0.9, 5, SILVER, 0.7);
  const tdL3 = scene.add.rectangle(-6,  15, 0.9, 5, SILVER, 0.7);
  const tdR1 = scene.add.rectangle(6,   15, 0.9, 5, SILVER, 0.7);
  const tdR2 = scene.add.rectangle(9,   15, 0.9, 5, SILVER, 0.7);
  const tdR3 = scene.add.rectangle(12,  15, 0.9, 5, SILVER, 0.7);

  // ---- CHASSIS / SKIRT — silver-steel with gold trim ----
  const skirtOuter = scene.add.polygon(0, 8, [-9, -3, 9, -3, 11, 9, -11, 9], STEEL).setStrokeStyle(0.7, STEEL_DK, 1);
  const skirt = scene.add.polygon(0, 8, [-7, -3, 7, -3, 9, 8, -9, 8], SILVER).setStrokeStyle(0.7, STEEL, 1);
  const skirtTrim = scene.add.rectangle(0, 8, 18, 1.2, GOLD);
  const skirtRiv1 = scene.add.circle(-6, 6, 0.6, GOLD, 1);
  const skirtRiv2 = scene.add.circle(6, 6, 0.6, GOLD, 1);
  // Center crescent emblem on chassis.
  const skirtEmblemBg = scene.add.circle(0, 5, 2.4, STEEL_DK);
  const skirtEmblem = scene.add.polygon(0, 5, [-1.4, 0, -1, -1.4, 0.4, -1.4, 0, 0, 0.4, 1.4, -1, 1.4], GOLD);

  // ---- CUIRASS / CHESTPLATE — silver with gold trim ----
  const torso = scene.add.ellipse(0, -1, 11, 12, SILVER).setStrokeStyle(0.8, STEEL, 1);
  const torsoMid = scene.add.line(0, 0, 0, -6, 0, 4, GOLD, 1).setLineWidth(0.7).setOrigin(0, 0);
  const torsoTrim = scene.add.line(0, 0, -5, -1, 5, -1, GOLD, 1).setLineWidth(0.6).setOrigin(0, 0);
  // Big sapphire chest gem (reactor) in gold setting.
  const chestGemSet = scene.add.circle(0, -1, 2.8, GOLD).setStrokeStyle(0.5, GOLD_DK, 1);
  const chestGem = scene.add.polygon(0, -1, [0, -2, 1.8, 0, 0, 2, -1.8, 0], SAPPHIRE).setStrokeStyle(0.4, 0xffffff, 1);
  scene.tweens.add({ targets: chestGem, alpha: { from: 0.7, to: 1 }, yoyo: true, repeat: -1, duration: 600 });
  // Side rivets.
  const rv1 = scene.add.circle(-4, -3, 0.6, STEEL_DK, 1);
  const rv2 = scene.add.circle(4, -3, 0.6, STEEL_DK, 1);
  const rv3 = scene.add.circle(-4, 3, 0.6, STEEL_DK, 1);
  const rv4 = scene.add.circle(4, 3, 0.6, STEEL_DK, 1);

  // ---- CRESCENT SHOULDER CANNON-PAULDRONS ----
  const pauldL = scene.add.ellipse(-7, -5, 5.6, 4, STEEL).setStrokeStyle(0.7, GOLD, 1);
  const pauldR = scene.add.ellipse(7, -5, 5.6, 4, STEEL).setStrokeStyle(0.7, GOLD, 1);
  // Crescent caps protruding outward (cannons).
  const cresL = scene.add.polygon(-9, -6, [0, 0, -2, -1.6, -2, -3, 0, -3.5, 0.4, -1.6, 0, 0], GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
  const cresR = scene.add.polygon(9, -6, [0, 0, 2, -1.6, 2, -3, 0, -3.5, -0.4, -1.6, 0, 0], GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
  const cresGemL = scene.add.circle(-9, -7, 0.7, SAPPHIRE);
  const cresGemR = scene.add.circle(9, -7, 0.7, SAPPHIRE);
  scene.tweens.add({ targets: [cresGemL, cresGemR], alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 600 });

  // ---- LEFT MECH ARM (rest pose) ----
  const armUL = scene.add.rectangle(-6, 0, 2.2, 6, STEEL).setStrokeStyle(0.5, 0x000, 1);
  const elbowL = scene.add.circle(-6, 3, 1.4, STEEL_DK).setStrokeStyle(0.5, SILVER, 1);
  const armLL = scene.add.rectangle(-6, 6, 2.0, 5, STEEL).setStrokeStyle(0.5, 0x000, 1);
  const wristL = scene.add.rectangle(-6, 9, 2.6, 1.2, GOLD);

  // ---- RIGHT ARM HOLDS THE GLAIVE-RIFLE (cast point) ----
  const armUR = scene.add.rectangle(7, -1, 2.4, 6, STEEL).setStrokeStyle(0.5, 0x000, 1).setRotation(-0.5);
  const elbowR = scene.add.circle(8, 1, 1.4, STEEL_DK).setStrokeStyle(0.5, SILVER, 1);
  const wristR = scene.add.rectangle(9, -2, 2.4, 1.2, GOLD).setRotation(-0.5);

  // ---- GLAIVE-RIFLE (laser cannon barrel + crescent blade head) ----
  // Body of the rifle (gold/steel).
  const rifleBody = scene.add.rectangle(11, -3, 8, 2.4, STEEL).setStrokeStyle(0.6, STEEL_DK, 1).setRotation(-0.4);
  const rifleTrim = scene.add.rectangle(11, -3.6, 8, 0.8, GOLD).setRotation(-0.4);
  // Long barrel forward.
  const barrel = scene.add.rectangle(15, -6, 8, 1.8, SILVER).setStrokeStyle(0.5, STEEL_DK, 1).setRotation(-0.4);
  const barrelRing1 = scene.add.rectangle(13, -5, 1.4, 2.6, GOLD).setRotation(-0.4);
  const barrelRing2 = scene.add.rectangle(17, -7, 1.4, 2.6, GOLD).setRotation(-0.4);
  // Crescent blade attached at the front of the rifle.
  const glaiveBladeBack = scene.add.polygon(20, -8, [0, 0, 5, -3, 8, -7, 4, -9, -2, -6], STEEL_DK).setStrokeStyle(0.7, 0x000, 1);
  const glaiveBlade = scene.add.polygon(19, -8, [0, 0, 4.5, -2.5, 7, -6, 3.5, -8, -2, -5], SILVER).setStrokeStyle(0.5, STEEL, 1);
  const glaiveBladeGem = scene.add.circle(20, -8, 1, SAPPHIRE).setStrokeStyle(0.3, 0xffffff, 1);
  const glaiveBladeEdge = scene.add.line(0, 0, 19, -8, 16, -14, 0xffffff, 0.85).setLineWidth(0.7).setOrigin(0, 0);
  // Muzzle glow at end of barrel.
  const muzzle = scene.add.circle(20, -9, 2.4, GOLD, 0.85);
  const muzzleCore = scene.add.circle(20, -9, 1.0, 0xffffff, 1);
  const muzzleHalo = scene.add.circle(20, -9, 4, GOLD, 0.4);
  scene.tweens.add({ targets: [muzzle, muzzleCore, muzzleHalo], alpha: { from: 0.5, to: 1 }, scale: { from: 0.85, to: 1.2 }, yoyo: true, repeat: -1, duration: 360 });
  scene.tweens.add({ targets: [glaiveBladeGem, glaiveBladeEdge], alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 540 });

  // ---- HELMET — silver knight head with gold visor and crescent crown ----
  const helmBack = scene.add.polygon(0, -10, [-5, 5, -5, -4, -2, -10, 2, -10, 5, -4, 5, 5], STEEL).setStrokeStyle(0.7, STEEL_DK, 1);
  const helmFace = scene.add.ellipse(0, -10, 7.5, 6.4, SILVER).setStrokeStyle(0.5, STEEL, 1);
  // T-visor — vertical line + horizontal slit (knightly).
  const visorV = scene.add.rectangle(0, -10, 1, 4, STEEL_DK);
  const visorH = scene.add.rectangle(0, -10.6, 6, 1.2, STEEL_DK);
  // Glowing slit cores.
  const visorGlowH = scene.add.rectangle(0, -10.6, 5, 0.5, SAPPHIRE, 1);
  const visorGlowV = scene.add.rectangle(0, -10, 0.4, 3, SAPPHIRE, 1);
  scene.tweens.add({ targets: [visorGlowH, visorGlowV], alpha: { from: 0.55, to: 1 }, yoyo: true, repeat: -1, duration: 460 });
  // Cheek vents.
  const cheekL = scene.add.rectangle(-2.6, -8, 0.7, 1.2, STEEL_DK);
  const cheekR = scene.add.rectangle(2.6, -8, 0.7, 1.2, STEEL_DK);
  // Gold tiara band.
  const tiaraBand = scene.add.rectangle(0, -14, 8, 0.9, GOLD);
  const tiaraGem = scene.add.polygon(0, -15, [0, -1.2, 1, 0, 0, 1.2, -1, 0], SAPPHIRE).setStrokeStyle(0.3, 0xffffff, 1);
  // Crescent helm wings.
  const helmCrescentL = scene.add.polygon(-3.8, -16, [0, 1, -1.2, -0.5, -1, -2, 0, -2.5, 0.4, -1, 0, 0], GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
  const helmCrescentR = scene.add.polygon(3.8, -16, [0, 1, 1.2, -0.5, 1, -2, 0, -2.5, -0.4, -1, 0, 0], GOLD).setStrokeStyle(0.4, GOLD_DK, 1);
  // Top antenna with sapphire pulse.
  const antenna = scene.add.rectangle(0, -19, 0.7, 4, SILVER);
  const antennaTip = scene.add.circle(0, -22, 1.1, SAPPHIRE);
  scene.tweens.add({ targets: antennaTip, alpha: { from: 0.4, to: 1 }, yoyo: true, repeat: -1, duration: 540 });

  c.add([
    shadow,
    moonAura, moonMid, moonInner, moonMask,
    star1, star2, star3, star4,
    treadL, treadR, tdL1, tdL2, tdL3, tdR1, tdR2, tdR3,
    skirtOuter, skirt, skirtTrim, skirtRiv1, skirtRiv2,
    skirtEmblemBg, skirtEmblem,
    torso, torsoMid, torsoTrim,
    rv1, rv2, rv3, rv4,
    chestGemSet, chestGem,
    pauldL, pauldR, cresL, cresR, cresGemL, cresGemR,
    armUL, elbowL, armLL, wristL,
    armUR, elbowR, wristR,
    rifleBody, rifleTrim, barrel, barrelRing1, barrelRing2,
    glaiveBladeBack, glaiveBlade, glaiveBladeGem, glaiveBladeEdge,
    muzzleHalo, muzzle, muzzleCore,
    helmBack, helmFace,
    visorH, visorV, visorGlowH, visorGlowV,
    cheekL, cheekR,
    tiaraBand, tiaraGem, helmCrescentL, helmCrescentR,
    antenna, antennaTip,
  ]);
  const castAnim = () => {
    scene.tweens.add({ targets: [muzzle, muzzleCore, muzzleHalo], scale: { from: 1, to: 2.6 }, alpha: { from: 1, to: 0 }, duration: 200, onComplete: () => { muzzle.setAlpha(0.85).setScale(1); muzzleCore.setAlpha(1).setScale(1); muzzleHalo.setAlpha(0.4).setScale(1); } });
    scene.tweens.add({ targets: [rifleBody, rifleTrim, barrel, barrelRing1, barrelRing2, glaiveBladeBack, glaiveBlade, glaiveBladeGem, glaiveBladeEdge], x: "-=2", yoyo: true, duration: 90 });
    scene.tweens.add({ targets: [moonAura, moonMid, moonInner], scale: { from: 1, to: 1.3 }, yoyo: true, duration: 220 });
    scene.tweens.add({ targets: [visorGlowH, visorGlowV, chestGem, cresGemL, cresGemR], scale: { from: 1.4, to: 1 }, duration: 200 });
  };
  return { castPoint: { x: 20, y: -9 }, orb: muzzleCore, castAnim };
}

// =====================================================================
// TINKER (Boush) — bearded gnome with rocket pack + shoulder laser turret
// =====================================================================
function buildTinker(scene: Phaser.Scene, c: Phaser.GameObjects.Container, p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {
  // ---- ROCKET BOOT EXHAUST GLOW (ground) ----
  const jetGlowL = scene.add.ellipse(-2.4, 20, 4, 1.6, p.energy, 0.65);
  const jetGlowR = scene.add.ellipse(2.4, 20, 4, 1.6, p.energy, 0.65);
  scene.tweens.add({ targets: [jetGlowL, jetGlowR], alpha: { from: 0.4, to: 0.85 }, scaleX: { from: 0.85, to: 1.2 }, yoyo: true, repeat: -1, duration: 220 });

  // ---- STUBBY LEGS in brass-trimmed leather greaves ----
  const legL = scene.add.rectangle(-2.4, 14, 3, 5, 0x4a2a08).setStrokeStyle(0.5, 0x000, 1);
  const legR = scene.add.rectangle(2.4, 14, 3, 5, 0x4a2a08).setStrokeStyle(0.5, 0x000, 1);
  const greaveBandL = scene.add.rectangle(-2.4, 13, 3.4, 0.8, p.trim);
  const greaveBandR = scene.add.rectangle(2.4, 13, 3.4, 0.8, p.trim);
  // Rocket boots — metal with brass trim
  const bootL = scene.add.rectangle(-2.4, 17, 3.8, 2, p.metal).setStrokeStyle(0.5, 0x000, 1);
  const bootR = scene.add.rectangle(2.4, 17, 3.8, 2, p.metal).setStrokeStyle(0.5, 0x000, 1);
  const bootTrimL = scene.add.rectangle(-2.4, 18, 4, 0.6, p.trim);
  const bootTrimR = scene.add.rectangle(2.4, 18, 4, 0.6, p.trim);

  // ---- WIDE STOCKY BODY in burnished bronze armor ----
  const body = scene.add.ellipse(0, 4, 13, 13, p.primary).setStrokeStyle(0.9, p.primaryDark, 1);
  // Brass plate on chest
  const chestPlate = scene.add.ellipse(0, 2, 10, 9, 0xc08838).setStrokeStyle(0.6, p.primaryDark, 1);
  // Engraved panel lines
  const platePanelL = scene.add.line(0, 0, -3, -1, -3, 5, 0x6a4a18, 1).setLineWidth(0.4).setOrigin(0, 0);
  const platePanelR = scene.add.line(0, 0, 3, -1, 3, 5, 0x6a4a18, 1).setLineWidth(0.4).setOrigin(0, 0);
  // Glowing core gauge in chest
  const gaugeRing = scene.add.circle(0, 2, 2.4, 0xffd84a).setStrokeStyle(0.5, 0x6a4a18, 1);
  const gaugeGlass = scene.add.circle(0, 2, 1.8, 0x1a1a2a).setStrokeStyle(0.3, p.metal, 1);
  const gaugeNeedle = scene.add.line(0, 0, 0, 2, 1.4, 1, 0xffffff, 1).setLineWidth(0.6).setOrigin(0, 0);
  const gaugeGlow = scene.add.circle(0, 2, 1.5, p.energy, 0.55);
  scene.tweens.add({ targets: gaugeGlow, alpha: { from: 0.3, to: 0.8 }, yoyo: true, repeat: -1, duration: 500 });
  scene.tweens.add({ targets: gaugeNeedle, rotation: { from: -0.3, to: 0.3 }, yoyo: true, repeat: -1, duration: 800 });
  // Gold belt with big ornate buckle
  const belt = scene.add.rectangle(0, 8, 13, 1.8, 0x4a2a08).setStrokeStyle(0.4, 0x000, 1);
  const beltBuckle = scene.add.rectangle(0, 8, 4.5, 2.4, p.trim).setStrokeStyle(0.4, 0x6a4a18, 1);
  const beltGem = scene.add.polygon(0, 8, [0, -1, 1, 0, 0, 1, -1, 0], p.energy);

  // ---- BACKPACK — multi-pipe steam pack with glowing crystals ----
  const pack = scene.add.rectangle(-7, 2, 5, 10, p.metal).setStrokeStyle(0.7, p.primaryDark, 1);
  const packTrim = scene.add.rectangle(-7, 0, 5.4, 0.7, p.trim);
  const packTrim2 = scene.add.rectangle(-7, 4, 5.4, 0.7, p.trim);
  // Three pipes rising from pack
  const pipeA = scene.add.rectangle(-8.5, -3, 1.2, 6, 0x6a6a78).setStrokeStyle(0.4, 0x000, 1);
  const pipeB = scene.add.rectangle(-7, -4, 1.2, 7, 0x6a6a78).setStrokeStyle(0.4, 0x000, 1);
  const pipeC = scene.add.rectangle(-5.5, -3, 1.2, 6, 0x6a6a78).setStrokeStyle(0.4, 0x000, 1);
  // Pipe caps (brass)
  const capA = scene.add.circle(-8.5, -6, 0.9, p.trim);
  const capB = scene.add.circle(-7, -7.5, 0.9, p.trim);
  const capC = scene.add.circle(-5.5, -6, 0.9, p.trim);
  // Glowing crystal pods on pack
  const crystalA = scene.add.polygon(-8.5, 1, [0, -1.2, 0.8, 0, 0, 1.2, -0.8, 0], p.energy).setStrokeStyle(0.3, 0xffffff, 1);
  const crystalB = scene.add.polygon(-5.5, 1, [0, -1.2, 0.8, 0, 0, 1.2, -0.8, 0], p.energy).setStrokeStyle(0.3, 0xffffff, 1);
  scene.tweens.add({ targets: [crystalA, crystalB], alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 600 });
  // Steam puffs from pipe caps
  const steamA = scene.add.circle(-8.5, -8, 1.2, 0xffffff, 0.5);
  const steamB = scene.add.circle(-7, -10, 1.4, 0xffffff, 0.5);
  scene.tweens.add({ targets: steamA, y: -12, alpha: 0, scale: 1.6, duration: 1200, repeat: -1, onRepeat: () => { steamA.setPosition(-8.5, -8); steamA.setAlpha(0.5); steamA.setScale(1); } });
  scene.tweens.add({ targets: steamB, y: -14, alpha: 0, scale: 1.7, duration: 1400, repeat: -1, onRepeat: () => { steamB.setPosition(-7, -10); steamB.setAlpha(0.5); steamB.setScale(1); } });

  // ---- GOGGLED FACE ----
  const head = scene.add.circle(0, -8, 4.8, p.skin).setStrokeStyle(0.7, p.primaryDark, 1);
  // Big white-streaked beard
  const beard = scene.add.polygon(0, -5, [-4.5, 0, -4, 5, 0, 7, 4, 5, 4.5, 0], p.hair).setStrokeStyle(0.5, p.primaryDark, 1);
  const beardStreak = scene.add.line(0, 0, -2, 0, -1, 6, 0xffffff, 0.7).setLineWidth(0.5).setOrigin(0, 0);
  // Dual goggles with multi-lens (twin small lenses + main lens)
  const goggleStrap = scene.add.rectangle(0, -9, 10, 1, p.primaryDark);
  const goggleStrapTrim = scene.add.rectangle(0, -9, 10, 0.4, p.trim);
  const goggleL = scene.add.circle(-1.8, -9, 1.8, 0x222).setStrokeStyle(0.7, p.trim, 1);
  const goggleR = scene.add.circle(1.8, -9, 1.8, 0x222).setStrokeStyle(0.7, p.trim, 1);
  const goggleGlowL = scene.add.circle(-1.8, -9, 1.2, p.energy, 1);
  const goggleGlowR = scene.add.circle(1.8, -9, 1.2, p.energy, 1);
  const goggleShineL = scene.add.circle(-1.4, -9.4, 0.4, 0xffffff);
  const goggleShineR = scene.add.circle(2.2, -9.4, 0.4, 0xffffff);
  // Side magnifier lens (engineer detail)
  const sideLens = scene.add.circle(4.4, -9, 0.9, p.energy, 0.85).setStrokeStyle(0.4, p.trim, 1);

  // ---- HAT — burnished pointy cap with gold trim ----
  const hatBrim = scene.add.ellipse(0, -12, 9, 1.6, p.primaryDark).setStrokeStyle(0.4, 0x000, 1);
  const hatBrimTrim = scene.add.rectangle(0, -12.5, 8, 0.6, p.trim);
  const hat = scene.add.polygon(0, -13, [-4.2, 0, 0, -9, 4.2, 0], p.primary).setStrokeStyle(0.6, p.primaryDark, 1);
  const hatBand = scene.add.rectangle(0, -14, 5.4, 0.7, p.trim);
  // Gear emblem on hat
  const hatEmblem = scene.add.circle(0, -16, 0.9, p.trim).setStrokeStyle(0.3, 0x6a4a18, 1);
  const hatEmblemDot = scene.add.circle(0, -16, 0.4, p.energy);
  // Hat tip with gold ball
  const hatTip = scene.add.circle(0, -22, 1.2, p.trim).setStrokeStyle(0.3, 0x6a4a18, 1);
  scene.tweens.add({ targets: hatTip, scale: { from: 1, to: 1.25 }, yoyo: true, repeat: -1, duration: 1100 });

  // ---- SHOULDER LASER TURRET — brass barrel, heat sink, gold rim ----
  const turretBaseR = scene.add.circle(6, -3, 2.4, p.metal).setStrokeStyle(0.5, p.primaryDark, 1);
  const turretMount = scene.add.rectangle(7, -3, 3.4, 3.4, p.metal).setStrokeStyle(0.5, p.primaryDark, 1);
  const turretRim = scene.add.rectangle(7, -3, 3.6, 0.7, p.trim);
  // Heat sink fins
  const finA = scene.add.rectangle(8, -5, 0.6, 2, p.primaryDark);
  const finB = scene.add.rectangle(9, -5, 0.6, 2, p.primaryDark);
  const finC = scene.add.rectangle(10, -5, 0.6, 2, p.primaryDark);
  // Brass barrel with glowing ring
  const turretBarrel = scene.add.rectangle(11, -4, 7, 1.8, 0xc08838).setStrokeStyle(0.5, 0x000, 1);
  const barrelRing1 = scene.add.rectangle(9, -4, 0.6, 2.4, p.trim);
  const barrelRing2 = scene.add.rectangle(13, -4, 0.6, 2.4, p.trim);
  const turretMuzzle = scene.add.circle(15, -4, 1.2, p.energy, 1).setStrokeStyle(0.3, 0xffffff, 1);
  const muzzleHalo = scene.add.circle(15, -4, 2.4, p.energy, 0.4);
  scene.tweens.add({ targets: turretMuzzle, alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 200 });
  scene.tweens.add({ targets: muzzleHalo, alpha: { from: 0.2, to: 0.55 }, scale: { from: 0.85, to: 1.15 }, yoyo: true, repeat: -1, duration: 350 });
  scene.tweens.add({ targets: [goggleGlowL, goggleGlowR], alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 400 });
  scene.tweens.add({ targets: sideLens, alpha: { from: 0.5, to: 1 }, yoyo: true, repeat: -1, duration: 600 });

  // ---- LEFT MECH-ARM with grasping claw + gold pocket watch chain ----
  const mechArmU = scene.add.rectangle(-6, 1, 2.2, 4, p.metal).setStrokeStyle(0.5, p.primaryDark, 1);
  const mechElbow = scene.add.circle(-7, 4, 1.2, p.trim).setStrokeStyle(0.3, 0x6a4a18, 1);
  const mechArmL = scene.add.rectangle(-8, 6, 2, 4, p.metal).setStrokeStyle(0.5, p.primaryDark, 1);
  const clawL = scene.add.triangle(-9, 9, 0, 0, -1.6, 2, 0, 2.2, p.metal).setStrokeStyle(0.4, 0x000, 1);
  const clawR = scene.add.triangle(-7, 9, 0, 0, 0, 2.2, 1.6, 2, p.metal).setStrokeStyle(0.4, 0x000, 1);
  // Pocket watch dangling
  const watchChain = scene.add.line(0, 0, -2, 8, -4, 11, p.trim, 1).setLineWidth(0.5).setOrigin(0, 0);
  const watch = scene.add.circle(-4, 12, 1.2, p.trim).setStrokeStyle(0.3, 0x6a4a18, 1);
  const watchFace = scene.add.circle(-4, 12, 0.7, 0xffffff);

  // ---- FLOATING DRONE companion ----
  const drone = scene.add.circle(13, -16, 1.6, p.metal).setStrokeStyle(0.4, p.primaryDark, 1);
  const droneEye = scene.add.circle(13, -16, 0.7, p.energy);
  const dronePropL = scene.add.line(0, 0, 11, -17, 11.4, -16, 0xffffff, 0.7).setLineWidth(0.5).setOrigin(0, 0);
  const dronePropR = scene.add.line(0, 0, 14.6, -16, 15, -17, 0xffffff, 0.7).setLineWidth(0.5).setOrigin(0, 0);
  scene.tweens.add({ targets: drone, y: -18, yoyo: true, repeat: -1, duration: 900, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: droneEye, y: -18, yoyo: true, repeat: -1, duration: 900, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: droneEye, alpha: { from: 0.6, to: 1 }, yoyo: true, repeat: -1, duration: 400 });

  c.add([
    jetGlowL, jetGlowR,
    legL, legR, greaveBandL, greaveBandR, bootL, bootR, bootTrimL, bootTrimR,
    pack, packTrim, packTrim2,
    pipeA, pipeB, pipeC, capA, capB, capC,
    crystalA, crystalB, steamA, steamB,
    body, chestPlate, platePanelL, platePanelR,
    gaugeRing, gaugeGlass, gaugeGlow, gaugeNeedle,
    belt, beltBuckle, beltGem,
    mechArmU, mechElbow, mechArmL, clawL, clawR, watchChain, watch, watchFace,
    head, beard, beardStreak,
    goggleStrap, goggleStrapTrim, goggleL, goggleR, goggleGlowL, goggleGlowR, goggleShineL, goggleShineR, sideLens,
    hatBrim, hatBrimTrim, hat, hatBand, hatEmblem, hatEmblemDot, hatTip,
    turretBaseR, turretMount, turretRim, finA, finB, finC,
    turretBarrel, barrelRing1, barrelRing2, muzzleHalo, turretMuzzle,
    drone, droneEye, dronePropL, dronePropR,
  ]);
  const castAnim = () => {
    scene.tweens.add({ targets: [turretBarrel, barrelRing1, barrelRing2], x: "-=2", yoyo: true, duration: 70 });
    scene.tweens.add({ targets: [turretMuzzle, muzzleHalo], scale: { from: 1, to: 2.6 }, alpha: { from: 1, to: 0 }, duration: 160, onComplete: () => { turretMuzzle.setAlpha(1).setScale(1); muzzleHalo.setAlpha(0.4).setScale(1); } });
    scene.tweens.add({ targets: [crystalA, crystalB, gaugeGlow], scale: { from: 1.6, to: 1 }, duration: 200 });
    scene.tweens.add({ targets: drone, scale: { from: 1.3, to: 1 }, duration: 200 });
  };
  return { castPoint: { x: 15, y: -4 }, orb: turretMuzzle, castAnim };
}

// =====================================================================
// TREANT PROTECTOR — Ancient tree guardian (Rooftrellen). Massive bark-
// armored trunk body, two gnarled living-branch arms, root-splay feet,
// flat bark-face head with twin glowing green Living Armor eyes, dense
// leaf canopy crown that sways in the breeze.
// Reference: Dota 2 Treant Protector — wide bark giant, nature's sentinel.
// =====================================================================
function buildTreantProtector(scene: Phaser.Scene, c: Phaser.GameObjects.Container, _p: CasterPalette): { castPoint: { x: number; y: number }; orb?: Phaser.GameObjects.GameObject; castAnim?: () => void } {

  const BARK_DEEP  = 0x3a2010;
  const BARK_MID   = 0x5a3a1a;
  const BARK_LIGHT = 0x7a5a2a;
  const BARK_HI    = 0x9a7a40;
  const LEAF_DEEP  = 0x1a3a0a;
  const LEAF_MID   = 0x3a6a1a;
  const LEAF_LIGHT = 0x7ab03a;
  const LEAF_TRIM  = 0xc8ff8a;
  const EYE_GLOW   = 0x6aff2a;
  const MOSS       = 0x4a7a2a;

  // ---- ROOT FEET (drawn first, behind stumps) ----
  const rootLL = scene.add.triangle(-7, 21, 0, 0, -4, 2.5, -0.5, 2.5, BARK_MID).setStrokeStyle(0.4, BARK_DEEP, 1);
  const rootLM = scene.add.triangle(-4.5, 21, 0, 0, -2, 2.5, 2, 2.5, BARK_MID).setStrokeStyle(0.4, BARK_DEEP, 1);
  const rootRM = scene.add.triangle(4.5, 21, 0, 0, -2, 2.5, 2, 2.5, BARK_MID).setStrokeStyle(0.4, BARK_DEEP, 1);
  const rootRR = scene.add.triangle(7, 21, 0, 0, 0.5, 2.5, 4, 2.5, BARK_MID).setStrokeStyle(0.4, BARK_DEEP, 1);
  const mossL = scene.add.ellipse(-5, 21.5, 5, 1.6, MOSS, 0.65);
  const mossR = scene.add.ellipse(5, 21.5, 5, 1.6, MOSS, 0.65);

  // ---- TRUNK STUMPS (wide short legs) ----
  const stumpL = scene.add.rectangle(-4.5, 13, 7.5, 10, BARK_MID).setStrokeStyle(0.9, BARK_DEEP, 1);
  const stumpR = scene.add.rectangle(4.5, 13, 7.5, 10, BARK_MID).setStrokeStyle(0.9, BARK_DEEP, 1);
  const stumpHiL = scene.add.rectangle(-4.5, 8.4, 7.5, 0.8, BARK_HI, 0.5);
  const stumpHiR = scene.add.rectangle(4.5, 8.4, 7.5, 0.8, BARK_HI, 0.5);
  // Bark grain on stumps
  const grainSL = scene.add.line(0, 0, -7, 9, -5.5, 18, BARK_LIGHT, 0.4).setLineWidth(0.5).setOrigin(0, 0);
  const grainSR = scene.add.line(0, 0, 7, 9, 5.5, 18, BARK_LIGHT, 0.4).setLineWidth(0.5).setOrigin(0, 0);

  // ---- WIDE TRUNK BODY ----
  const bodyShadow = scene.add.ellipse(0.5, 2, 26, 21, BARK_DEEP, 0.7);
  const body = scene.add.ellipse(0, 1, 22, 18, BARK_MID).setStrokeStyle(1.2, BARK_DEEP, 1);
  const chest = scene.add.ellipse(0, 0, 16, 13, BARK_LIGHT, 0.4);
  // Bark grain lines
  const bkG1 = scene.add.line(0, 0, -8, -3, -7, 8, BARK_LIGHT, 0.35).setLineWidth(0.6).setOrigin(0, 0);
  const bkG2 = scene.add.line(0, 0, -3, -7, -2, 8, BARK_DEEP, 0.35).setLineWidth(0.4).setOrigin(0, 0);
  const bkG3 = scene.add.line(0, 0, 3, -7, 2, 8, BARK_DEEP, 0.35).setLineWidth(0.4).setOrigin(0, 0);
  const bkG4 = scene.add.line(0, 0, 8, -3, 7, 8, BARK_LIGHT, 0.35).setLineWidth(0.6).setOrigin(0, 0);
  // Moss patches
  const mossBodyL = scene.add.ellipse(-5, 2, 4.5, 2.5, MOSS, 0.4);
  const mossBodyR = scene.add.ellipse(5, 2, 4.5, 2.5, MOSS, 0.4);
  // Bark knot
  const knotOuter = scene.add.circle(0, 5.5, 2.4, BARK_DEEP, 0.55).setStrokeStyle(0.5, BARK_HI, 0.4);
  const knotInner = scene.add.circle(0, 5.5, 1, BARK_DEEP);

  // ---- LEFT BRANCH ARM (extends left) ----
  const armLU = scene.add.rectangle(-10, -1, 3.2, 10, BARK_MID).setStrokeStyle(0.7, BARK_DEEP, 1);
  armLU.setRotation(-0.35);
  const armLElbow = scene.add.circle(-13.5, 4.5, 1.8, BARK_MID).setStrokeStyle(0.5, BARK_DEEP, 1);
  const armLL = scene.add.rectangle(-15, 7, 2.6, 8, BARK_MID).setStrokeStyle(0.6, BARK_DEEP, 1);
  armLL.setRotation(-0.12);
  const twigL1 = scene.add.triangle(-17, 10, 0, 0, -2, 3.5, 0, 4, BARK_LIGHT).setRotation(-0.35);
  const twigL2 = scene.add.triangle(-15, 13, 0, 0, -1.5, 3, 1.5, 3, BARK_LIGHT);
  const leafArmL = scene.add.ellipse(-16, 9, 5.5, 3, LEAF_MID, 0.8).setRotation(-0.4);

  // ---- RIGHT BRANCH ARM (raised, cast arm) ----
  const armRU = scene.add.rectangle(10, -3, 3.2, 10, BARK_MID).setStrokeStyle(0.7, BARK_DEEP, 1);
  armRU.setRotation(0.38);
  const armRElbow = scene.add.circle(13.5, 2.5, 1.8, BARK_MID).setStrokeStyle(0.5, BARK_DEEP, 1);
  const armRL = scene.add.rectangle(15.5, -0.5, 2.6, 8, BARK_MID).setStrokeStyle(0.6, BARK_DEEP, 1);
  armRL.setRotation(0.1);
  const tendrilR1 = scene.add.line(0, 0, 13.5, 5, 14.5, 10, LEAF_MID, 0.85).setLineWidth(0.8).setOrigin(0, 0);
  const tendrilR2 = scene.add.line(0, 0, 15.5, 3, 17, 8, LEAF_MID, 0.7).setLineWidth(0.6).setOrigin(0, 0);
  const armRTip = scene.add.ellipse(16.5, -4, 4, 3, LEAF_DEEP).setStrokeStyle(0.5, BARK_DEEP, 1);
  const leafArmR = scene.add.ellipse(17.5, -5, 5.5, 3, LEAF_MID, 0.85).setRotation(0.3);
  scene.tweens.add({ targets: [tendrilR1, tendrilR2], y: "+=1.5", yoyo: true, repeat: -1, duration: 1900, ease: "Sine.easeInOut" });

  // ---- BARK FACE HEAD ----
  const headShadow = scene.add.ellipse(0.5, -13, 16, 11, BARK_DEEP, 0.65);
  const head = scene.add.ellipse(0, -13, 14, 10, BARK_MID).setStrokeStyle(1, BARK_DEEP, 1);
  const faceLighter = scene.add.ellipse(0, -13.5, 10, 7, BARK_LIGHT, 0.3);
  const brow = scene.add.ellipse(0, -16.8, 13, 2, BARK_DEEP, 0.65);
  const faceG1 = scene.add.line(0, 0, -4, -16, -3, -10, BARK_DEEP, 0.35).setLineWidth(0.5).setOrigin(0, 0);
  const faceG2 = scene.add.line(0, 0, 4, -16, 3, -10, BARK_DEEP, 0.35).setLineWidth(0.5).setOrigin(0, 0);

  // Eye sockets (dark recesses in bark)
  const eyeSocketL = scene.add.ellipse(-3.5, -13.5, 5, 3, BARK_DEEP);
  const eyeSocketR = scene.add.ellipse(3.5, -13.5, 5, 3, BARK_DEEP);
  // Living Armor glowing eyes
  const eyeGlowL = scene.add.ellipse(-3.5, -13.5, 4.5, 2.8, EYE_GLOW, 0.75);
  const eyeGlowR = scene.add.ellipse(3.5, -13.5, 4.5, 2.8, EYE_GLOW, 0.75);
  const eyeCoreL = scene.add.ellipse(-3.5, -13.5, 2.2, 1.4, 0xaaffaa);
  const eyeCoreR = scene.add.ellipse(3.5, -13.5, 2.2, 1.4, 0xaaffaa);
  const eyeShineL = scene.add.circle(-2.7, -14, 0.5, 0xffffff);
  const eyeShineR = scene.add.circle(4.2, -14, 0.5, 0xffffff);
  // Breathing eye glow
  scene.tweens.add({ targets: [eyeGlowL, eyeGlowR], alpha: { from: 0.52, to: 0.92 }, yoyo: true, repeat: -1, duration: 1600, ease: "Sine.easeInOut" });

  // ---- LEAF CANOPY CROWN ----
  const crownBack = scene.add.ellipse(0, -20, 19, 9, LEAF_DEEP, 0.9);
  const crownCL   = scene.add.ellipse(-5.5, -21, 10, 7, LEAF_MID).setStrokeStyle(0.5, LEAF_DEEP, 0.7);
  const crownCC   = scene.add.ellipse(0, -23, 12, 8, LEAF_MID).setStrokeStyle(0.5, LEAF_DEEP, 0.7);
  const crownCR   = scene.add.ellipse(5.5, -21, 10, 7, LEAF_MID).setStrokeStyle(0.5, LEAF_DEEP, 0.7);
  const crownHL   = scene.add.ellipse(-6, -22.5, 6, 4, LEAF_LIGHT, 0.65);
  const crownHC   = scene.add.ellipse(0, -25, 7, 4.5, LEAF_LIGHT, 0.65);
  const crownHR   = scene.add.ellipse(6, -22.5, 6, 4, LEAF_LIGHT, 0.65);
  const leafTipL  = scene.add.triangle(-8, -22, 0, 0, -1.6, 3.5, 1.6, 3.5, LEAF_TRIM, 0.8).setRotation(-0.4);
  const leafTipC  = scene.add.triangle(0, -27, 0, 0, -1.8, 3.5, 1.8, 3.5, LEAF_TRIM, 0.9);
  const leafTipR  = scene.add.triangle(8, -22, 0, 0, -1.6, 3.5, 1.6, 3.5, LEAF_TRIM, 0.8).setRotation(0.4);
  // Gentle canopy sway
  scene.tweens.add({ targets: [crownCL, crownHL, leafTipL], x: "-=0.9", yoyo: true, repeat: -1, duration: 2300, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [crownCR, crownHR, leafTipR], x: "+=0.9", yoyo: true, repeat: -1, duration: 2000, ease: "Sine.easeInOut" });
  scene.tweens.add({ targets: [crownCC, crownHC, leafTipC], y: "-=0.7", yoyo: true, repeat: -1, duration: 2500, ease: "Sine.easeInOut" });

  c.add([
    rootLL, rootLM, rootRM, rootRR, mossL, mossR,
    stumpL, stumpR, stumpHiL, stumpHiR, grainSL, grainSR,
    bodyShadow, body, chest,
    bkG1, bkG2, bkG3, bkG4,
    mossBodyL, mossBodyR, knotOuter, knotInner,
    armLU, armLElbow, armLL, twigL1, twigL2, leafArmL,
    tendrilR1, tendrilR2,
    armRU, armRElbow, armRL, armRTip, leafArmR,
    crownBack,
    headShadow, head, faceLighter, brow,
    faceG1, faceG2,
    eyeSocketL, eyeSocketR,
    eyeGlowL, eyeGlowR,
    eyeCoreL, eyeCoreR,
    eyeShineL, eyeShineR,
    crownCL, crownCC, crownCR,
    crownHL, crownHC, crownHR,
    leafTipL, leafTipC, leafTipR,
  ]);

  const castAnim = () => {
    // Right arm thrusts forward (channeling Living Armor nature magic)
    scene.tweens.add({ targets: [armRU, armRElbow, armRL, armRTip, leafArmR, tendrilR1, tendrilR2], x: "+=3", yoyo: true, duration: 130, ease: "Sine.easeInOut" });
    // Left arm opens outward (spreading the protective aura)
    scene.tweens.add({ targets: [armLU, armLElbow, armLL, twigL1, twigL2, leafArmL], x: "-=1.5", yoyo: true, duration: 130, ease: "Sine.easeInOut" });
    // Eyes flash bright — Living Armor green surge
    scene.tweens.add({ targets: [eyeGlowL, eyeGlowR, eyeCoreL, eyeCoreR], scale: { from: 1.8, to: 1 }, alpha: { from: 1, to: 0.75 }, duration: 240, ease: "Quad.easeOut" });
    // Crown burst — leaves surge on cast
    scene.tweens.add({ targets: [crownCC, crownHC, leafTipC], scale: { from: 1.3, to: 1 }, duration: 250, ease: "Quad.easeOut" });
    scene.tweens.add({ targets: [crownCL, crownHL, leafTipL, crownCR, crownHR, leafTipR], scale: { from: 1.2, to: 1 }, duration: 220, ease: "Quad.easeOut" });
  };
  return { castPoint: { x: 17, y: -5 }, castAnim };
}

